import * as i1 from '@ngrx/store';
import { createAction, props, createReducer, on, createFeatureSelector, createSelector, StoreModule } from '@ngrx/store';
import * as i0 from '@angular/core';
import { Injectable, InjectionToken, Inject, APP_INITIALIZER, NgModule, Optional } from '@angular/core';
import * as i5 from '@panamax/app-state';
import { APP_STATE_KEY, selectShowInventory, retryBackoff } from '@panamax/app-state';
import * as i1$1 from '@panamax/browser-storage';
import { MessageTypes, BrowserStorageService } from '@panamax/browser-storage';
import { combineLatest, of, defer, throwError, Observable, forkJoin } from 'rxjs';
import { first, map, take, filter, mergeMap, switchMap, timeout, catchError, withLatestFrom, concatMap, distinctUntilChanged, retry, takeWhile } from 'rxjs/operators';
import { createEntityAdapter } from '@ngrx/entity';
import dayjs from 'dayjs';
import { ProductPropertiesEnum, TypeaheadGroupNames, ProductInventoryEventTypes, ProductScoopEventTypes, ProductContractEventTypes, ProductSummaryEventActions, ProductInventoryEventActions, ProductScoopEventActions, ProductContractEventActions, MyProductChangesEventActions } from '@usf/product-types';
import * as i3 from '@panamax/websocket';
import { WebSocketService } from '@panamax/websocket';
import { nanoid } from 'nanoid';
import jwt_decode from 'jwt-decode';
import * as i1$2 from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import * as i1$3 from '@ngrx/effects';
import { createEffect, ofType, concatLatestFrom, EffectsModule } from '@ngrx/effects';

/*eslint no-shadow: 0*/
var LoadingState;
(function (LoadingState) {
    LoadingState["notReady"] = "notReady";
    LoadingState["init"] = "init";
    LoadingState["loading"] = "loading";
    LoadingState["loaded"] = "loaded";
    LoadingState["notFound"] = "notFound";
    LoadingState["error"] = "error";
    LoadingState["slim"] = "slim";
})(LoadingState || (LoadingState = {}));

var LoadingNextPageState;
(function (LoadingNextPageState) {
    LoadingNextPageState["loadingNextPage"] = "loadingNextPage";
})(LoadingNextPageState || (LoadingNextPageState = {}));
const searchLoadingState = { ...LoadingState, ...LoadingNextPageState };

var SortOptionsEnum;
(function (SortOptionsEnum) {
    SortOptionsEnum["asc"] = "asc";
    SortOptionsEnum["desc"] = "desc";
    SortOptionsEnum["productName"] = "productName";
    SortOptionsEnum["productNameDisplayText"] = "i18n.nonUsfProducts.sortLabels.productName";
    SortOptionsEnum["vendor"] = "vendor";
    SortOptionsEnum["vendorDisplayText"] = "i18n.nonUsfProducts.sortLabels.vendor";
    SortOptionsEnum["brand"] = "brand";
    SortOptionsEnum["brandDisplayText"] = "i18n.nonUsfProducts.sortLabels.brand";
    SortOptionsEnum["productNumber"] = "productNumber";
    SortOptionsEnum["productNumberDisplayText"] = "i18n.nonUsfProducts.sortLabels.productNumber";
    SortOptionsEnum["manufacturerNumber"] = "manufacturerNumber";
    SortOptionsEnum["manufacturerNumberDisplayText"] = "i18n.nonUsfProducts.sortLabels.manufacturerNumber";
    SortOptionsEnum["packSize"] = "packSize";
    SortOptionsEnum["packSizeDisplayText"] = "i18n.nonUsfProducts.sortLabels.packSize";
    SortOptionsEnum["pricing"] = "pricing";
    SortOptionsEnum["pricingDisplayText"] = "i18n.nonUsfProducts.sortLabels.price";
})(SortOptionsEnum || (SortOptionsEnum = {}));
const initialNonUsfProductSortOptions = {
    selectedHeader: SortOptionsEnum.productNumber,
    productName: {
        displayText: SortOptionsEnum.productNameDisplayText,
        headerName: SortOptionsEnum.productName,
        sortDirection: SortOptionsEnum.desc,
    },
    vendor: {
        displayText: SortOptionsEnum.vendorDisplayText,
        headerName: SortOptionsEnum.vendor,
        sortDirection: SortOptionsEnum.desc,
    },
    brand: {
        displayText: SortOptionsEnum.brandDisplayText,
        headerName: SortOptionsEnum.brand,
        sortDirection: SortOptionsEnum.desc,
    },
    productNumber: {
        displayText: SortOptionsEnum.productNumberDisplayText,
        headerName: SortOptionsEnum.productNumber,
        sortDirection: SortOptionsEnum.desc,
    },
    manufacturerNumber: {
        displayText: SortOptionsEnum.manufacturerNumberDisplayText,
        headerName: SortOptionsEnum.manufacturerNumber,
        sortDirection: SortOptionsEnum.desc,
    },
    packSize: {
        displayText: SortOptionsEnum.packSizeDisplayText,
        headerName: SortOptionsEnum.packSize,
        sortDirection: SortOptionsEnum.desc,
    },
    pricing: {
        displayText: SortOptionsEnum.pricingDisplayText,
        headerName: SortOptionsEnum.pricing,
        sortDirection: SortOptionsEnum.desc,
    },
};

/* eslint-disable @typescript-eslint/naming-convention */
const MyProductActions = {
    ResetStore: createAction('[My Products] Reset Store'),
    LoadFromStorageSuccess: createAction('[My Products] Load From Storage Success', props()),
    LoadFromStorageFail: createAction('[My Products] Failed to Load From Storage', props()),
    UpdateMyProducts: createAction('[My Products] Update my products list', props()),
};

/* eslint-disable @typescript-eslint/naming-convention */
const ProductActions = {
    LoadProducts: createAction('[NGRXTrace - Products Resolver] Load All My Products'),
    LoadProductsOnList: createAction('[NGRXTrace - Products Resolver] Load Products on My List', props()),
    LoadProductsSuccess: createAction('[NGRXTrace - API Effect] All Products Loaded', props()),
    LoadProductDetail: createAction('[NGRXTrace - Product Resolver] Load Product Detail By Product Number', props()),
    LoadProductDetailSuccess: createAction('[NGRXTrace - Product Resolver] Load Product Detail Success By Product Number', props()),
    LoadProductDetailFailure: createAction('[NGRXTrace - Product Resolver] Load Product Detail Failure By Product Number', props()),
    LoadSlimProducts: createAction('[NGRXTrace - Products Resolver] Load Slim Products', props()),
    LoadSlimProductsSuccess: createAction('[NGRXTrace - API Effect] Load Slim Products Succeeded', props()),
    LoadSlimProductsFail: createAction('[NGRXTrace - API Effect] Load Slim Products Failed', props()),
    ProductCreated: createAction('[NGRXTrace - WebSocket Event] Create Product', props()),
    ProductUpdated: createAction('[NGRXTrace - WebSocket Event] Update Product', props()),
    ProductsUpdated: createAction('[NGRXTrace - Effect] My Products Updated Successfully', props()),
    ProductDeleted: createAction('[NGRXTrace - WebSocket Event] Product Deleted', props()),
    FetchProductDetail: createAction('[NGRXTrace - Product Resolver] Fetch Product Detail By Product Number', props()),
    FetchProductDetailSuccess: createAction('[NGRXTrace - Product Resolver] Fetch Product Detail Success By Product Number', props()),
    FetchProductDetailFailure: createAction('[NGRXTrace - Product Resolver] Fetch Product Detail Failure By Product Number', props()),
    FetchProductDetails: createAction('[NGRXTrace - Product Resolver] Fetch Product Details By Product Numbers', props()),
    FetchProductDetailsSuccess: createAction('[NGRXTrace - Product Resolver] Fetch Product Details Success By Product Numbers', props()),
    FetchProductDetailsFailure: createAction('[NGRXTrace - Product Resolver] Fetch Product Details Failure By Product Numbers', props()),
    initializeStorage: createAction('[NGRXTrace - Product] - Initialize Offline Storage'),
    initializeStorageFail: createAction('[NGRXTrace - Product] - Initialize Offline Storage Fail', props()),
    LoadAllAvailableDeals: createAction('[NGRXTrace - Product] - Load All Available Deals Page', props()),
    LoadAllAvailableDealsSuccess: createAction('[NGRXTrace - Product] - Load All Available Deals Page Success', props()),
    LoadProductOrderabilities: createAction('[NGRXTrace - Product] Load Product Orderabilities', props()),
    FetchProductOrderabilities: createAction('[NGRXTrace - Product] Fetch Product Orderabilities', props()),
    LoadProductOrderabilitiesSuccess: createAction('[NGRXTrace - API Effect] Load Product Orderabilities Success', props()),
    LoadProductOrderabilitiesFailure: createAction('[NGRXTrace - Product Resolver] Load Product Orderability Failure By Product Number', props()),
    FetchPowerReviewsProduct: createAction('[NGRXTrace - Power Reviews] Load Power Reviews Product', props()),
    UpdatePowerReviewsProduct: createAction('[NGRXTrace - Power Reviews] Load New List of Power Reviews Product', props()),
    UpdatePowerReviewsProductSuccess: createAction('[NGRXTrace - Power Reviews] Load New List of Power Reviews Product Success', props()),
    FetchPowerReviewsProductSuccess: createAction('[NGRXTrace - Power Reviews] Load Power Reviews Product Success', props()),
    FetchPowerReviewsProductFailure: createAction('[NGRXTrace - Power Reviews] Load Power Reviews Product Failure', props()),
};

/* eslint-disable @typescript-eslint/naming-convention */
const ProductSummaryActions = {
    ResetStore: createAction('[NGRXTrace - Product Summary] Reset Product Summaries'),
    LoadProductSummaries: createAction('[NGRXTrace - Product Summary] Load Product Summaries', props()),
    FetchProductSummaries: createAction('[NGRXTrace - Product Summary] Fetch Product Summaries', props()),
    FetchProductSummariesSuccess: createAction('[NGRXTrace - Product Summary] Fetch Product Summaries Success', props()),
    FetchProductSummariesFail: createAction('[NGRXTrace - Product Summary] Fetch Product Summaries Fail', props()),
    LoadFromStorage: createAction('[NGRXTrace - Product Summary] Load From Storage'),
    LoadFromStorageSuccess: createAction('[NGRXTrace - Product Summary] Load From Storage Success', props()),
    LoadFromStorageFail: createAction('[NGRXTrace - Product Summary] Load From Storage Fail', props()),
    LoadProductSummariesChanges: createAction('[NGRXTrace - Product Summary] Load Product Summaries Changes', props()),
    RequestChanges: createAction('[NGRXTrace - Product Summary] Request changes'),
    UpdateProductSummariesWithMslData: createAction('[NGRXTrace - Product Summary] Update Summaries in State with Master List Data', props()),
};

/* eslint-disable @typescript-eslint/naming-convention */
const ProductInventoryActions = {
    ResetStore: createAction('[NGRXTrace - Product Inventory] Reset Product Inventories'),
    LoadProductInventories: createAction('[NGRXTrace - Product Inventory] Load Product Inventories', props()),
    FetchProductInventories: createAction('[NGRXTrace - Product Inventory] Fetch Product Inventories', props()),
    FetchProductInventoriesSuccess: createAction('[NGRXTrace - Product Inventory] Fetch Product Inventories Success', props()),
    FetchProductInventoriesFail: createAction('[NGRXTrace - Product Inventory] Fetch Product Inventories Fail', props()),
    FetchProductInventoriesAsync: createAction('[NGRXTrace - Product Inventory] Fetch Product Inventories Async', props()),
    loadFromStorage: createAction('[NGRXTrace - Product Inventory] Load From Storage'),
    loadFromStorageSuccess: createAction('[NGRXTrace - Product Inventory] Loaded From Storage Successfully', props()),
    loadFromStorageFail: createAction('[NGRXTrace - Product Inventory] Failed to Load From Storage', props()),
    writeToOfflineStorage: createAction('[NGRXTrace - Product Inventory] Write To Offline Storage', props()),
};

/* eslint-disable @typescript-eslint/naming-convention */
const ProductContractActions = {
    ResetStore: createAction('[NGRXTrace - Product Contract] Reset Product Contracts'),
    LoadProductContracts: createAction('[NGRXTrace - Product Contract] Load Product Contracts', props()),
    FetchProductContracts: createAction('[NGRXTrace - Product Contract] Fetch Product Contracts', props()),
    FetchProductContractsSuccess: createAction('[NGRXTrace - Product Contract] Fetch Product Contracts Success', props()),
    FetchProductContractsFail: createAction('[NGRXTrace - Product Contract] Fetch Product Contracts Fail', props()),
    FetchProductContractsAsync: createAction('[NGRXTrace - Product Contract] Fetch Product Contracts Async'),
    FetchProductContractsSuccessAsync: createAction('[NGRXTrace - Product Contract] Fetch Product Contracts Success Async', props()),
    FetchProductContractsFailAsync: createAction('[NGRXTrace - Product Contract] Fetch Product Contracts Fail Async'),
    loadFromStorage: createAction('[NGRXTrace - Product Contract] Load From Storage'),
    loadFromStorageSuccess: createAction('[NGRXTrace - Product Contract] Loaded From Storage Successfully', props()),
    loadFromStorageFail: createAction('[NGRXTrace - Product Contract] Failed to Load From Storage', props()),
    writeToOfflineStorage: createAction('[NGRXTrace - Product Contract] Write To Offline Storage', props()),
};

/* eslint-disable @typescript-eslint/naming-convention */
const SearchActions = {
    ResetStore: createAction('[NGRXTrace - Search] Reset Search'),
    LoadProductsOnSearch: createAction('[NGRXTrace - Search] Load Products on Search', props()),
    FetchSearchResponse: createAction('[NGRXTrace - Search] Search Products', props()),
    FetchSearchResponseSuccess: createAction('[NGRXTrace - Search] Search Products Success', props()),
    FetchSearchResponseFail: createAction('[NGRXTrace - Search] Search Products Failure', props()),
    SearchResultsPageLoaded: createAction('[NGRXTrace - Search] Search Results Page Loaded', props()),
    WhileSuppliesLast: createAction('[NGRXTrace - Search] While Supplies Last', props()),
    WhileSuppliesLastSuccess: createAction('[NGRXTrace - Search] While Supplies Last Success', props()),
    Scoop: createAction('[NGRXTrace - Search] Scoop', props()),
};

/* eslint-disable @typescript-eslint/naming-convention */
const SearchActionsV2 = {
    ResetStore: createAction('[NGRXTrace - SearchV2] Reset Search'),
    FetchSearchResponse: createAction('[NGRXTrace - SearchV2] Search Products', props()),
    FetchSearchResponseSuccess: createAction('[NGRXTrace - SearchV2] Search Products Success', props()),
    FetchSearchResponseFail: createAction('[NGRXTrace - SearchV2] Search Products Failure', props()),
};

/* eslint-disable @typescript-eslint/naming-convention */
const ProductScoopActions = {
    loadFromStorage: createAction('[NGRXTrace - Product Scoop] Load Product Scoops From Storage'),
    loadFromStorageSuccess: createAction('[NGRXTrace - Product Scoop] Product Scoops Loaded From Storage Successfully', props()),
    loadFromStorageFail: createAction('[NGRXTrace - Product Scoop] Failed to Load Product Scoops From Storage', props()),
    loadProductScoops: createAction('[NGRXTrace - Product Scoop] Load Product Scoops From Domain Api'),
    loadProductScoopsSuccess: createAction('[NGRXTrace - Product Scoop] Product Scoops From Domain Api Loaded Successfully', props()),
    loadProductScoopsFail: createAction('[NGRXTrace - Product Scoop] Failed to Load Product Scoops From Domain Api', props()),
    writeToOfflineStorage: createAction('[NGRXTrace - Product Scoop] Write Product Scoops To Offline Storage', props()),
    loadProductScoopsAsync: createAction('[NGRXTrace - Product Scoop] Load Product Scoops From Domain Api Async'),
};

/* eslint-disable @typescript-eslint/naming-convention */
const BrowseCategoryActions = {
    ResetStore: createAction('[NGRXTrace - Browse] Reset Browse Categories'),
    FetchBrowseResponse: createAction('[NGRXTrace - Browse] Browse Catalog Products', props()),
    FetchBrowseResponseSuccess: createAction('[NGRXTrace - Browse] Browse Catalog Products Success', props()),
    FetchBrowseResponseFail: createAction('[NGRXTrace - Browse] Browse Catalog Products Failure', props()),
    LoadBrowseResponse: createAction('[NGRXTrace - Browse] Load Browse Response', props()),
};

/* eslint-disable @typescript-eslint/naming-convention */
const NonUSFProductActions = {
    load: createAction('[Non USF Product] Load Non USF Products Synchronously'),
    loadSuccess: createAction('[Non USF Product] Non USF Products Loaded Successfully', props()),
    loadFail: createAction('[Non USF Product] Load Non USF Products From Domain Api Failed', props()),
    resetStore: createAction('[Non USF Product] Reset Non USF Products in store'),
    create: createAction('[Non USF Product] Create Non USF Product POST', props()),
    createSuccess: createAction('[Non USF Product] Create Non USF Product Successfully', props()),
    createFail: createAction('[Non USF Product] Create Non USF Product Failed', props()),
    delete: createAction('[Non USF Product] Delete Non USF Product', props()),
    deleteSuccess: createAction('[Non USF Product] Delete Non USF Product Successfully', props()),
    deleteFail: createAction('[Non USF Product] Delete Non USF Product Failed', props()),
    select: createAction('[Non USF Product] Select Non Usf Product', props()),
    resetSelections: createAction('[Non USF Product] Reset Selections Non Usf Product'),
    selectAll: createAction('[Non USF Product] Select All Non Usf Product', props()),
    update: createAction('[Non USF Product] Update Non USF Product', props()),
    updateSuccess: createAction('[Non USF Product] Update Non USF Product Successfully', props()),
    updateFail: createAction('[Non USF Product] Update Non USF Product Failed', props()),
    searchNonUsfProductWorksheet: createAction('[Non USF Product] Search Non USF Product Worksheet', props()),
    resetSort: createAction('[Non USF Product] Reset the Sort on Non USF Product Worksheet'),
    setSort: createAction('[Non USF Product] Set the Sort on Non USF Product Worksheet', props()),
    downloadNonUsfProductData: createAction('[Non USF Product] Download Non USF Product Data', props()),
    import: createAction('[Non USF Product] Import Non USF Products', props()),
    importSuccess: createAction('[Non USF Product] Import Non USF Products Successfully', props()),
    importFail: createAction('[Non USF Product] Import Non USF Products Failed', props()),
};

/* eslint-disable @typescript-eslint/naming-convention */
const ExternalVendorActions = {
    load: createAction('[External Vendor] Load Vendors Synchronously'),
    loadSuccess: createAction('[External Vendor] Vendors Loaded Successfully', props()),
    loadFail: createAction('[External Vendor] Load Vendors From Domain Api Failed', props()),
    resetStore: createAction('[External Vendor] Reset vendors in store'),
    create: createAction('[External Vendor] Create External Vendor POST', props()),
    createSuccess: createAction('[External Vendor] Create External Vendor Successfully', props()),
    createFail: createAction('[External Vendor] Create External Vendor Failed', props()),
    delete: createAction('[External Vendor] Delete External Vendor', props()),
    deleteSuccess: createAction('[External Vendor] Delete External Vendor Successfully', props()),
    deleteFail: createAction('[External Vendor] Delete External Vendor Failed', props()),
    edit: createAction('[External Vendor] Edit External Vendor POST', props()),
    editSuccess: createAction('[External Vendor] Edit External Vendor Successfully', props()),
    editFail: createAction('[External Vendor] Edit External Vendor Failed', props()),
    changeSort: createAction('[External Vendor] Change Sort for Vendors Data', props()),
    resetSort: createAction('[External Vendor] Resets the sort for vendors data to the original'),
    setIsSelected: createAction('[External Vendor] Set isSelected attribute on External Vendor', props()),
    domainCallSuccess: createAction('[ExternalVendor] Upserts vendor with currentAction we pass', props()),
};

/* eslint-disable @typescript-eslint/naming-convention */
const BetterBuysActions = {
    load: createAction('[Better Buys] Load Better Buys Synchronously', props()),
    loadSuccess: createAction('[Better Buys] Better Buys Loaded Successfully', props()),
    loadFail: createAction('[Better Buys] Load Better Buys From Domain Api Failed', props()),
    loadSavingsOverTime: createAction('[Better Buys] Load Better Buys Savings Over Time Synchronously', props()),
    loadSavingsOverTimeSuccess: createAction('[Better Buys] Load Better Buys Savings Over Time Loaded Successfully', props()),
    loadSavingsOverTimeFail: createAction('[Better Buys] Load Better Buys Savings Over Time Failed', props()),
    resetStore: createAction('[Better Buys] Reset Better Buys in store'),
    savePricing: createAction('[Better Buys] Save Pricing from better buys into pricing state', props()),
    acceptSuggestion: createAction('[Better Buys] Acccept Better Buys Suggestion', props()),
    acceptSuggestionAndMulesoftSuccess: createAction('[Better Buys] Acccept Better Buys Suggestion and mulesoft api call was successful', props()),
    declineSuggestion: createAction('[Better Buys] Decline Better Buys Suggestion', props()),
    filterOutRestrictedBetterBuys: createAction('[Better Buys] Filter out products that arent on a master list or order guide if customer is restricted', props()),
    filterOutRestrictedBetterBuysSuccess: createAction('[Better Buys] Filtering of products not on Order Guide or Master List succeeded', props()),
    downloadProductConversion: createAction('[Better Buys] Download product convversion data', props()),
};

/* eslint-disable @typescript-eslint/naming-convention */
const MslProductActions = {
    insertMslProducts: createAction('[Msl Product] Insert msl products into state', props()),
    ResetStore: createAction('[Msl Product] Reset msl products in state'),
};

/* eslint-disable @typescript-eslint/naming-convention */
const MerchandisingActions = {
    NOOP: createAction('[NGRXTrace - Merchandising] No Operation'),
    ResetStore: createAction('[NGRXTrace - Merchandising] Reset Merchandising'),
    FetchMerchandisingResult: createAction('[NGRXTrace - Merchandising] Fetch Merchandising Result', props()),
    FetchMerchandisingResultSuccess: createAction('[NGRXTrace - Merchandising] Fetch Merchandising Result Success', props()),
    FetchMerchandisingResultFail: createAction('[NGRXTrace - Merchandising] Fetch Merchandising Result Failure', props()),
    SendPersonalizationEvent: createAction('[NGRXTrace - Merchandising] Send Personalization Event', props()),
};

/* eslint-disable @typescript-eslint/naming-convention */
const PIMClassActions = {
    load: createAction('[PIM Class] Load PIM Class info'),
    loadSuccess: createAction('[PIM Class] Load PIM Class info Loaded Successfully', props()),
    loadFail: createAction('[PIM Class] Load PIM Class info Domain Api Failed', props()),
};

/* eslint-disable @typescript-eslint/naming-convention */
const RevenueManagementActions = {
    ResetStore: createAction('[NGRXTrace - Revenue Management] Reset Revenue Management'),
    FetchRevenueManagementResult: createAction('[NGRXTrace - Revenue Management] Fetch Revenue Management Result'),
    FetchRevenueManagementResultSuccess: createAction('[NGRXTrace - Revenue Management] Fetch Revenue Management Result Success', props()),
    FetchRevenueManagementResultFail: createAction('[NGRXTrace - Revenue Management] Fetch Revenue Management Result Failure'),
};

/* eslint-disable @typescript-eslint/naming-convention */
const BrowseHierarchyActions = {
    ResetStore: createAction('[NGRXTrace - BrowseHierarchy] Reset Browse Hierarchy'),
    LoadBrowseHierarchy: createAction('[NGRXTrace - BrowseHierarchy] Load BrowseHierarchy', props()),
    FetchBrowseHierarchy: createAction('[NGRXTrace - BrowseHierarchy] Fetch BrowseHierarchy', props()),
    FetchBrowseHierarchySuccess: createAction('[NGRXTrace - BrowseHierarchy] Fetch BrowseHierarchy Success', props()),
    FetchBrowseHierarchyFail: createAction('[NGRXTrace - BrowseHierarchy] Fetch BrowseHierarchy Failure', props()),
};

/* eslint-disable @typescript-eslint/naming-convention */
const SneakPeekActions = {
    ResetStore: createAction('[NGRXTrace - Sneak Peek] Reset Sneak Peek'),
    FetchSneakPeekResult: createAction('[NGRXTrace - Sneak Peek] Fetch Sneak Peek Result'),
    FetchSneakPeekResultSuccess: createAction('[NGRXTrace - Sneak Peek] Fetch Sneak Peek Result Success', props()),
    FetchSneakPeekResultFail: createAction('[NGRXTrace - Sneak Peek] Fetch Sneak Peek Result Failure'),
};

/* eslint-disable @typescript-eslint/naming-convention */
const CustomersAlsoBoughtActions = {
    ResetStore: createAction('[NGRXTrace - CAB] Reset CAB'),
    FetchCabProductNumbers: createAction('[NGRXTrace - CAB] Fetch CAB Product Numbers', props()),
    FetchCabProductNumbersSuccess: createAction('[NGRXTrace - CAB] Fetch CAB Product Numbers Success', props()),
    FetchCabProductNumbersFail: createAction('[NGRXTrace - CAB] Fetch CAB Product Numbers Failure', props()),
};

/* eslint-disable @typescript-eslint/naming-convention */
const SellerShowcaseActions = {
    ResetStore: createAction('[NGRXTrace - Seller Showcase] Reset Seller Showcase'),
    FetchSellerShowcaseResult: createAction('[NGRXTrace - Seller Showcase] Fetch Seller Showcase Result'),
    FetchSellerShowcaseResultSuccess: createAction('[NGRXTrace - Seller Showcase] Fetch Seller Showcase Result Success', props()),
    FetchSellerShowcaseResultFail: createAction('[NGRXTrace - Seller Showcase] Fetch Seller Showcase Result Failure'),
};

/* eslint-disable @typescript-eslint/naming-convention */
const CustomerRebateActions = {
    ResetStore: createAction('[NGRXTrace - Customer Rebate] Reset Customer Rebate'),
    FetchCustomerRebates: createAction('[NGRXTrace - Customer Rebate] Fetch Customer Rebates'),
    FetchCustomerRebatesSuccess: createAction('[NGRXTrace - Customer Rebate] Fetch Customer Rebates Success', props()),
    FetchCustomerRebatesFail: createAction('[NGRXTrace - Customer Rebate] Fetch Customer Rebates Failure', props()),
    NOOP: createAction('[NGRXTrace - Customer Rebate] No Operation'),
};

/* eslint-disable @typescript-eslint/naming-convention */
const ProductChangesActions = {
    ResetStore: createAction('[Product Changes] Reset Store'),
    LoadFromStorageSuccess: createAction('[Product Changes] Load From Storage Success', props()),
    LoadFromStorageFail: createAction('[Product Changes] Failed to Load From Storage', props()),
    RequestChanges: createAction('[Product Changes] Request Changes'),
    RequestListItemChanges: createAction('[Product Changes] Request List Item Changes'),
    ListItemsAddedReceived: createAction('[Product Changes] List Items Added Event Received', props()),
    UpdateWatermark: createAction('[Product Changes] Update Watermark', props()),
};

/* eslint-disable @typescript-eslint/naming-convention */
const CustomerPillActions = {
    ResetStore: createAction('[NGRXTrace - Customer Pill] Reset Customer Pills'),
    FetchCustomerPillsSuccess: createAction('[NGRXTrace - Customer Pill] Fetch Customer Pills Success', props()),
    FetchCustomerPillsFail: createAction('[NGRXTrace - Customer Pill] Fetch Customer Pills Fail', props()),
};

/* eslint-disable @typescript-eslint/naming-convention */
const TypeaheadActions = {
    ResetStore: createAction('[NGRXTrace - Typeahead] Reset Typeahead'),
    LoadTypeahead: createAction('[NGRXTrace - Typeahead] Load Typeahead', props()),
    FetchTypeahead: createAction('[NGRXTrace - Typeahead] Search Typeahead', props()),
    FetchTypeaheadSuccess: createAction('[NGRXTrace - Typeahead] Search Typeahead Success', props()),
    FetchTypeaheadFail: createAction('[NGRXTrace - Typeahead] Search Typeahead Failure', props()),
};

const TEAM_CAPABILITY = 'product discovery';
const LISTS_CAPABILITY = 'lists';
const ALERTS_CAPABILITY = 'alerts';
const PAGE_NAME_BASE_PATH = {
    r4: 'us foods:r4:',
};
const EVENT = {
    pageLoaded: 'page loaded',
    featureLoaded: 'feature loaded',
    searchSubmit: 'search submit',
    searchResultClick: 'search result click',
    loadMore: 'load more',
    reviewEngagement: 'review engagement',
    reviewSubmit: 'submit review',
    filtersApplied: 'filters applied',
    qaEngangement: 'qa engagement',
    submitQuestion: 'submit question',
    opportunityCancel: 'modal close',
    opportunityOpen: 'modal open',
    opportunitySubmit: 'create opportunity',
    opportunityContinue: 'publish to ecomm',
    promoBannerClick: 'promo banner click',
    customPricingRationaleUpdate: 'custom pricing rationale submit',
    cprModalOpen: 'modal open',
    sharePrintDownload: 'share print download',
    buttonClick: 'button click',
    compareSubmit: 'compare products',
    acceptSwap: 'accept swap',
    rejectSwap: 'reject swap',
    productCompare: 'product compare',
    modalClosed: 'modal closed',
    featureEngagement: 'feature engagement',
    modalOpen: 'modal open',
    filterClick: 'filter click',
    addNonUsfProduct: 'add non usf product',
    linkClick: 'link click',
    pdpEngagement: 'pdp engagement',
    directInfoClick: 'direct info click',
    productImageClick: 'product image click',
};
const CREATE_OPPORTUNITY = {
    publishNote: '1',
    modalName: 'create opportunity',
};
const PAGE_DETAIL = {
    productDetails: 'pdp:product details',
    sellerDetails: 'pdp:seller details',
    sellerQuickView: 'pdp:seller quick view',
    searchDetails: 'search',
    catalogDetails: 'catalog',
    customerIncentive: 'customer incentive',
};
const PRODUCT_ATTRIBUTE_TRACK = {
    contract: 'contract',
    direct: 'direct',
    locallySourced: 'locally sourced',
    ces: 'ces',
    specialOrder: 'special order',
    scoop: 'scoop',
    dwo: 'dwo',
    discontinued: 'discontinued',
    recentlyPurchased: 'recently purchased',
    jit: 'jit',
    supplierOutOfStock: 'temporarily unavailable',
    outOfStock: 'out of stock',
    onOrderGuide: 'on list',
    specialVendor: 'special vendor',
    onShoppingList: 'on list',
    onOrderGuideOrOnShoppingList: 'on list',
    onMasterList: 'on list',
    availableToPromise: 'ATP',
    mslRestricted: 'msl restricted',
    proprietary: 'proprietary',
};
const SEARCH_PAGE_TRACK = {
    searchResults: 'search results',
    searchSubmit: 'search submit',
};
const SEARCH_TYPE$1 = {
    catalogSearch: 'catalog search',
    catalogSearchWithin: 'browse products search within',
    searchWithinSearch: 'catalog search',
    typeAhead: 'type ahead search',
    searchForSubs: 'search for subs',
};
const REVIEW_PRODUCT = {
    writeReview: 'write review',
    askQuestion: 'ask questions',
    submitQuestion: 'submit question',
};
const TM_NOTES_DETAIL_PAGE = {
    promoView: '1',
    merchFeatureView: '1',
    tmNotesDetailPage: 'tm notes:note details',
    idPrefix: 'TMN-',
};
//  MERCH_FEATURE  is present in CUSTOMER_ALSO_BOUGHT,YOU_MAY_ALSO_NEED AND TM_NOTES
const MERCH_FEATURE = {
    tmNotesName: 'tm notes',
    tmNotesView: '1',
    customerAlsoBoughtName: 'customers also bought',
    customerAlsoBoughtView: '1',
    youMayAlsoNeedName: 'you may also need',
    youMayAlsoNeedView: '1',
    betterBuys: 'better buys',
};
const DEALS = {
    pageName: 'us foods:r4:deal products',
};
const SUBSTITUTE_TYPE = {
    divsionSub: 'div',
    mslSub: 'msl',
};
const CPR = {
    modalName: 'custom pricing rationale',
};
const SHARE_TYPE = {
    fileType: 'share:pdp',
};
const FILE_TYPE = {
    fileType: 'print:pdp',
};
const PRODUCT_COMPARISON_PRINT = {
    type: 'print:product comparison',
};
const PRODUCT_COMPARISON_DOWNLOAD = {
    type: 'download:product comparison',
};
const CUSTOM_PRICING_RATIONALE = {
    cprFlag: '1',
};
const BUTTON_NAME = {
    buttonOn: 'toggle us foods delivered on',
    buttonOff: 'toggle us foods delivered off',
    compareProducts: 'compare products',
    serveYou: 'serve you:header',
};
const MONETATE_EVENT_NAMES = {
    setPageType: 'setPageType',
    addProductDetails: 'addProductDetails',
    addProducts: 'addProducts',
};
const MONETATE_ENGINE_NAMES = {
    productDetailView: 'monetate:context:ProductDetailView',
    pageView: 'monetate:context:PageView',
};
const PAGE_PRODUCT_COMPARE = {
    productCompare: 'product compare',
};
const BETTER_BUYS = {
    betterBuys: 'better buys',
};
const ENGAGEMENT = {
    engagementType: 'works well with view',
};
const MERCH_FEATURES = {
    worksWellWith: 'works well with',
};
const ACTIVE_PROMO = {
    promoId: 'deals for you',
};
const SCOOP_PREVIEW = {
    promoId: 'scoop preview',
};
const MONETATE_ADD_SEARCH = {
    addSearch: 'addSearch',
    searchType: 'site',
};
const NON_USF_PRODUCTS = {
    manageNonUsf: 'manage non usf products',
};
const SCOOP = {
    pageName: 'scoop preview',
};
const SELLER_INCENTIVES = {
    pageName: 'seller incentives',
};
const SEARCH_ENGINE = {
    coveo: 'coveo',
    endeca: 'endeca',
};
const GUEST_LANDING_PAGE = {
    variantName: 'guest landing page',
    becomeACustomer: 'become a customer',
};

const PRODUCTS_KEY = 'products';
const PRODUCTS_DOMAIN_NAME = 'products';
const PRODUCTS_WATERMARK_KEY = 'products-watermark';
const MY_PRODUCTS_KEY = 'my-products';
const PRODUCT_SUMMARY_DETAIL_KEY$1 = 'product-summary-detail';
const PRODUCT_DETAIL_KEY$1 = 'product-detail';
const PRODUCT_CHANGES_KEY = 'product-changes';
const MY_PRODUCTS_WATERMARK_KEY = 'my-products-watermark';
const PRODUCT_INVENTORY_KEY = 'product-inventories';
const PRODUCT_SUMMARY_KEY = 'product-summaries';
const PRODUCT_CONTRACT_KEY = 'product-contracts';
const CUSTOMER_PILL_KEY = 'customer-pills';
const SEARCH_RESPONSE_KEY = 'search-responses';
const SEARCH_RESPONSE_V2_KEY = 'search-responses-v2';
const SEARCH_TYPEAHEAD_KEY = 'search-typeahead';
const PRODUCT_SCOOP_KEY = 'product-scoops';
const BROWSE_CATEGORIES_KEY = 'browse-categories';
const PRODUCT_OPPORTUNITY_KEY = 'product-opportunities';
const POWER_REVIEW_PRODUCT_KEY = 'power-review-product';
const NON_USF_PRODUCT_KEY = 'non-usf-products';
const EXTERNAL_VENDOR_KEY = 'external-vendors';
const BETTER_BUYS_KEY = 'better-buys';
const PRICING_KEY = 'pricing';
const MSL_PRODUCT_KEY = 'msl-products';
const MERCHANDISING_KEY = 'merchandising';
const PERSONALIZATION_KEY = 'personalization';
const PIM_CLASS_KEY = 'pim-class';
const REVENUE_MANAGEMENT_KEY = 'revenue-management';
const CAB_KEY = 'customers-also-bought';
const BROWSE_HIERARCHY_KEY = 'browse-hierarchy';
const SNEAK_PEEK_KEY = 'sneak-peek';
const SELLER_SHOWCASE_KEY = 'seller-showcase';
const CUSTOMER_REBATE_KEY = 'customer-rebate';
const REBATE_TAKEOUT_OPTIONS_KEY = 'rebate-takeout-options';
const PRODUCT_STORAGE_KEYS = [
    PRODUCTS_KEY,
    PRODUCT_CHANGES_KEY,
    MY_PRODUCTS_KEY,
    PRODUCT_SUMMARY_DETAIL_KEY$1,
    PRODUCT_DETAIL_KEY$1,
    PRODUCT_INVENTORY_KEY,
    PRODUCT_SUMMARY_KEY,
    PRODUCT_CONTRACT_KEY,
    CUSTOMER_PILL_KEY,
    SEARCH_RESPONSE_KEY,
    SEARCH_RESPONSE_V2_KEY,
    SEARCH_TYPEAHEAD_KEY,
    PRODUCT_SCOOP_KEY,
    BROWSE_CATEGORIES_KEY,
    PRODUCT_OPPORTUNITY_KEY,
    POWER_REVIEW_PRODUCT_KEY,
    NON_USF_PRODUCT_KEY,
    EXTERNAL_VENDOR_KEY,
    BETTER_BUYS_KEY,
    PRICING_KEY,
    MSL_PRODUCT_KEY,
    MERCHANDISING_KEY,
    PIM_CLASS_KEY,
    REVENUE_MANAGEMENT_KEY,
    BROWSE_HIERARCHY_KEY,
    SNEAK_PEEK_KEY,
    CAB_KEY,
    CUSTOMER_REBATE_KEY,
    REBATE_TAKEOUT_OPTIONS_KEY,
];

const MY_PRODUCTS_BATCH_SIZE = 100;

const PERSONALIZATION_TYPE = {
    recentlyViewed: 'RECENTLY_VIEWED',
};

/* eslint-disable no-shadow */
var VendorColumns;
(function (VendorColumns) {
    VendorColumns["vendorCode"] = "vendorId";
    VendorColumns["vendorName"] = "distributorName";
    VendorColumns["contactName"] = "contactName";
    VendorColumns["cityState"] = "city";
    VendorColumns["phoneNumber"] = "telephoneNumber";
    VendorColumns["email"] = "email";
})(VendorColumns || (VendorColumns = {}));

/* eslint-disable prefer-arrow/prefer-arrow-functions */
const productContractAdapter = createEntityAdapter({
    selectId: productContract => productContract.productNumber,
});
const notReadyProductContractState = productContractAdapter.getInitialState({
    loadingState: LoadingState.notReady,
});
const initialProductContractState = productContractAdapter.getInitialState({ loadingState: LoadingState.init });
// eslint-disable-next-line no-underscore-dangle
const _productContractReducer = createReducer(notReadyProductContractState, 
// Reset store
on(ProductContractActions.ResetStore, () => initialProductContractState), 
//Set this elements to be on a 'loading' state
on(ProductContractActions.FetchProductContracts, (state, { productNumbers }) => {
    const updateChanges = productNumbers.map(productNumber => ({
        loadingState: LoadingState.loading,
        productNumber,
        client: undefined,
        clientConcept: undefined,
        startDate: undefined,
        endDate: undefined,
        contractNumber: undefined,
        groupNumber: undefined,
        iconId: undefined,
        iconFile: undefined,
        iconText: undefined,
        iconDescription: undefined,
    }));
    return productContractAdapter.upsertMany(updateChanges, state);
}), on(ProductActions.FetchProductOrderabilities, (state, { productNumbers }) => {
    const updateChanges = productNumbers.map(productNumber => ({
        loadingState: LoadingState.loading,
        productNumber,
        client: undefined,
        clientConcept: undefined,
        startDate: undefined,
        endDate: undefined,
        contractNumber: undefined,
        groupNumber: undefined,
        iconId: undefined,
        iconFile: undefined,
        iconText: undefined,
        iconDescription: undefined,
    }));
    return productContractAdapter.upsertMany(updateChanges, state);
}), 
//Store retrieved product inventories and mark them as 'loaded'
//if a product was not found mark it loaded and not found
on(ProductContractActions.FetchProductContractsSuccess, (state, { requestedProductNumbers, productContracts }) => {
    const notRetrievedProductContractChanges = [];
    const retrievedSet = new Set();
    const retrievedProductContractChanges = productContracts?.map(productContract => {
        retrievedSet.add(productContract.productNumber);
        return {
            ...productContract,
            loadingState: LoadingState.loaded,
        };
    });
    requestedProductNumbers.forEach(prodNumber => {
        if (!retrievedSet.has(prodNumber)) {
            notRetrievedProductContractChanges.push({
                productNumber: prodNumber,
                loadingState: LoadingState.notFound,
            });
        }
    });
    const allChanges = [
        ...notRetrievedProductContractChanges,
        ...retrievedProductContractChanges,
    ];
    return productContractAdapter.upsertMany(allChanges, state);
}), on(ProductActions.LoadSlimProductsSuccess, (state, { requestedProductNumbers, products }) => {
    const contractsToInsert = [];
    products.forEach(product => {
        const currentContract = state.entities[product?.productNumber];
        if (currentContract?.loadingState !== LoadingState.loaded &&
            currentContract?.loadingState !== LoadingState.notFound) {
            contractsToInsert.push({
                productNumber: product?.productNumber,
                loadingState: product?.notFound
                    ? LoadingState.notFound
                    : LoadingState.slim,
            });
        }
    });
    return productContractAdapter.upsertMany(contractsToInsert, state);
}), 
// On failure the entity will go back to not be loading and not loaded
// this will allow for further requests to retry to fetch this entity
on(ProductContractActions.FetchProductContractsFail, (state, { productNumbers }) => {
    const updateChanges = productNumbers.map(productNumber => ({
        productNumber,
        loadingState: LoadingState.error,
    }));
    return productContractAdapter.upsertMany(updateChanges, state);
}), on(ProductContractActions.loadFromStorageSuccess, (state, productContractState) => ({
    ...state,
    productContractState,
})));
function productContractReducer(state, action) {
    return _productContractReducer(state, action);
}

const { selectAll: selectAll$8, selectEntities: selectEntities$8 } = productContractAdapter.getSelectors();
const selectProductContractsState = createFeatureSelector(PRODUCT_CONTRACT_KEY);
const isProductContractStateReady = createSelector(selectProductContractsState, state => state.loadingState === LoadingState.init);
const selectAllProductContracts = createSelector(selectProductContractsState, selectAll$8);
const selectProductContractEntities = createSelector(selectProductContractsState, selectEntities$8);
const selectCompletedProductContracts = createSelector(selectAllProductContracts, productContracts => productContracts.filter(productContract => productContract.loadingState === LoadingState.loaded ||
    productContract.loadingState === LoadingState.notFound));
const selectPendingProductContracts = (productNumbers) => createSelector(selectProductContractEntities, productContracts => productNumbers.filter(productNumber => {
    const productContractState = productContracts[productNumber];
    if (!productContractState) {
        // Element wasn't found in the state, so we need to fetch it
        return true;
    }
    // Or element is in hasn't been fetched yet
    return productContractState.loadingState === LoadingState.init;
}));

/* eslint-disable prefer-arrow/prefer-arrow-functions */
const productInventoryAdapter = createEntityAdapter({
    selectId: productInventory => productInventory.productNumber,
});
const notReadyProductInventoryState = productInventoryAdapter.getInitialState({
    loadingState: LoadingState.notReady,
});
const initialProductInventoryState = productInventoryAdapter.getInitialState({ loadingState: LoadingState.init });
// eslint-disable-next-line no-underscore-dangle
const _productInventoryReducer = createReducer(notReadyProductInventoryState, 
// Reset store
on(ProductInventoryActions.ResetStore, () => initialProductInventoryState), 
//Set this elements to be on a 'loading' state
on(ProductInventoryActions.FetchProductInventories, (state, { productNumbers }) => {
    const updateChanges = productNumbers.map(productNumber => ({
        loadingState: LoadingState.loading,
        productNumber,
        divisionNumber: undefined,
        casesOnHand: undefined,
        casesOnReserve: undefined,
        casesWaitlistReserve: undefined,
        casesToEachDemand: undefined,
        eachOnHand: undefined,
        eachOnReserve: undefined,
        eachWaitListReserve: undefined,
        eachConversionFactor: undefined,
    }));
    return productInventoryAdapter.upsertMany(updateChanges, state);
}), on(ProductActions.FetchProductOrderabilities, (state, { productNumbers }) => {
    const updateChanges = productNumbers.map(productNumber => ({
        loadingState: LoadingState.loading,
        productNumber,
        divisionNumber: undefined,
        casesOnHand: undefined,
        casesOnReserve: undefined,
        casesWaitlistReserve: undefined,
        casesToEachDemand: undefined,
        eachOnHand: undefined,
        eachOnReserve: undefined,
        eachWaitListReserve: undefined,
        eachConversionFactor: undefined,
    }));
    return productInventoryAdapter.upsertMany(updateChanges, state);
}), 
//Store retrieved product inventories and mark them as 'loaded'
//if a product was not found mark it as a not loaded and not loading so that it can be retried
on(ProductInventoryActions.FetchProductInventoriesSuccess, (state, { requestedProductNumbers, productInventories }) => {
    const notRetrievedProductInventoriesChanges = [];
    const retrievedSet = new Set();
    const retrievedProductInventoriesChanges = productInventories?.map(productInventory => {
        retrievedSet.add(productInventory.productNumber);
        return {
            ...productInventory,
            loadingState: LoadingState.loaded,
        };
    });
    requestedProductNumbers.forEach(prodNumber => {
        if (!retrievedSet.has(prodNumber)) {
            notRetrievedProductInventoriesChanges.push({
                productNumber: prodNumber,
                loadingState: LoadingState.notFound,
            });
        }
    });
    const allChanges = [
        ...notRetrievedProductInventoriesChanges,
        ...retrievedProductInventoriesChanges,
    ];
    return productInventoryAdapter.upsertMany(allChanges, state);
}), on(ProductActions.LoadSlimProductsSuccess, (state, { requestedProductNumbers, products }) => {
    const notRetrievedProductInventoriesChanges = [];
    const retrievedSet = new Set();
    const retrievedProductInventoriesChanges = products
        ?.filter(product => {
        const inventoryInState = state.entities[product?.productNumber];
        if (inventoryInState?.loadingState === LoadingState.loaded) {
            retrievedSet.add(product?.productNumber);
        }
        return (!!product.inventory &&
            inventoryInState?.loadingState !== LoadingState.loaded);
    })
        ?.map(product => {
        retrievedSet.add(product?.inventory.productNumber);
        return {
            ...product?.inventory,
            loadingState: LoadingState.slim,
        };
    });
    requestedProductNumbers.forEach(prodNumber => {
        if (!retrievedSet.has(prodNumber)) {
            notRetrievedProductInventoriesChanges.push({
                productNumber: prodNumber,
                loadingState: LoadingState.notFound,
            });
        }
    });
    const allChanges = [
        ...notRetrievedProductInventoriesChanges,
        ...retrievedProductInventoriesChanges,
    ];
    return productInventoryAdapter.upsertMany(allChanges, state);
}), 
// On failure the entity will go back to not be loading and not loaded
// this will allow for further requests to retry to fetch this entity
on(ProductInventoryActions.FetchProductInventoriesFail, ProductActions.LoadSlimProductsFail, (state, { productNumbers }) => {
    const updateChanges = productNumbers.map(productNumber => ({
        productNumber,
        loadingState: LoadingState.error,
    }));
    return productInventoryAdapter.upsertMany(updateChanges, state);
}), on(ProductInventoryActions.loadFromStorageSuccess, (state, productInventoryState) => ({
    ...state,
    productInventoryState,
})));
function productInventoryReducer(state, action) {
    return _productInventoryReducer(state, action);
}

const { selectAll: selectAll$7, selectEntities: selectEntities$7 } = productInventoryAdapter.getSelectors();
const selectProductInventoriesState = createFeatureSelector(PRODUCT_INVENTORY_KEY);
const isProductInventoryStateReady = createSelector(selectProductInventoriesState, state => state.loadingState === LoadingState.init);
const selectAllProductInventories = createSelector(selectProductInventoriesState, selectAll$7);
const selectProductInventoryEntities = createSelector(selectProductInventoriesState, selectEntities$7);
const selectCompletedProductInventories = createSelector(selectAllProductInventories, productInventories => productInventories.filter(productInventory => productInventory.loadingState === LoadingState.loaded ||
    productInventory.loadingState === LoadingState.notFound));
const selectPendingProductInventories = (productNumbers) => createSelector(selectProductInventoryEntities, productInventories => productNumbers.filter(productNumber => {
    const productInventory = productInventories[productNumber];
    if (!productInventory) {
        // Element wasn't found in the state, so we need to fetch it
        return true;
    }
    // Or element is in hasn't been fetched yet
    return productInventory.loadingState === LoadingState.init;
}));

const hasContract = (product, currentDate) => dayjs(product?.contract?.startDate).isBefore(currentDate) &&
    dayjs(product?.contract?.endDate).isAfter(currentDate);
const isSupplierOutOfStock = (productInventory) => productHasStatus('2', productInventory);
const isScoop = (productSummary, productScoops) => !!productScoops && !!productScoops[productSummary?.productNumber];
const productHasStatus = (status, productInventory) => productInventory?.productStatus === status;
const getPropertiesSet = (product, scoops, currentDate = dayjs()) => {
    const propertiesSet = new Set();
    if (product?.inventory?.available) {
        propertiesSet.add(ProductPropertiesEnum.available);
    }
    if (product?.inventory?.isCES) {
        propertiesSet.add(ProductPropertiesEnum.ces);
    }
    if (product?.inventory?.isDirect) {
        propertiesSet.add(ProductPropertiesEnum.direct);
    }
    if (product?.inventory?.isSpecialOrder) {
        propertiesSet.add(ProductPropertiesEnum.specialOrder);
    }
    if (product?.inventory?.isSpecialVendor) {
        propertiesSet.add(ProductPropertiesEnum.specialVendor);
    }
    if (product?.summary?.locallySourceIndicator) {
        propertiesSet.add(ProductPropertiesEnum.locallySourced);
    }
    if (hasContract(product, currentDate)) {
        propertiesSet.add(ProductPropertiesEnum.contract);
    }
    if (product?.customerPill?.pillText) {
        propertiesSet.add(ProductPropertiesEnum.customerPill);
    }
    if (product?.inventory?.justInTime) {
        propertiesSet.add(ProductPropertiesEnum.jit);
    }
    if (!product?.inventory?.isInStock) {
        propertiesSet.add(ProductPropertiesEnum.outOfStock);
    }
    if (product?.inventory?.productStatus === '2' &&
        (product?.inventory?.isInStock === false ||
            product?.inventory?.isDirect === true)) {
        propertiesSet.add(ProductPropertiesEnum.supplierOutOfStock);
    }
    if (product?.inventory?.productStatus === '9') {
        propertiesSet.add(ProductPropertiesEnum.discontinued);
    }
    if (product?.inventory?.isDwo) {
        propertiesSet.add(ProductPropertiesEnum.dwo);
    }
    if (isScoop(product?.summary, scoops)) {
        propertiesSet.add(ProductPropertiesEnum.scoop);
    }
    if (isOnMasterList(product?.summary)) {
        propertiesSet.add(ProductPropertiesEnum.onMasterList);
    }
    return propertiesSet;
};
const isOnMasterList = (productSummary) => (productSummary?.propertiesArray ?? []).includes(ProductPropertiesEnum.onMasterList);

/* eslint-disable prefer-arrow/prefer-arrow-functions */
const productSummaryAdapter = createEntityAdapter({
    selectId: productSummary => productSummary.productNumber,
});
const notReadySearchResponseState$1 = productSummaryAdapter.getInitialState({
    loadingState: LoadingState.notReady,
});
const initialProductSummaryState = productSummaryAdapter.getInitialState({ loadingState: LoadingState.init });
// eslint-disable-next-line no-underscore-dangle
const _productSummaryReducer = createReducer(notReadySearchResponseState$1, 
// Reset store
on(ProductSummaryActions.ResetStore, () => initialProductSummaryState), 
//Set this elements to be on a 'loading' state
on(ProductSummaryActions.FetchProductSummaries, (state, { productNumbers }) => {
    const updateChanges = productNumbers.map(productNumber => ({
        loadingState: LoadingState.loading,
        productNumber,
    }));
    return productSummaryAdapter.upsertMany(updateChanges, state);
}), on(ProductActions.FetchProductOrderabilities, (state, { productNumbers }) => {
    const updateChanges = productNumbers.map(productNumber => ({
        loadingState: LoadingState.loading,
        productNumber,
    }));
    return productSummaryAdapter.upsertMany(updateChanges, state);
}), 
//Store retrieved product summaries and mark them as 'loaded'
//if a product was not found mark it as a not loaded and not loading so that it can be retried
on(ProductSummaryActions.FetchProductSummariesSuccess, (state, { requestedProductNumbers, productSummaries }) => {
    const notRetrievedProductSummaryChanges = [];
    const retrievedSet = new Set();
    const retrievedProductSummaryChanges = productSummaries?.map(productSummary => {
        retrievedSet.add(productSummary.productNumber);
        return {
            ...productSummary,
            loadingState: LoadingState.loaded,
        };
    });
    requestedProductNumbers.forEach(prodNumber => {
        if (!retrievedSet.has(prodNumber)) {
            notRetrievedProductSummaryChanges.push({
                productNumber: prodNumber,
                loadingState: LoadingState.notFound,
            });
        }
    });
    const allChanges = [
        ...notRetrievedProductSummaryChanges,
        ...retrievedProductSummaryChanges,
    ];
    return productSummaryAdapter.upsertMany(allChanges, state);
}), on(ProductActions.LoadSlimProductsSuccess, (state, { requestedProductNumbers, products }) => {
    const notRetrievedProductSummaryChanges = [];
    const retrievedSet = new Set();
    const retrievedProductSummaryChanges = products
        ?.filter(product => {
        const summaryInState = state.entities[product?.productNumber];
        if (summaryInState?.loadingState === LoadingState.loaded) {
            retrievedSet.add(product?.productNumber);
        }
        return (!!product.summary &&
            summaryInState?.loadingState !== LoadingState.loaded);
    })
        ?.map(product => {
        retrievedSet.add(product?.summary.productNumber);
        return {
            ...product?.summary,
            loadingState: LoadingState.slim,
            propertiesArray: Array.from(getPropertiesSet(product, {})),
        };
    });
    requestedProductNumbers.forEach(prodNumber => {
        if (!retrievedSet.has(prodNumber)) {
            notRetrievedProductSummaryChanges.push({
                productNumber: prodNumber,
                loadingState: LoadingState.notFound,
            });
        }
    });
    const allChanges = [
        ...notRetrievedProductSummaryChanges,
        ...retrievedProductSummaryChanges,
    ];
    return productSummaryAdapter.upsertMany(allChanges, state);
}), 
// On failure the entity will go back to not be loading and not loaded
// this will allow for further requests to retry to fetch this entity
on(ProductSummaryActions.FetchProductSummariesFail, ProductActions.LoadSlimProductsFail, (state, { productNumbers }) => {
    const updateChanges = productNumbers.map(productNumber => ({
        productNumber,
        loadingState: LoadingState.error,
    }));
    return productSummaryAdapter.upsertMany(updateChanges, state);
}), 
//
on(ProductSummaryActions.LoadFromStorageSuccess, (state, { summaries }) => {
    const productSummaryEntities = {};
    for (const key in summaries.entities) {
        if (summaries.entities.hasOwnProperty(key)) {
            productSummaryEntities[key] = { ...summaries.entities[key] };
        }
    }
    if (Object.keys(productSummaryEntities).length > 0) {
        const newState = {
            ...summaries,
            entities: productSummaryEntities,
            loadingState: LoadingState.init,
        };
        return {
            ...state,
            ...newState,
        };
    }
    else {
        return initialProductSummaryState;
    }
}), 
// Load summary CDC changes
on(ProductSummaryActions.LoadProductSummariesChanges, (state, { content }) => {
    const changes = content;
    // Guard
    if (!!changes || !Array.isArray(changes)) {
        return state;
    }
    // Consolidate changes into single collections
    let summaries = [];
    let productNumbers = [];
    let divisionNumber = null;
    if (!!changes && Array.isArray(changes)) {
        Array.from(changes).forEach(change => {
            productNumbers = [...productNumbers, change?.data?.productNumbers];
            summaries = [...summaries, change?.data?.summaries];
            if (!!divisionNumber && !!change?.data?.divisionNumber) {
                divisionNumber = change?.data?.divisionNumber;
            }
        });
    }
    const requestedProductNumbers = productNumbers;
    const productSummaries = summaries;
    const notRetrievedProductSummariesChanges = requestedProductNumbers
        .filter(requestedProductNumber => !productSummaries.some(productSummary => productSummary.productNumber === requestedProductNumber))
        .map(requestedProductNumber => ({
        productNumber: requestedProductNumber,
        loadingState: LoadingState.notFound,
    }));
    const retrievedProductSummariesChanges = productSummaries.map(productSummary => ({
        ...productSummary,
        loadingState: LoadingState.loaded,
    }));
    const allChanges = [
        ...notRetrievedProductSummariesChanges,
        ...retrievedProductSummariesChanges,
    ];
    // TODO include only items that either are currently in the store
    // TODO or include those that are included in the myProducts list
    return productSummaryAdapter.upsertMany(allChanges, state);
}), on(ProductSummaryActions.UpdateProductSummariesWithMslData, (state, action) => {
    const productSummaryStates = action.productSummaries.map(summary => {
        const summaryState = {
            ...summary,
            loadingState: LoadingState.loaded,
        };
        return summaryState;
    });
    return productSummaryAdapter.upsertMany(productSummaryStates, state);
}));
function productSummaryReducer(state, action) {
    return _productSummaryReducer(state, action);
}

const getProductAlternates = (products) => {
    if (!!products) {
        return Array.from(products.reduce((alternatives, product) => {
            if (product.summary?.replacementProductNumber > 0) {
                alternatives.add(product.summary.replacementProductNumber);
            }
            product?.inventory?.substituteProducts
                ?.filter(sub => sub.productNumber > 0)
                .forEach(sub => alternatives.add(sub.productNumber));
            return alternatives;
        }, new Set()));
    }
    return [];
};

const { selectAll: selectAll$6, selectEntities: selectEntities$6 } = productSummaryAdapter.getSelectors();
const selectProductSummariesState = createFeatureSelector(PRODUCT_SUMMARY_KEY);
const isProductSummaryStateReady = createSelector(selectProductSummariesState, state => state.loadingState === LoadingState.init);
const selectAllProductSummaries = createSelector(selectProductSummariesState, selectAll$6);
const selectProductSummaryEntities = createSelector(selectProductSummariesState, selectEntities$6);
const selectCompletedProductSummaries = createSelector(selectAllProductSummaries, productSummaries => productSummaries.filter(productSummary => productSummary.loadingState === LoadingState.loaded ||
    productSummary.loadingState === LoadingState.slim ||
    productSummary.loadingState === LoadingState.notFound));
const selectPendingProductSummaries = (productNumbers) => createSelector(selectProductSummaryEntities, productSummaries => productNumbers.filter(productNumber => {
    const productSummary = productSummaries[productNumber];
    if (!productSummary) {
        // Element wasn't found in the state, so we need to fetch it
        return true;
    }
    // consider subs/replacements being loaded or not.
    if (productSummary.loadingState === LoadingState.loaded) {
        const pendingAlternative = Array.from(getProductAlternates([productSummary])).filter(altProductNumber => !productSummaries[altProductNumber]);
        return pendingAlternative.length > 0;
    }
    // Or element is in hasn't been fetched yet
    return productSummary.loadingState === LoadingState.init;
}));

const selectSearchResponseStateV2 = createFeatureSelector(SEARCH_RESPONSE_V2_KEY);
const isSearchStateReadyV2 = createSelector(selectSearchResponseStateV2, state => state.loadingState === searchLoadingState.init);
const selectSearchResponseById = (correlationId) => createSelector(selectSearchResponseStateV2, (searchStateV2) => searchStateV2?.entities[correlationId]);

/* eslint-disable prefer-arrow/prefer-arrow-functions */
const searchResponseAdapter = createEntityAdapter({
    selectId: searchResponse => 0,
});
const notReadySearchResponseState = searchResponseAdapter.getInitialState({ loadingState: LoadingState.notReady });
const initialSearchResponseState = searchResponseAdapter.getInitialState({ loadingState: LoadingState.init });
// eslint-disable-next-line no-underscore-dangle
const _searchResponseReducer = createReducer(notReadySearchResponseState, 
// Reset store
on(SearchActions.ResetStore, () => initialSearchResponseState), 
//Set this elements to be on a 'loading' state
on(SearchActions.FetchSearchResponse, state => {
    const updateChange = {
        loadingState: LoadingState.loading,
    };
    return searchResponseAdapter.upsertOne(updateChange, state);
}), 
// On failure the entity will go back to not be loading and not loaded
// this will allow for further requests to retry to fetch this entity
on(SearchActions.FetchSearchResponseSuccess, (state, { result }) => {
    const updateChange = {
        loadingState: LoadingState.loaded,
        ...result,
    };
    return searchResponseAdapter.setOne(updateChange, state);
}), on(SearchActions.FetchSearchResponseFail, (state, { result }) => {
    const updateChange = {
        loadingState: LoadingState.error,
        ...result,
    };
    return searchResponseAdapter.upsertOne(updateChange, state);
}));
function searchResponseReducer(state, action) {
    return _searchResponseReducer(state, action);
}

const selectSearchResponseState = createFeatureSelector(SEARCH_RESPONSE_KEY);
const isSearchStateReady = createSelector(selectSearchResponseState, state => state.loadingState === LoadingState.init);
const selectSearchResponse = state => searchResponseAdapter
    .getSelectors(selectSearchResponseState)
    .selectAll(state)[0];
const isSearchDataLoaded = () => createSelector(selectSearchResponseState, state => state?.loadingState === LoadingState.loaded);
const { selectIds: selectSearchIds, selectEntities: selectSearchEntities, selectAll: selectAllSearches, selectTotal: selectTotalSearches, } = searchResponseAdapter.getSelectors(selectSearchResponseState);

/* eslint-disable prefer-arrow/prefer-arrow-functions */
const typeaheadAdapter = createEntityAdapter({
    selectId: typeahead => typeahead.searchTerm,
});
const notReadyTypeaheadState = typeaheadAdapter.getInitialState({
    loadingState: LoadingState.notReady,
});
const initialTypeaheadState = typeaheadAdapter.getInitialState({
    loadingState: LoadingState.init,
});
// eslint-disable-next-line no-underscore-dangle
const _typeaheadReducer = createReducer(notReadyTypeaheadState, 
//
on(TypeaheadActions.ResetStore, () => initialTypeaheadState), 
//
on(TypeaheadActions.FetchTypeahead, (state, action) => {
    const updateChange = {
        searchTerm: action.searchTerm,
        loadingState: LoadingState.loading,
    };
    return typeaheadAdapter.upsertOne(updateChange, state);
}), on(TypeaheadActions.FetchTypeaheadSuccess, (state, { result }) => {
    const updateChange = {
        loadingState: LoadingState.loaded,
        ...result.typeaheadResult,
    };
    return typeaheadAdapter.upsertOne(updateChange, state);
}), on(TypeaheadActions.FetchTypeaheadFail, (state, { searchTerm }) => {
    const updateChange = {
        loadingState: LoadingState.error,
        searchTerm,
    };
    return typeaheadAdapter.upsertOne(updateChange, state);
}));
function typeaheadReducer(state, action) {
    return _typeaheadReducer(state, action);
}

const { selectEntities: selectEntities$5 } = typeaheadAdapter.getSelectors();
const selectTypeaheadState = createFeatureSelector(SEARCH_TYPEAHEAD_KEY);
const isTypeaheadStateReady = createSelector(selectTypeaheadState, state => state.loadingState === LoadingState.init);
const selectTypeaheadEntities = createSelector(selectTypeaheadState, selectEntities$5);
const selectLoadedTypeahead = searchTerm => createSelector(selectTypeaheadEntities, entities => entities[searchTerm].loadingState === LoadingState.loaded
    ? entities[searchTerm]
    : undefined);
const selectPendingTypeahead = searchTerm => createSelector(selectTypeaheadEntities, entities => entities[searchTerm] === undefined ||
    entities[searchTerm].loadingState === LoadingState.init);

/* eslint-disable @typescript-eslint/naming-convention */
const RebateTakeoutOptionsActions = {
    ResetStore: createAction('[NGRXTrace - Rebate Takeout Options] Reset Rebate Takeout Options'),
    FetchRebateTakeoutOptions: createAction('[NGRXTrace - Rebate Takeout Options] Fetch Rebate Takeout Options'),
    FetchRebateTakeoutOptionsSuccess: createAction('[NGRXTrace - Rebate Takeout Options] Fetch Rebate Takeout Options Success', props()),
    FetchRebateTakeoutOptionsFail: createAction('[NGRXTrace - Rebate Takeout Options] Fetch Rebate Takeout Options Failure', props()),
    NOOP: createAction('[NGRXTrace - Rebate Takeout Options] No Operation'),
};

/* eslint-disable @typescript-eslint/naming-convention */
const ResetStore = createAction('[NGRXTrace - Personalization] Reset Personalization');
const GetPersonalizationEvent = createAction('[NGRXTrace - Personalization] Get Personalization Event', props());
const GetPersonalizationEventSuccess = createAction('[NGRXTrace - Personalization] Get Personalization Event Success', props());
const GetPersonalizationEventFail = createAction('[NGRXTrace - Personalization] Get Personalization Event Fail', props());
const PersonalizationActions = {
    ResetStore,
    GetPersonalizationEvent,
    GetPersonalizationEventSuccess,
    GetPersonalizationEventFail,
};

class MyProductFacadeService {
    constructor(store) {
        this.store = store;
    }
}
MyProductFacadeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MyProductFacadeService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
MyProductFacadeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MyProductFacadeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MyProductFacadeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class MerchandisingStoreService {
    constructor(store) {
        this.store = store;
    }
    sendPersonalizationEvent(personalizationType, productNumber) {
        this.store.dispatch(MerchandisingActions.SendPersonalizationEvent({
            personalizationType,
            productNumber,
        }));
    }
}
MerchandisingStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MerchandisingStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
MerchandisingStoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MerchandisingStoreService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MerchandisingStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class ProductSummaryStoreService {
    constructor(browserStorageService, store) {
        this.browserStorageService = browserStorageService;
        this.store = store;
    }
    loadProductSummaries(productNumbers) {
        this.store.dispatch(ProductSummaryActions.LoadProductSummaries({
            productNumbers,
        }));
    }
    loadProductSummary(productNumber) {
        this.loadProductSummaries([productNumber]);
    }
    getFromOfflineStorage(user, customer, storeEntityName) {
        // eslint-disable-next-line max-len
        const dbName = `${user?.userId}_${customer?.divisionNumber}_${customer?.customerNumber}_${customer?.departmentNumber}_${PRODUCTS_DOMAIN_NAME}`;
        return this.browserStorageService.loadFromOfflineStorage(dbName, storeEntityName);
    }
    writeToOfflineStorage(user, customer, storeEntityName, state) {
        // eslint-disable-next-line max-len
        const dbName = `${user?.userId}_${customer?.divisionNumber}_${customer?.customerNumber}_${customer?.departmentNumber}_${PRODUCTS_DOMAIN_NAME}`;
        return this.browserStorageService.indexDB(dbName, storeEntityName, '0', state, MessageTypes.saveBrowserStorage);
    }
}
ProductSummaryStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductSummaryStoreService, deps: [{ token: i1$1.BrowserStorageService }, { token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ProductSummaryStoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductSummaryStoreService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductSummaryStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.BrowserStorageService }, { type: i1.Store }]; } });

class ProductInventoryStoreService {
    constructor(store) {
        this.store = store;
    }
    loadProductInventories(productNumbers) {
        this.store.dispatch(ProductInventoryActions.LoadProductInventories({
            productNumbers,
        }));
    }
    loadProductInventory(productNumber) {
        this.loadProductInventories([productNumber]);
    }
}
ProductInventoryStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductInventoryStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ProductInventoryStoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductInventoryStoreService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductInventoryStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class ProductContractStoreService {
    constructor(store) {
        this.store = store;
    }
    loadProductContracts(productNumbers) {
        this.store.dispatch(ProductContractActions.LoadProductContracts({
            productNumbers,
        }));
    }
    loadProductContract(productNumber) {
        this.loadProductContracts([productNumber]);
    }
}
ProductContractStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductContractStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ProductContractStoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductContractStoreService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductContractStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

/* eslint-disable prefer-arrow/prefer-arrow-functions */
const PRODUCT_DETAIL_KEY = 'productDetail';
const productDetailAdapter = createEntityAdapter({
    selectId: product => product?.productNumber,
});
const initialProductDetailState = productDetailAdapter.getInitialState({
    // additional entity state properties
    loadingState: LoadingState.init,
});
// eslint-disable-next-line no-underscore-dangle
const _productReducer$2 = createReducer(initialProductDetailState, on(ProductActions.FetchProductDetailSuccess, (state, { productNumber, product }) => {
    const productDetail = !!product
        ? {
            ...product,
            loadingState: LoadingState.loaded,
        }
        : {
            productNumber,
            loadingState: LoadingState.notFound,
        };
    return productDetailAdapter.upsertOne(productDetail, state);
}), on(ProductActions.FetchProductDetailFailure, (state, { productNumber }) => {
    const productDetail = {
        productNumber,
        loadingState: LoadingState.error,
    };
    return productDetailAdapter.upsertOne(productDetail, state);
}), on(ProductActions.FetchProductDetailsSuccess, (state, { productNumbers, products }) => {
    const loadedProductDetailSet = new Set();
    const loadedProductDetails = products?.map(product => {
        loadedProductDetailSet.add(product?.productNumber);
        return {
            ...product,
            loadingState: LoadingState.loaded,
        };
    });
    const notFoundProductDetails = productNumbers?.reduce((productDetails, productNumber) => {
        if (!loadedProductDetailSet.has(productNumber)) {
            productDetails?.push({
                productNumber,
                loadingState: LoadingState.notFound,
            });
        }
        return productDetails;
    }, []);
    return productDetailAdapter.upsertMany([...loadedProductDetails, ...notFoundProductDetails], state);
}), on(ProductActions.FetchProductDetailsFailure, (state, { productNumbers }) => {
    const productDetails = productNumbers?.map(productNumber => ({
        productNumber,
        loadingState: LoadingState.error,
    }));
    return productDetailAdapter.upsertMany(productDetails, state);
}));
function productDetailReducer(state, action) {
    return _productReducer$2(state, action);
}

const { selectEntities: selectEntities$4, selectAll: selectAll$5 } = productDetailAdapter.getSelectors();
const selectProductDetailState = createFeatureSelector(PRODUCT_DETAIL_KEY$1);
const selectProdDetailEntities = createSelector(selectProductDetailState, selectEntities$4);
const selectAllProdDetailEntities = createSelector(selectProductDetailState, selectAll$5);
const selectProductDetail = productNumber => createSelector(selectProdDetailEntities, entities => entities[productNumber]);
const selectProductDetails = (productNumbers) => createSelector(selectProdDetailEntities, productDetails => productNumbers.map(productNumber => productDetails[productNumber] ? productDetails[productNumber] : null));
const productDetailLoaded = () => createSelector(selectProductDetailState, state => state?.loadingState === LoadingState.loaded);
const { selectIds: selectProductIds$1, selectEntities: selectProductEntities$1, selectAll: selectAllProducts$1, selectTotal: selectTotalProducts$1, } = productDetailAdapter.getSelectors(selectProductDetailState);

class ProductDetailStoreService {
    constructor(store) {
        this.store = store;
    }
    loadProductDetail(productNumber) {
        this.store.dispatch(ProductActions.FetchProductDetail({
            productNumber,
        }));
    }
    loadProductDetails(productNumbers) {
        this.store.dispatch(ProductActions.FetchProductDetails({
            productNumbers,
        }));
    }
    getProductDetail(productNumber) {
        this.loadProductDetail(productNumber);
        return this.store.select(selectProductDetail(productNumber));
    }
}
ProductDetailStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductDetailStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ProductDetailStoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductDetailStoreService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductDetailStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class ProductScoopStoreService {
    constructor(store) {
        this.store = store;
    }
    loadProductScoops() {
        this.store.dispatch(ProductScoopActions.loadProductScoops());
    }
}
ProductScoopStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductScoopStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ProductScoopStoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductScoopStoreService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductScoopStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const selectRevenueManagementState = createFeatureSelector(REVENUE_MANAGEMENT_KEY);
const isRevenueManagementStateReady = createSelector(selectRevenueManagementState, state => state.loadingState !== LoadingState.notReady);

class RevenueManagementStoreService {
    constructor(store) {
        this.store = store;
    }
    loadRevenueManagement$() {
        return this.store.select(isRevenueManagementStateReady).pipe(first(isReady => isReady), map(isReady => {
            this.store.dispatch(RevenueManagementActions.FetchRevenueManagementResult());
            return isReady;
        }));
    }
}
RevenueManagementStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RevenueManagementStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
RevenueManagementStoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RevenueManagementStoreService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RevenueManagementStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const SEARCH_TYPE = {
    catalogSearch: 'catalog search',
    typeAhead: 'type ahead search',
};
const ACTIONS = {
    searchSubmit: 'Search Submit',
};
const TRACE_CONTEXT = {
    search: 'search',
    typeahead: 'typeahead',
    createOpportunity: 'createOpportunity',
    deals: 'deals',
    browseHierarchy: 'browseHierarchy',
};

const getProductSummaries = (productNumbers, store) => {
    let summaries = [];
    productNumbers.forEach(productNumber => {
        summaries = [
            ...summaries,
            store[PRODUCT_SUMMARY_KEY].entities[productNumber],
        ];
    });
    return summaries;
};
const getProductInventories = (productNumbers, store) => {
    let inventories = [];
    productNumbers.forEach(productNumber => {
        inventories = [
            ...inventories,
            store[PRODUCT_INVENTORY_KEY].entities[productNumber],
        ];
    });
    return inventories;
};
const getProductSummarie = (productNumber, store) => store[PRODUCT_SUMMARY_KEY].entities[productNumber];
const getSubcategoryTrackingData = resultState => {
    const category = resultState[SEARCH_RESPONSE_KEY]?.entities[0]?.breadCrumbs
        .selectedFilters[0]?.ancestors[0]?.label;
    const subcategory = resultState[SEARCH_RESPONSE_KEY]?.entities[0]?.breadCrumbs
        .selectedFilters[0]?.label;
    const trackingData = {
        category,
        subcategory,
    };
    return trackingData;
};
const getSearchResultTrackingData = resultState => {
    let searchTerm = resultState[SEARCH_RESPONSE_KEY]?.entities[0]?.breadCrumbs?.searchCrumbs[0]
        ?.terms;
    if (!searchTerm) {
        const filter = resultState[SEARCH_RESPONSE_KEY]?.entities[0]?.breadCrumbs
            ?.selectedFilters[0];
        const prefix = filter?.displayName?.toLowerCase().indexOf('brand') > -1
            ? 'brand'
            : 'category';
        searchTerm = `${prefix}:${filter?.label?.toLowerCase()}`;
    }
    const totalNumberOfRecords = resultState[SEARCH_RESPONSE_KEY]?.entities[0]?.totalNumberOfRecords;
    const trackingData = {
        searchTerm,
        totalNumberOfRecords,
    };
    return trackingData;
};
const getSearchResultFromCoveoTrackingData = tracking => {
    const searchTerm = tracking.data.searchTerm;
    const totalNumberOfRecords = tracking.data.totalCount;
    const trackingData = {
        searchTerm,
        totalNumberOfRecords,
    };
    return trackingData;
};
const getSearchResultTrackingProductsData = resultState => resultState[SEARCH_RESPONSE_KEY]?.entities[0]?.productNumbers;
const getPriceProduct = (productNumber, store) => store[PRICING_KEY].entities[productNumber];

/**
 * SEARCH SUBMIT PAGE LOAD - SEARCH MODULE
 *
 * @param personalizationData
 * @returns Personalization Layer needed for tracking the Search Submit Page Load
 */
const searchResultPageLoadMonetate = (personalizationData) => {
    const { pageName, pageType, pageValue, addProducts, searchTerm } = personalizationData.personalization.events[0];
    const productNumbers = getSearchResultTrackingProductsData(personalizationData.resultState);
    const products = [];
    if (!!productNumbers) {
        productNumbers.forEach(product => {
            const productSummary = getProductSummaries([product], personalizationData?.resultState)[0];
            const productId = `${productSummary?.divisionNumber?.toString()}-${productSummary?.productNumber?.toString()}`;
            products.push({
                sku: productId,
                productId,
            });
        });
    }
    const addSearchData = {
        searchTerm,
        searchType: MONETATE_ADD_SEARCH.searchType,
    };
    if (!searchTerm) {
        delete addSearchData.searchTerm;
    }
    const data = {
        personalization: {
            page: {
                pageName,
            },
            events: [
                [pageType, pageValue],
                [addProducts, products],
                [MONETATE_ADD_SEARCH.addSearch, addSearchData],
            ],
        },
    };
    return data;
};
/**
 * PRODUCT DETAIL PAGE LOAD - PRODUCT DETAIL MODULE
 *
 * @param personalizationData
 * @returns Personalization Layer needed for tracking the Product Detail Page Load
 */
const productDetailPageLoadMonetate = (personalizationData) => {
    const { pageName, pageType, pageValue, addProductDetails, productNumber } = personalizationData.personalization.events[0];
    const productSummaries = getProductSummaries([productNumber], personalizationData.resultState);
    if (productSummaries.length === 0 || !productSummaries[0]) {
        return null;
    }
    const productSummary = productSummaries[0];
    const data = {
        personalization: {
            useEngineApi: true,
            page: {
                pageName,
            },
            events: [
                {
                    eventType: pageType,
                    url: pageValue,
                    pageType: pageName,
                },
                {
                    eventType: addProductDetails,
                    products: [
                        {
                            sku: `${productSummary?.divisionNumber}-${productNumber}`,
                            productId: `${productSummary?.divisionNumber}-${productNumber}`,
                        },
                    ],
                },
            ],
        },
    };
    return data;
};
/**
 * PRODUCT DETAIL PAGE LOAD - PRODUCT DETAIL MODULE
 *
 * @param tracking
 * @returns Data Layer needed for tracking the Product Detail Page Load
 */
const productDetailPageLoad = (tracking) => {
    const { productNumber, attributes, customersAlsoBought, mayAlsoNeed, productRating, questionCount, betterBuyProductNumber, betterBuyProductAttributeList, price, } = tracking.data;
    const productSummary = getProductSummaries([productNumber], tracking.resultState)[0];
    const productInventory = getProductInventories([productNumber], tracking.resultState)[0];
    const category = `${productSummary?.classDescription}; ${productSummary?.classDescription} |
  ${productSummary?.categoryDescription}; ${productSummary?.classDescription} |
  ${productSummary?.categoryDescription} | ${productSummary?.groupDescription}`;
    const name = productSummary?.productDescTxtl;
    const brand = productSummary?.brand;
    const isUnavailable = productInventory?.isDirect && productInventory?.productStatus === '2';
    const isOutOfStockOrDiscontinued = productSummary?.propertiesArray
        ? productSummary?.propertiesArray?.includes(ProductPropertiesEnum.discontinued) ||
            productSummary?.propertiesArray?.includes(ProductPropertiesEnum.outOfStock)
        : false;
    const products = [];
    if (productSummary?.availableToPromise &&
        attributes.includes(PRODUCT_ATTRIBUTE_TRACK.availableToPromise)) {
        products.push({
            attributes,
            apn: productNumber,
            divisionApn: `${productSummary.divisionNumber}-${productNumber}`,
            primaryProduct: 1,
            name,
            category,
            price,
            brand,
            availabletoSellView: 1,
        });
    }
    else if (isUnavailable || isOutOfStockOrDiscontinued) {
        products.push({
            attributes,
            apn: productNumber,
            divisionApn: `${productSummary.divisionNumber}-${productNumber}`,
            primaryProduct: 1,
            name,
            category,
            price,
            brand,
            exceptionView: 1,
            exceptionNumberofSubs: 0,
        });
    }
    else {
        products.push({
            attributes,
            apn: productNumber,
            divisionApn: `${productSummary.divisionNumber}-${productNumber}`,
            primaryProduct: 1,
            name,
            category,
            price,
            brand,
        });
    }
    const data = {
        page: {
            pageName: tracking.data.pageName,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
        products,
    };
    const customersAlsoBoughtFinal = [];
    customersAlsoBought?.forEach(cab => {
        if (!customersAlsoBoughtFinal.some(product => product.productNumber === cab.productNumber)) {
            customersAlsoBoughtFinal.push(cab);
        }
    });
    customersAlsoBoughtFinal?.forEach(cab => {
        const productSummarie = getProductSummarie(cab.productNumber, tracking.resultState);
        data.products.push({
            attributes: cab.attributes,
            divisionApn: `${productSummarie?.divisionNumber}-${productSummarie?.productNumber}`,
            merchFeatureView: MERCH_FEATURE.customerAlsoBoughtView,
            merchFeatureName: MERCH_FEATURE.customerAlsoBoughtName,
            variant: cab?.variant,
        });
    });
    mayAlsoNeed?.forEach(man => {
        const productSummarie = getProductSummarie(man.productNumber, tracking.resultState);
        data.products.push({
            attributes: man.attributes,
            divisionApn: `${productSummarie?.divisionNumber}-${productSummarie?.productNumber}`,
            merchFeatureView: MERCH_FEATURE.youMayAlsoNeedView,
            merchFeatureName: MERCH_FEATURE.youMayAlsoNeedName,
            mapType: man.mapType,
        });
    });
    if (productRating > 0) {
        /* eslint-disable @typescript-eslint/dot-notation */
        data.products[0]['hasReviews'] = '1';
        data.products[0]['productRating'] = productRating;
    }
    if (questionCount > 0) {
        data.products[0]['hasQA'] = '1';
    }
    if (!!betterBuyProductNumber) {
        const betterBuyProduct = getProductSummaries([betterBuyProductNumber], tracking.resultState)[0];
        const betterBuyData = {
            divisionApn: `${betterBuyProduct?.divisionNumber}-${betterBuyProduct?.productNumber}`,
            attributes: betterBuyProductAttributeList,
            substitutionParentApn: `${productSummary.divisionNumber}-${productNumber}`,
            bbOpportunityView: '1',
            merchFeatureName: 'better buys',
        };
        // @ts-ignore
        data.products.push(betterBuyData);
    }
    return data;
};
const pdpYMANCABSCroll = (tracking) => {
    const data = {
        page: {
            pageName: tracking.data.pageName,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
        products: tracking.data.products,
        engagement: tracking.data.engagement,
    };
    return data;
};
/**
 * SUBSTITUTE PRODUCTS - PRODUCT DETAIL MODULE
 *
 * @param tracking
 * @returns Data Layer needed for tracking displayed substitute products
 */
const substituteProductsInPDP = (tracking) => {
    const { product, parentProduct } = tracking.data;
    const productSummaries = getProductSummaries([product.productNumber, parentProduct.productNumber], tracking.resultState);
    const data = {
        page: {
            pageName: tracking.data.pageName,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
        products: [
            {
                attributes: parentProduct.attributes,
                apn: parentProduct.productNumber,
                divisionApn: `${productSummaries[1].divisionNumber}-${parentProduct.productNumber}`,
                exceptionView: 1,
                exceptionNumberofSubs: 1,
                primaryProduct: 1,
            },
            {
                attributes: product.attributes,
                divisionApn: `${productSummaries[0].divisionNumber}-${product.productNumber}`,
                substitutionView: 1,
                substitutionParentApn: `${productSummaries[0].divisionNumber}-${parentProduct.productNumber}`,
                ...(!!product.substituteInfo && {
                    substituteType: product.substituteInfo.substituteType,
                    substitutePriority: product.substituteInfo.substitutePriority,
                }),
            },
        ],
    };
    return data;
};
/**
 * BANNER CLICK - SCOOP BANNER
 *
 * @param tracking
 * @returns Data Layer needed for tracking scoop banner click from home page
 */
const scoopBannerClick = (tracking) => {
    const data = {
        activePromo: {
            promoId: tracking.data.activePromo.promoId,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
    return data;
};
/**
 * BANNER CLICK - REBATES BANNER
 *
 * @param tracking
 * @returns Data Layer needed for tracking rebates banner click
 */
const rebatesBannerClick = (tracking) => {
    const data = {
        activePromo: {
            promoId: tracking.data.activePromo.promoId,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
    return data;
};
/**
 * BANNER CLICK - REBATES DROPDOWN
 *
 * @param tracking
 * @returns Data Layer needed for tracking rebates dropdown click
 */
const rebatesDropdownClick = (tracking) => {
    const data = {
        button: {
            buttonName: tracking.data.button.buttonName,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
    return data;
};
/**
 * BANNER CLICK - SERVE YOU HEADER
 *
 * @param tracking
 * @returns Data Layer needed for tracking serve you header click
 */
const serveYouHeaderClick = (tracking) => {
    const data = {
        activePromo: {
            promoId: tracking.data.activePromo.promoId,
        },
        button: {
            buttonName: tracking.data.button.buttonName,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
    return data;
};
const cartridgeBannerClickTrack = (tracking) => {
    const bannerImageId = tracking.data.bannerImageId;
    const data = {
        activePromo: {
            promoId: bannerImageId,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
    return data;
};
/**
 *
 * @param tracking
 * @returns Data layer needed for tracking While Supplies Last banner click from the deals landing page
 */
const whileSuppliesLastClick = (tracking) => {
    const data = {
        activePromo: {
            promoId: tracking.data.activePromo.promoId,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
    return data;
};
const searchPageSingleBanner = (tracking) => {
    const data = {
        activePromo: {
            promoId: tracking.data.activePromo.promoId,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
    return data;
};
/**
 *
 * @param tracking
 * @returns Data layer needed for tracking when the all available deals page is completely loaded
 */
const allDealsPageLoaded = (tracking) => {
    const data = {
        event: tracking.data.event,
        capability: tracking.data.capability,
        page: {
            pageName: tracking.data.page.pageName,
        },
        products: tracking.data.products,
    };
    return data;
};
/**
 * CATALOG PAGE LOAD - SEARCH MODULE
 *
 * @param tracking
 * @returns Data Layer needed for tracking catalog Page Load
 */
const catalogPageLoad = (tracking) => {
    const data = {
        page: {
            pageName: tracking.data.pageName,
            variant: tracking.data.pageVariant,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
    return data;
};
/**
 * CATALOG CATEGORY PAGE LOAD
 *
 * @param tracking
 * @returns Data Layer needed for tracking Catalog Category on Page Load
 */
const catalogCategoryPageLoad = (tracking) => {
    const pageNamePrefix = tracking.data.pageName;
    const category = tracking.data.category;
    const data = {
        page: {
            pageName: `${pageNamePrefix}:${category}`,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
    return data;
};
/**
 * CATALOG SUBCATEGORY PAGE LOAD
 *
 * @param tracking
 * @returns Data Layer needed for tracking Catalog Subcategory on Page Load
 */
const catalogSubcategoryPageLoad = (tracking) => {
    const subcategoryData = getSubcategoryTrackingData(tracking.resultState);
    const pageNamePrefix = tracking.data.pageName;
    const category = subcategoryData.category;
    const subcategory = subcategoryData.subcategory;
    const data = {
        filters: tracking.data.filters,
        page: {
            pageName: `${pageNamePrefix}:${category}:${subcategory}`,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
    return data;
};
/**
 * CATALOG BROWSE PAGE LOAD - SEARCH MODULE
 *
 * @param tracking
 * @returns Data Layer needed for tracking catalog Browse Page Load
 */
const catalogBrowsePageLoad = (tracking) => {
    const data = {
        page: {
            pageName: tracking.data.pageName,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
    return data;
};
/**
 * CATALOG CATEGORY BROWSE PAGE LOAD
 *
 * @param tracking
 * @returns Data Layer needed for tracking Catalog Category on Page Load
 */
const catalogCategoryBrowsePageLoad = (tracking) => {
    const pageNamePrefix = tracking.data.pageName;
    const category = tracking.data.category;
    const data = {
        page: {
            pageName: `${pageNamePrefix}:${category}`,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
    return data;
};
/**
 * SEARCH RESULT PAGE LOAD
 *
 * @param tracking
 * @returns Data Layer needed for tracking Catalog Subcategory on Page Load
 */
const searchResultPageLoad = (tracking) => {
    const searchEngine = tracking?.data?.searchEngine;
    const searchResultData = searchEngine === 'coveo'
        ? getSearchResultFromCoveoTrackingData(tracking)
        : getSearchResultTrackingData(tracking.resultState);
    const pageNamePrefix = tracking.data.pageName;
    const searchType = tracking.data?.searchType;
    const term = searchResultData.searchTerm;
    const totalNumberOfRecords = searchResultData.totalNumberOfRecords;
    const bannerImageIds = tracking.data.bannerImageIds;
    const products = tracking.data.products;
    const data = {
        filters: tracking.data.filters,
        page: {
            pageName: pageNamePrefix,
        },
        search: { term, searchType, searchEngine },
        results: {
            numberOfResults: totalNumberOfRecords,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
        promo: {
            view: '1',
            list: bannerImageIds,
        },
        products,
    };
    if (!bannerImageIds || bannerImageIds.length === 0) {
        delete data.promo;
    }
    if (data.search.term.includes('undefined')) {
        delete data.search;
    }
    return data;
};
const searchResultsLoadMoreTransform = (tracking) => {
    const data = {
        event: tracking.data.event,
        capability: tracking.data.capability,
        products: tracking.data.products,
    };
    return data;
};
/**
 * SEARCH SUBMIT
 *
 * @param tracking
 * @returns Data Layer needed for tracking Catalog Subcategory on Page Load
 */
const searchSubmitFn = (tracking) => {
    const searchTerm = tracking?.data?.searchRequest?.searchText;
    const data = {
        activePromo: {
            promo: tracking.data.activePromo.promo,
        },
        search: {
            term: searchTerm,
            searchType: tracking?.data?.searchType,
            searchEngine: tracking?.data?.searchEngine,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
    return data;
};
/**
 * SEARCH SUBMIT - TypeAhead
 *
 * @param tracking
 * @returns Data Layer needed for tracking TypeAhead Search
 */
const productTypeAheadSubmitFn = (tracking) => {
    const { term, searchType, rank, dimensionId: productNumber } = tracking.data;
    const divisionApn = tracking.resultState[APP_STATE_KEY].selectedCustomer.divisionNumber;
    const data = {
        search: {
            term,
            searchType,
        },
        results: {
            resultRank: rank,
        },
        products: [{ divisionApn: `${divisionApn}-${productNumber}` }],
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
    return data;
};
/**
 * SEARCH SUBMIT - TypeAhead
 *
 * @param tracking
 * @returns Data Layer needed for tracking TypeAhead Search (Brand or Category)
 */
const searchTypeAheadSubmitFn = (tracking) => {
    const data = {
        search: {
            term: tracking.data.term,
            searchType: tracking?.data?.searchType,
            typeAheadRank: tracking?.data?.rank,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
    return data;
};
/**
 * SEARCH SUBMIT
 *
 * @param tracking
 * @returns Data Layer needed for tracking Catalog Subcategory on Page Load
 */
const searchResultClickFn = (tracking) => {
    const searchResultData = getSearchResultTrackingData(tracking.resultState);
    const numberOfResults = searchResultData.totalNumberOfRecords;
    const productNumber = tracking?.data?.productNumber;
    const resultRank = tracking?.data?.resultRank;
    const attributes = tracking?.data?.attributes;
    const price = tracking?.data?.price;
    const productSummary = getProductSummaries([productNumber], tracking.resultState)[0];
    const divisionApn = `${productSummary.divisionNumber}-${productNumber}`;
    const category = `${productSummary?.classDescription}; ${productSummary?.classDescription} |
  ${productSummary?.categoryDescription}; ${productSummary?.classDescription} |
  ${productSummary?.categoryDescription} | ${productSummary?.groupDescription}`;
    const name = productSummary?.productDescTxtl;
    const brand = productSummary?.brand;
    const data = {
        event: tracking.data.event,
        results: {
            resultRank,
            numberOfResults,
        },
        products: [
            {
                attributes,
                divisionApn,
                name,
                category,
                price,
                brand,
            },
        ],
    };
    return data;
};
const searchTracingTransform = (tracking) => ({
    event: tracking.data?.attributes?.eventName,
    search: {
        ...tracking.data?.attributes?.request,
        searchType: tracking.data?.attributes?.searchType,
    },
});
/**
 * SEARCH SUBMIT
 *
 * @param tracking
 * @returns Data Layer needed for tracking TM Note Detail Page Load
 */
const tmNoteDetailPageLoadTransform = (tracking) => {
    const productsTransform = tracking?.data?.products.map(product => {
        let productTransform = {
            attributes: product.attributes,
            divisionApn: `${product.divisionNumber}-${product.productNumber}`,
            merchFeatureName: MERCH_FEATURE.tmNotesName,
            merchFeatureView: MERCH_FEATURE.tmNotesView,
        };
        if (product.hasOwnProperty('exceptionView')) {
            productTransform = {
                ...productTransform,
                exceptionNumberofSubs: product.exceptionNumberofSubs,
                exceptionView: '1',
            };
            return productTransform;
        }
        if (product.hasOwnProperty('substitutionView')) {
            productTransform = {
                ...productTransform,
                substitutionView: product.substitutionView,
                substitutionParentApn: product.substitutionParentApn,
            };
            return productTransform;
        }
        return productTransform;
    });
    const data = {
        event: tracking.data.event,
        capability: tracking.data.capability,
        page: {
            pageName: tracking.data.page.pageName,
        },
        promo: {
            list: `${TM_NOTES_DETAIL_PAGE.idPrefix}${tracking.data.promo.list}`,
            view: TM_NOTES_DETAIL_PAGE.promoView,
        },
        products: productsTransform,
    };
    return data;
};
const searchFilterApplyTransform = (tracking) => {
    const data = {
        event: tracking.data.event,
        capability: tracking.data.capability,
        filters: tracking.data.filters,
    };
    return data;
};
const searchFilterToggleOn = (tracking) => {
    const data = {
        event: tracking.data.event,
        capability: tracking.data.capability,
        button: {
            name: BUTTON_NAME.buttonOn,
        },
    };
    return data;
};
const searchFilterToggleOff = (tracking) => {
    const data = {
        event: tracking.data.event,
        capability: tracking.data.capability,
        button: {
            name: BUTTON_NAME.buttonOff,
        },
    };
    return data;
};
const productCompareStart = (tracking) => {
    const data = {
        event: tracking.data.event,
        capability: tracking.data.capability,
        button: {
            buttonName: BUTTON_NAME.compareProducts,
        },
    };
    return data;
};
const productCompareStartOnSearchForSubs = (tracking) => {
    const data = {
        event: tracking.data.event,
        capability: tracking.data.capability,
        button: {
            buttonName: BUTTON_NAME.compareProducts,
        },
        products: tracking.data.products.map(product => ({
            attributes: product.attributes,
            divisionApn: product.divisionApn,
            exceptionView: '1',
        })),
    };
    return data;
};
const searchForSubsTransform = (tracking) => {
    const data = {
        event: tracking.data.event,
        capability: tracking.data.capability,
        promo: tracking.data.promo,
        activePromo: tracking.data.activePromo,
        search: {
            term: tracking.data.search.term,
            searchType: tracking.data.search.searchType,
        },
        products: tracking.data.products,
    };
    return data;
};
const userSubmitOpportunity = (tracking) => {
    const { event, capability, products, pageName, publishNote } = tracking.data;
    return {
        event,
        capability,
        products,
        modal: {
            name: CREATE_OPPORTUNITY.modalName,
        },
        ...(!!pageName && {
            page: {
                pageName,
            },
        }),
        ...(publishNote && {
            publish: {
                publishNote: CREATE_OPPORTUNITY.publishNote,
            },
        }),
    };
};
const productComparePageLoad = (tracking) => {
    const { pageName, event, capability, products } = tracking.data;
    return {
        page: {
            pageName,
        },
        event,
        capability,
        products,
    };
};
const bannerDealsDisplayed = (tracking) => {
    const { event, capability, engagement } = tracking.data;
    return {
        event,
        capability,
        engagement,
    };
};
const bannerDealsView = (tracking) => {
    const { event, capability, promo, engagement } = tracking.data;
    return {
        event,
        capability,
        promo,
        engagement,
    };
};
const bannerDealsClick = (tracking) => {
    const { event, capability, activePromo } = tracking.data;
    return {
        event,
        capability,
        activePromo,
    };
};
const revManWinbacksView = (tracking) => {
    const { capability, event, products } = tracking.data;
    return {
        capability,
        event,
        engagement: { engagementType: 'revman winbacks view' },
        products: products.map(product => ({
            divisionApn: `${product?.divisionNumber}-${product?.productNumber}`,
            attributes: product?.attributes,
            merchFeatureView: '1',
            merchFeatureName: 'revman winbacks',
        })),
    };
};
const userProductCompareEllipsisMenu = (tracking) => {
    const { capability, event, productData } = tracking.data;
    let products = [];
    if (!!productData && !!productData.original && !!productData.alternative) {
        products = [
            {
                divisionApn: productData.original.divisionApn,
                attributes: productData.original.attributes,
                exceptionView: '1',
                exceptionNumberOfSubs: productData.original.exceptionNumberOfSubs,
            },
            {
                divisionApn: productData.alternative.divisionApn,
                attributes: productData.alternative.attributes,
                substitutionView: '1',
                substitutionParentApn: productData.original.divisionApn,
                substituteType: productData.alternative.substituteType,
                substitutePriority: productData.alternative.substitutePriority,
            },
        ];
    }
    return {
        capability,
        event,
        products,
    };
};
const productWorksWellWithButton = (tracking) => {
    const { event, capability, engagementType, productsData } = tracking.data;
    const products = [];
    productsData.forEach(productData => {
        const product = {
            divisionApn: productData.divisionApn,
            attributes: productData.attributes,
            merchFeatureView: '1',
            merchFeatureName: MERCH_FEATURES.worksWellWith,
        };
        products.push(product);
    });
    return {
        capability,
        event,
        engagement: {
            engagementType,
        },
        products,
    };
};
const betterBuysPageLoad = (tracking) => {
    const { pageName, event, capability } = tracking.data;
    return {
        page: {
            pageName,
        },
        event,
        capability,
    };
};
const betterBuysAccept = (tracking) => {
    const { event, acceptSwapTrackingInfo } = tracking.data;
    let products = [];
    if (!!acceptSwapTrackingInfo.originalProduct &&
        !!acceptSwapTrackingInfo.newProduct) {
        const divisionNumber = acceptSwapTrackingInfo.originalProduct.summary.divisionNumber;
        const productNumber = acceptSwapTrackingInfo.originalProduct.productNumber;
        const parentDivisionApn = `${divisionNumber}-${productNumber}`;
        const newProductDivisionNumber = acceptSwapTrackingInfo.newProduct.summary.divisionNumber;
        const newProductProductNumber = acceptSwapTrackingInfo.newProduct.productNumber;
        products = [
            {
                divisionApn: `${newProductDivisionNumber}-${newProductProductNumber}`,
                attributes: acceptSwapTrackingInfo.newProductAttributes,
                listReplaceIn: '1',
                substitutionParentApn: parentDivisionApn,
                merchFeatureName: MERCH_FEATURE.betterBuys,
                bbSwapIn: '1',
            },
            {
                divisionApn: parentDivisionApn,
                attributes: acceptSwapTrackingInfo.originalProductAttributes,
                bbSwapOut: '1',
            },
        ];
    }
    return {
        event,
        capability: acceptSwapTrackingInfo.capability,
        products,
    };
};
const betterBuysReject = (tracking) => {
    const { event, rejectSwapTrackingInfo } = tracking.data;
    let products = [];
    if (!!rejectSwapTrackingInfo.originalProduct &&
        !!rejectSwapTrackingInfo.newProduct) {
        const divisionNumber = rejectSwapTrackingInfo.originalProduct.summary.divisionNumber;
        const productNumber = rejectSwapTrackingInfo.originalProduct.productNumber;
        const parentDivisionApn = `${divisionNumber}-${productNumber}`;
        const newProductDivisionNumber = rejectSwapTrackingInfo.newProduct.summary.divisionNumber;
        const newProductProductNumber = rejectSwapTrackingInfo.newProduct.productNumber;
        products = [
            {
                divisionApn: `${newProductDivisionNumber}-${newProductProductNumber}`,
                attributes: rejectSwapTrackingInfo.newProductAttributes,
                substitutionParentApn: parentDivisionApn,
                merchFeatureName: MERCH_FEATURE.betterBuys,
                bbOpportunityRejected: '1',
                bbRejectedReasonCode: rejectSwapTrackingInfo.rejectedReasonCode,
                bbRejectedReasonText: rejectSwapTrackingInfo.rejectedReasonText,
            },
            {
                divisionApn: parentDivisionApn,
                attributes: rejectSwapTrackingInfo.originalProductAttributes,
                bbOriginalKept: '1',
            },
        ];
    }
    return {
        event,
        capability: rejectSwapTrackingInfo.capability,
        products,
    };
};
const trackProductCompareTransformer = (tracking) => {
    const { capability, event, productData } = tracking.data;
    let products = [];
    if (!!productData) {
        const divisionNumber = productData.divisionNumber;
        const productNumber = productData.productNumber;
        const parentDivisionApn = `${divisionNumber}-${productNumber}`;
        products = [
            {
                divisionApn: `${productData.betterBuy.divisionNumber}-${productData.betterBuy.productNumber}`,
                attributes: productData.betterBuy.attributes,
                substitutionParentApn: parentDivisionApn,
                bbOpportunityView: '1',
            },
            {
                divisionApn: parentDivisionApn,
                attributes: productData.attributes,
                bbCurrentView: '1',
            },
        ];
    }
    return {
        capability,
        event,
        products,
    };
};
const submitOrderStartMonetateTracing = (personalizationData) => {
    const monetateProducts = [];
    const currency = 'USD';
    if (personalizationData.personalization?.events?.length > 0) {
        const cartOrder = personalizationData.personalization?.events[0].order;
        const orderHeader = cartOrder?.orderHeader;
        const orderItemsIds = cartOrder?.orderItems;
        if (!!orderHeader && !!orderItemsIds) {
            if (Array.isArray(orderItemsIds)) {
                orderItemsIds.forEach(orderItemId => {
                    const productId = `${orderHeader?.divisionNumber}-${orderItemId?.productNumber}`;
                    const pricing = getPriceProduct(orderItemId?.productNumber, personalizationData.resultState);
                    if (orderItemId?.unitsOrdered?.currentValue > 0) {
                        monetateProducts.push({
                            purchaseId: orderHeader?.orderId,
                            productId,
                            quantity: orderItemId.unitsOrdered.currentValue,
                            unitPrice: pricing?.unitPrice,
                            currency,
                        });
                    }
                    if (orderItemId?.eachesOrdered?.currentValue > 0) {
                        monetateProducts.push({
                            purchaseId: orderHeader?.orderId,
                            productId,
                            quantity: orderItemId.eachesOrdered.currentValue,
                            unitPrice: pricing?.eachPrice,
                            currency,
                        });
                    }
                });
            }
        }
    }
    return {
        personalization: {
            events: [['addPurchaseRows', monetateProducts]],
        },
    };
};
const generatePrintPDPData = (tracking) => {
    const { event, capability, fileType } = tracking.data;
    return {
        event,
        capability,
        file: {
            type: fileType,
        },
    };
};
const generateShareComparisonEvents = (tracking) => {
    const { event, capability, file } = tracking.data;
    return {
        event,
        capability,
        file,
    };
};
const equipmentAndSuppliesTransform = (tracking) => {
    const data = {
        event: tracking.data.event,
        capability: tracking.data.capability,
        button: {
            buttonName: tracking.data.button.buttonName,
        },
        activePromo: {
            promoId: tracking.data.activePromo.promoId,
        },
    };
    return data;
};
const scoopPageLoad = (tracking) => {
    const { capability, event, pageName } = tracking.data;
    return {
        capability,
        event,
        page: {
            pageName,
        },
    };
};
const shareViaEmailPDPOpenModal = (tracking) => {
    const { capability, event, modalName } = tracking.data;
    return {
        capability,
        event,
        modal: {
            name: modalName,
        },
    };
};
const shareViaEmailPDP = (tracking) => {
    const { capability, event, fileType } = tracking.data;
    return {
        capability,
        event,
        file: {
            type: fileType,
        },
    };
};
const createNonUsfProductTransform = (tracking) => {
    const data = {
        event: tracking.data.event,
        capability: tracking.data.capability,
        products: [
            {
                divisionApn: tracking.data.divisionApn,
                nonUsfProductName: tracking.data.nonUsfProductName,
                nonUsfProductPrice: tracking.data.nonUsfProductPrice,
            },
        ],
    };
    return data;
};
const pageLoadNonUsfProductsTransform = (tracking) => {
    const data = {
        capability: tracking.data.capability,
        event: tracking.data.event,
        page: {
            pageName: tracking.data.pageName,
        },
        products: tracking.data.products,
    };
    return data;
};
const linkClickTransform = (tracking) => {
    const data = {
        link: {
            text: tracking.data.link.text,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
    return data;
};
const productClickTransform = (tracking) => {
    const data = { ...tracking.data };
    if (!!tracking.data.resultRank) {
        data.results = {
            resultRank: tracking.data.resultRank,
        };
    }
    return data;
};
const pageLoadedTransform = (tracking) => {
    const data = {
        page: {
            pageName: tracking.data.pageName,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
    return data;
};
const buttonClickTransform = (tracking) => {
    const data = {
        button: {
            buttonName: tracking.data.buttonName,
        },
        event: tracking.data.event,
        capability: tracking.data.capability,
    };
    return data;
};

class SearchStoreService {
    constructor(store) {
        this.store = store;
    }
    executeSearch(request) {
        const searchType = SEARCH_TYPE.catalogSearch;
        const tracking = {
            tracing: {
                data: {
                    traceContext: TRACE_CONTEXT.search,
                    isStartOfTrace: true,
                    isEndOfTrace: false,
                    attributes: {
                        action: ACTIONS.searchSubmit,
                        request,
                        eventName: EVENT.searchSubmit,
                        searchType,
                    },
                },
                transformFunc: searchTracingTransform,
            },
        };
        this.store.dispatch(SearchActions.FetchSearchResponse({
            request,
            tracking,
        }));
    }
    getSearchResponse() {
        return this.store.select(selectSearchResponse);
    }
}
SearchStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
SearchStoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchStoreService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class SearchStoreServiceV2 {
    constructor(store) {
        this.store = store;
    }
    executeSearch(request, correlationId, applyDefaultDeliveryMethodFilter) {
        this.store.dispatch(SearchActionsV2.FetchSearchResponse({
            request,
            correlationId,
            applyDefaultDeliveryMethodFilter,
        }));
    }
}
SearchStoreServiceV2.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchStoreServiceV2, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
SearchStoreServiceV2.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchStoreServiceV2, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchStoreServiceV2, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

/* eslint-disable @typescript-eslint/naming-convention */
const OpportunityActions = {
    ResetStore: createAction('[NGRXTrace - Opportunity] Reset Opportunity'),
    CreateProductOpportunity: createAction('[NGRXTrace - Opportunity] Create Opportunity', props()),
    CreateProductOpportunitySuccess: createAction('[NGRXTrace - Opportunity] Create Opportunity Success', props()),
    CreateProductOpportunityFail: createAction('[NGRXTrace - Opportunity] Create Opportunity Failure', props()),
};

const opportunityAdapter = createEntityAdapter({
    selectId: opportunity => opportunity.id,
});
const initialOpportunityState = opportunityAdapter.getInitialState({
    loadingState: LoadingState.init,
});
// eslint-disable-next-line no-underscore-dangle
const _opportunityReducer = createReducer(initialOpportunityState, 
//Set this elements to be on a 'loading' state
on(OpportunityActions.CreateProductOpportunity, (state, action) => {
    const updateChange = {
        id: action.id,
        request: action.request,
        loadingState: LoadingState.loading,
    };
    return opportunityAdapter.upsertOne(updateChange, state);
}), on(OpportunityActions.CreateProductOpportunitySuccess, (state, { id, request, result }) => {
    const updateChange = {
        id,
        request,
        response: result,
        loadingState: LoadingState.loaded,
    };
    return opportunityAdapter.upsertOne(updateChange, state);
}), on(OpportunityActions.CreateProductOpportunityFail, (state, { id, request, error }) => {
    const updateChange = {
        id,
        request,
        loadingState: LoadingState.error,
    };
    return opportunityAdapter.upsertOne(updateChange, state);
}));
function opportunityReducer(state, action) {
    return _opportunityReducer(state, action);
}

const { selectEntities: selectEntities$3 } = opportunityAdapter.getSelectors();
const selectOpportunityResponseState = createFeatureSelector(PRODUCT_OPPORTUNITY_KEY);
const isOpportunityStateReady = createSelector(selectOpportunityResponseState, state => state.loadingState === LoadingState.init);
const selectOpportunityEntities = createSelector(selectOpportunityResponseState, selectEntities$3);
const selectOpportunityById = (id) => createSelector(selectOpportunityEntities, entities => entities[id]);

const populateTraceHeader = (action) => action.tracking?.tracing?.data?.traceContext;
const startTrace = (traceContext) => ({
    tracing: {
        data: {
            traceContext,
            isStartOfTrace: true,
            isEndOfTrace: false,
        },
    },
});
const endTrace = (traceContext) => ({
    tracing: {
        data: {
            traceContext,
            isStartOfTrace: false,
            isEndOfTrace: true,
        },
    },
});
const traceHelperUtil = {
    populateTraceHeader,
    startTrace,
    endTrace,
};

class OpportunityStoreService {
    constructor(store) {
        this.store = store;
    }
    createProductOpportunity(request, id) {
        const opportunityRequestId = id ?? nanoid(16);
        this.store.dispatch(OpportunityActions.CreateProductOpportunity({
            id: opportunityRequestId,
            request,
            tracking: traceHelperUtil.startTrace(TRACE_CONTEXT.createOpportunity),
        }));
        return this.store.select(selectOpportunityById(opportunityRequestId)).pipe(first(opportunity => opportunity?.loadingState === LoadingState.loaded ||
            opportunity?.loadingState === LoadingState.error), map(opportunity => {
            const success = opportunity.loadingState === LoadingState.loaded &&
                opportunity.response.statusCode === 200;
            let statusCode;
            let message;
            if (opportunity.loadingState === LoadingState.loaded) {
                statusCode = opportunity.response.statusCode;
                message = opportunity.response.description;
            }
            else {
                statusCode = 0;
                message = 'Connection Error';
            }
            const opportunityResult = {
                id: opportunity.id,
                opportunityId: opportunity?.response?.opportunityId,
                success,
                statusCode,
                message,
            };
            return opportunityResult;
        }));
    }
}
OpportunityStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OpportunityStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
OpportunityStoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OpportunityStoreService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OpportunityStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class TypeaheadStoreService {
    constructor(store) {
        this.store = store;
    }
    getTypeahead(searchTerm) {
        const searchType = SEARCH_TYPE.typeAhead;
        const tracking = {
            tracing: {
                data: {
                    traceContext: TRACE_CONTEXT.typeahead,
                    isStartOfTrace: true,
                    isEndOfTrace: false,
                    attributes: {
                        action: ACTIONS.searchSubmit,
                        term: searchTerm,
                        eventName: EVENT.searchSubmit,
                        searchType,
                    },
                },
                transformFunc: searchTracingTransform,
            },
        };
        this.store.dispatch(TypeaheadActions.LoadTypeahead({ searchTerm, tracking }));
        return this.store
            .select(selectLoadedTypeahead(searchTerm))
            .pipe(first(typeahead => typeahead !== undefined));
    }
}
TypeaheadStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TypeaheadStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
TypeaheadStoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TypeaheadStoreService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: TypeaheadStoreService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const selectSneakPeekState = createFeatureSelector(SNEAK_PEEK_KEY);
const isSneakPeekStateReady = createSelector(selectSneakPeekState, state => state.loadingState !== LoadingState.notReady);

class SneakPeekStoreService {
    constructor(store) {
        this.store = store;
    }
    loadSneakPeek$() {
        return this.store.select(isSneakPeekStateReady).pipe(first(isReady => isReady), map(isReady => {
            this.store.dispatch(SneakPeekActions.FetchSneakPeekResult());
            return isReady;
        }));
    }
}
SneakPeekStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SneakPeekStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
SneakPeekStoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SneakPeekStoreService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SneakPeekStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const nonUSFProductAdapter = createEntityAdapter({
    selectId: nonUSFProduct => nonUSFProduct?.productNumber,
});
const initialNonUSFProductState = nonUSFProductAdapter.getInitialState({
    loaded: false,
    loading: false,
    selections: {},
    keyword: '',
    sortOptions: initialNonUsfProductSortOptions,
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _nonUSFProductReducer = createReducer(initialNonUSFProductState, on(NonUSFProductActions.load, (state, action) => ({
    ...state,
    loading: true,
    loaded: false,
})), on(NonUSFProductActions.loadSuccess, (state, action) => nonUSFProductAdapter.upsertMany(action.nonUSFProducts.map(prod => ({ ...prod })), {
    ...state,
    loaded: true,
    loading: false,
})), on(NonUSFProductActions.resetStore, () => initialNonUSFProductState), on(NonUSFProductActions.loadFail, (state, action) => ({
    ...state,
    loaded: true,
    loading: false,
    error: action.error,
})), on(NonUSFProductActions.createSuccess, (state, action) => 
// eslint-disable-next-line max-len
nonUSFProductAdapter.upsertOne({ ...action.nonUSFProduct, loaded: true }, state)), on(NonUSFProductActions.delete, (state, action) => {
    const keys = [];
    action.nonUSFProducts?.forEach(product => {
        keys.push(product.productNumber);
    });
    return nonUSFProductAdapter.removeMany(keys, state);
}), on(NonUSFProductActions.deleteFail, (state, action) => nonUSFProductAdapter.upsertMany(action.nonUSFProducts.map(prod => ({ ...prod })), state)), on(NonUSFProductActions.updateSuccess, (state, action) => nonUSFProductAdapter.upsertOne({ ...action.nonUSFProduct, loaded: true }, state)), on(NonUSFProductActions.select, (state, action) => ({
    ...state,
    selections: {
        ...state.selections,
        [action.nonUSFProduct.productNumber]: action.select,
    },
})), on(NonUSFProductActions.resetSelections, (state, action) => ({
    ...state,
    selections: {},
})), on(NonUSFProductActions.selectAll, (state, action) => {
    let trueSelections = {};
    action.nonUSFProducts.forEach(product => {
        trueSelections = { ...trueSelections, [product.productNumber]: true };
    });
    return {
        ...state,
        selections: { ...trueSelections },
    };
}), on(NonUSFProductActions.searchNonUsfProductWorksheet, (state, action) => ({
    ...state,
    keyword: action.keyword,
})), on(NonUSFProductActions.resetSort, (state, action) => ({
    ...state,
    sortOptions: initialNonUsfProductSortOptions,
})), on(NonUSFProductActions.setSort, (state, action) => {
    const selectedSortOption = {
        ...state.sortOptions[action.headerName],
    };
    if (selectedSortOption.sortDirection === SortOptionsEnum.asc) {
        selectedSortOption.sortDirection = SortOptionsEnum.desc;
    }
    else {
        selectedSortOption.sortDirection = SortOptionsEnum.asc;
    }
    const newSortOptions = {
        ...initialNonUsfProductSortOptions,
        selectedHeader: action.headerName,
    };
    newSortOptions[action.headerName] = selectedSortOption;
    return {
        ...state,
        sortOptions: newSortOptions,
    };
}), on(ExternalVendorActions.deleteSuccess, (state, action) => {
    const updatedNonUsfProducts = [];
    let updatedNonUsfProduct;
    for (const key in state.entities) {
        if (key && state.entities[key] !== undefined) {
            action.vendorIds.forEach(vendorId => {
                if (state.entities[key].vendorId === vendorId) {
                    updatedNonUsfProduct = {
                        ...state.entities[key],
                        vendorId: '',
                    };
                    updatedNonUsfProducts.push(updatedNonUsfProduct);
                }
            });
        }
    }
    return nonUSFProductAdapter.upsertMany(updatedNonUsfProducts, state);
}), on(NonUSFProductActions.import, (state, action) => ({
    ...state,
    loading: true,
    loaded: false,
})), on(NonUSFProductActions.importSuccess, (state, action) => nonUSFProductAdapter.upsertMany(action.response.nonUsfProductProcessed.map(prod => ({ ...prod })), {
    ...state,
    loaded: true,
    loading: false,
})), on(NonUSFProductActions.importFail, (state, action) => ({
    ...state,
    loaded: true,
    loading: false,
    error: action.error,
})));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function nonUSFProductReducer(state, action) {
    return _nonUSFProductReducer(state, action);
}

const selectNonUSFProductsState = createFeatureSelector(NON_USF_PRODUCT_KEY);
const allNonUSFProducts = () => createSelector(selectNonUSFProductsState, (productState) => Object.values(productState?.entities));
const getKeyword = () => createSelector(selectNonUSFProductsState, (productState) => productState.keyword);
const getNonUSFProductById = (productNumber) => createSelector(selectNonUSFProductsState, (productState) => productState?.entities[productNumber]);
const getNonUSFProductSelections = () => createSelector(selectNonUSFProductsState, (productState) => productState.selections);
const getNonUSFSortOptions = () => createSelector(selectNonUSFProductsState, (productState) => productState.sortOptions);
const { selectIds: selectNonUSFProductIds, selectEntities: selectNonUSFProductEntities, selectAll: selectAllNonUSFProducts, selectTotal: selectTotalNonUSFProducts, } = nonUSFProductAdapter.getSelectors(selectNonUSFProductsState);

class NonUSFProductFacadeService {
    constructor(browserStorageService, store, panAppState) {
        this.browserStorageService = browserStorageService;
        this.store = store;
        this.panAppState = panAppState;
        this.allProducts$ = () => this.store.select(allNonUSFProducts());
        this.productById$ = (productNumber) => this.store.select(getNonUSFProductById(productNumber));
    }
    loadNonUSFProducts() {
        this.store.dispatch(NonUSFProductActions.load());
    }
}
NonUSFProductFacadeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NonUSFProductFacadeService, deps: [{ token: i1$1.BrowserStorageService }, { token: i1.Store }, { token: i5.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
NonUSFProductFacadeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NonUSFProductFacadeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NonUSFProductFacadeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.BrowserStorageService }, { type: i1.Store }, { type: i5.PanAppState }]; } });

/* eslint-disable no-shadow */
const externalVendorAdapter = createEntityAdapter({
    selectId: externalVendor => externalVendor?.vendorId,
});
const initialExternalVendorState = externalVendorAdapter.getInitialState({
    loaded: true,
    loading: false,
    selectedSort: {
        columnName: VendorColumns.vendorName,
        sortDirection: 'asc',
    },
    nextSortStates: {
        [VendorColumns.vendorCode]: 'asc',
        [VendorColumns.vendorName]: 'desc',
        [VendorColumns.contactName]: 'asc',
        [VendorColumns.cityState]: 'asc',
        [VendorColumns.phoneNumber]: 'asc',
        [VendorColumns.email]: 'asc',
    },
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _externalVendorReducer = createReducer(initialExternalVendorState, on(ExternalVendorActions.load, (state, action) => ({
    ...state,
    loading: true,
    loaded: false,
})), on(ExternalVendorActions.resetStore, (state, action) => initialExternalVendorState), on(ExternalVendorActions.createSuccess, (state, action) => externalVendorAdapter.upsertOne(action.vendor, state)), on(ExternalVendorActions.editSuccess, (state, action) => externalVendorAdapter.upsertOne(action.updatedVendor, state)), on(ExternalVendorActions.delete, (state, action) => externalVendorAdapter.removeMany(action.vendorIds, state)), on(ExternalVendorActions.deleteFail, (state, action) => externalVendorAdapter.upsertMany(action.vendors, state)), on(ExternalVendorActions.loadSuccess, (state, action) => externalVendorAdapter.upsertMany(action.vendors, {
    ...state,
    loaded: true,
    loading: false,
})), on(ExternalVendorActions.loadFail, (state, action) => ({
    ...state,
    loaded: true,
    loading: false,
    error: action.error,
})), on(ExternalVendorActions.changeSort, (state, action) => ({
    ...state,
    selectedSort: action.selectedSort,
    nextSortStates: action.nextSortStates,
})), on(ExternalVendorActions.resetSort, (state, action) => ({
    ...state,
    selectedSort: {
        columnName: VendorColumns.vendorName,
        sortDirection: 'asc',
    },
    nextSortStates: {
        [VendorColumns.vendorCode]: 'asc',
        [VendorColumns.vendorName]: 'desc',
        [VendorColumns.contactName]: 'asc',
        [VendorColumns.cityState]: 'asc',
        [VendorColumns.phoneNumber]: 'asc',
        [VendorColumns.email]: 'asc',
    },
})), on(ExternalVendorActions.setIsSelected, (state, action) => {
    const externalVendorState = [];
    if (!action.vendorId) {
        for (const vendorId in state.entities) {
            if (state.entities.hasOwnProperty(vendorId)) {
                externalVendorState.push({
                    ...state.entities[vendorId],
                    isSelected: action.value,
                });
            }
        }
    }
    else {
        const externalVendor = {
            ...state.entities[action.vendorId],
            isSelected: action.value,
        };
        externalVendorState.push(externalVendor);
    }
    return externalVendorAdapter.upsertMany(externalVendorState, {
        ...state,
        loaded: true,
        loading: false,
    });
}));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function externalVendorReducer(state, action) {
    return _externalVendorReducer(state, action);
}

const selectExternalVendorsState = createFeatureSelector(EXTERNAL_VENDOR_KEY);
const allExternalVendors = () => createSelector(selectExternalVendorsState, (vendorState) => Object.values(vendorState?.entities));
const getExternalVendorById = (vendorId) => createSelector(selectExternalVendorsState, (vendorState) => vendorState?.entities[vendorId]);
const { selectIds: selectExternalVendorIds, selectEntities: selectExternalVendorEntities, selectAll: selectAllExternalVendors, selectTotal: selectTotalExternalVendors, } = externalVendorAdapter.getSelectors(selectExternalVendorsState);

class ExternalVendorFacadeService {
    constructor(store, panAppState) {
        this.store = store;
        this.panAppState = panAppState;
        this.allVendors$ = () => this.store.select(allExternalVendors());
        this.vendorById$ = (vendorId) => this.store.select(getExternalVendorById(vendorId));
    }
    loadVendors() {
        this.panAppState.accessToken$.pipe(take(1)).subscribe(token => {
            try {
                const decodedToken = jwt_decode(token);
                if (decodedToken) {
                    if (decodedToken['usf-claims']?.hideInventory === 'Y') {
                        return false;
                    }
                    this.store.dispatch(ExternalVendorActions.load());
                }
            }
            catch (Error) { }
        });
    }
}
ExternalVendorFacadeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ExternalVendorFacadeService, deps: [{ token: i1.Store }, { token: i5.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
ExternalVendorFacadeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ExternalVendorFacadeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ExternalVendorFacadeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: i5.PanAppState }]; } });

/* eslint-disable no-shadow */
const betterBuysAdapter = createEntityAdapter({
    selectId: betterBuy => betterBuy?.originalProductNumber,
});
const initialBetterBuysState = betterBuysAdapter.getInitialState({
    loaded: false,
    loading: false,
    totalAnnualSavings: 0,
    remaining: 0,
    accepted: 0,
    estimatedTodaySavings: 0,
    estimatedYearToDateSavings: 0,
    estimatedAllTimeSavings: 0,
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _betterBuysReducer = createReducer(initialBetterBuysState, on(BetterBuysActions.load, BetterBuysActions.loadSavingsOverTime, (state, action) => ({
    ...state,
    loaded: false,
    loading: true,
})), on(BetterBuysActions.resetStore, (state, action) => initialBetterBuysState), on(BetterBuysActions.loadSuccess, (state, action) => betterBuysAdapter.upsertMany(action.betterBuys, {
    ...state,
    loaded: true,
    loading: false,
    remaining: action.betterBuys.length,
    accepted: 0,
})), on(BetterBuysActions.loadFail, BetterBuysActions.loadSavingsOverTimeFail, (state, action) => ({
    ...state,
    loaded: true,
    loading: false,
    error: action.error,
})), on(BetterBuysActions.acceptSuggestion, (state, action) => ({
    ...state,
    remaining: state.remaining - 1,
    accepted: state.accepted + 1,
    estimatedTodaySavings: state.estimatedTodaySavings + action.annualSavings,
    estimatedYearToDateSavings: state.estimatedYearToDateSavings + action.annualSavings,
    estimatedAllTimeSavings: state.estimatedAllTimeSavings + action.annualSavings,
})), on(BetterBuysActions.declineSuggestion, (state, action) => ({
    ...state,
    remaining: state.remaining - 1,
    totalAnnualSavings: state.totalAnnualSavings - action.annualSavings,
})), 
// Keying off the target product number and better buy number, if multiple matches are found we will clear potential duplicate offerings.
on(BetterBuysActions.acceptSuggestionAndMulesoftSuccess, BetterBuysActions.declineSuggestion, (state, action) => {
    const betterBuysToRemove = [];
    for (const key in state.entities) {
        if (key && state.entities[key] !== undefined) {
            const item = state.entities[key];
            if (item.originalProductNumber === action.oldNumber &&
                item.betterBuyProductNumber === action.newNumber) {
                betterBuysToRemove.push(key);
            }
        }
    }
    return betterBuysAdapter.removeMany(betterBuysToRemove, state);
}), on(BetterBuysActions.loadSavingsOverTimeSuccess, (state, action) => ({
    ...state,
    ...action.savingsOverTime,
    loaded: true,
    loading: false,
})));
const betterBuysReducer = (state, action) => _betterBuysReducer(state, action);

const selectBetterBuysState = createFeatureSelector(BETTER_BUYS_KEY);
const allBetterBuys = () => createSelector(selectBetterBuysState, (productState) => Object.values(productState?.entities));
const getBetterBuysById = (originalProductNumber) => createSelector(selectBetterBuysState, (betterBuysState) => betterBuysState?.entities[originalProductNumber]);
const betterBuysLoaded = createSelector(selectBetterBuysState, (betterBuysState) => betterBuysState.loaded);
const totalAnnualSavings = createSelector(selectBetterBuysState, (betterBuysState) => betterBuysState.totalAnnualSavings);
const estimatedAllTimeSavings = createSelector(selectBetterBuysState, (betterBuysState) => betterBuysState.estimatedAllTimeSavings);
const estimatedTodaySavings = createSelector(selectBetterBuysState, (betterBuysState) => betterBuysState.estimatedTodaySavings);
const estimatedYearToDateSavings = createSelector(selectBetterBuysState, (betterBuysState) => betterBuysState.estimatedYearToDateSavings);
const betterBuyEntities = createSelector(selectBetterBuysState, (betterBuysState) => betterBuysState.entities);
const remainingCount = createSelector(selectBetterBuysState, (betterBuysState) => betterBuysState.remaining);
const acceptedCount = createSelector(selectBetterBuysState, (betterBuysState) => betterBuysState.accepted);
const { selectIds: selectBetterBuysIds, selectEntities: selectBetterBuysEntities, selectAll: selectAllBetterBuys, selectTotal: selectTotalBetterBuys, } = betterBuysAdapter.getSelectors(selectBetterBuysState);

class BetterBuysFacadeService {
    constructor(store) {
        this.store = store;
        this.allBetterBuys$ = () => this.store.select(allBetterBuys());
        this.betterBuyById$ = (originalProductNumber) => this.store.select(getBetterBuysById(originalProductNumber));
    }
    loadBetterBuys(selectedCustomer) {
        this.store.dispatch(BetterBuysActions.load({ selectedCustomer }));
    }
}
BetterBuysFacadeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BetterBuysFacadeService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
BetterBuysFacadeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BetterBuysFacadeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BetterBuysFacadeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const selectCabState = createFeatureSelector(CAB_KEY);
const isCabStateReady = createSelector(selectCabState, state => state.loadingState !== LoadingState.notReady);
const selectCabProduct = (productNumber) => createSelector(selectCabState, (cabState) => cabState?.entities[productNumber]);

class CustomersAlsoBoughtStoreService {
    constructor(store) {
        this.store = store;
    }
    loadCustomersAlsoBought$(cabRequest) {
        return this.store.select(isCabStateReady).pipe(first(isReady => isReady), map(isReady => {
            this.store.dispatch(CustomersAlsoBoughtActions.FetchCabProductNumbers({
                request: cabRequest,
            }));
            return isReady;
        }));
    }
}
CustomersAlsoBoughtStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CustomersAlsoBoughtStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
CustomersAlsoBoughtStoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CustomersAlsoBoughtStoreService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CustomersAlsoBoughtStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const selectSellerShowcaseState = createFeatureSelector(SELLER_SHOWCASE_KEY);
const isSellerShowcaseStateReady = createSelector(selectSellerShowcaseState, state => state.loadingState !== LoadingState.notReady);

class SellerShowcaseService {
    constructor(store) {
        this.store = store;
    }
    loadSellerShowcase$() {
        return this.store.select(isSellerShowcaseStateReady).pipe(first(isReady => isReady), map(isReady => {
            this.store.dispatch(SellerShowcaseActions.FetchSellerShowcaseResult());
            return isReady;
        }));
    }
}
SellerShowcaseService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SellerShowcaseService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
SellerShowcaseService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SellerShowcaseService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SellerShowcaseService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const powerReviewAdapter = createEntityAdapter({
    selectId: pw => 0,
});
const initialPowerReviewProductState = powerReviewAdapter.getInitialState({
    loadingState: LoadingState.init,
});
// eslint-disable-next-line no-underscore-dangle
const powerReviewsProduct = createReducer(initialPowerReviewProductState, 
//Set this elements to be on a 'loading' state
on(ProductActions.FetchPowerReviewsProduct, state => {
    const updateChange = {
        loadingState: LoadingState.loading,
    };
    return powerReviewAdapter.upsertOne(updateChange, state);
}), on(ProductActions.UpdatePowerReviewsProduct, state => {
    const updateChange = {
        loadingState: LoadingState.loading,
    };
    return powerReviewAdapter.upsertOne(updateChange, state);
}), on(ProductActions.FetchPowerReviewsProductSuccess, (state, { productSnippet }) => {
    const updateChange = {
        loading: false,
        productSnippet,
        loadingState: LoadingState.loaded,
    };
    return powerReviewAdapter.upsertOne(updateChange, state);
}), on(ProductActions.UpdatePowerReviewsProductSuccess, (state, { productSnippet }) => {
    const newSnipped = {
        ...productSnippet.configuration,
        name: productSnippet.name,
        results: [
            ...state.entities[0].productSnippet.results,
            ...productSnippet.results,
        ],
    };
    const updateChange = {
        loading: false,
        productSnippet: newSnipped,
        loadingState: LoadingState.loaded,
    };
    return powerReviewAdapter.upsertOne(updateChange, state);
}), on(ProductActions.FetchPowerReviewsProductFailure, (state, { error }) => {
    const updateChange = {
        loading: false,
        loadingState: LoadingState.error,
        error,
    };
    return powerReviewAdapter.upsertOne(updateChange, state);
}));
const powerReviewsProductReducer = (state, action) => powerReviewsProduct(state, action);

const { selectAll: selectAll$4 } = powerReviewAdapter.getSelectors();
const selectPowerReviewState = createFeatureSelector(POWER_REVIEW_PRODUCT_KEY);
const selectPowerReviewResponse = state => powerReviewAdapter.getSelectors(selectPowerReviewState).selectAll(state)[0];
const selectAllPowerReviews = createSelector(selectPowerReviewState, selectAll$4);

class PowerReviewProductStoreService {
    constructor(store) {
        this.store = store;
    }
    loadPowerReviewsProduct(pageIds) {
        this.store.dispatch(ProductActions.FetchPowerReviewsProduct({
            pageIds,
        }));
        return this.store.select(selectPowerReviewResponse).pipe(first(pwr => pwr?.loadingState === LoadingState.loaded ||
            pwr?.loadingState === LoadingState.error), map(response => {
            const pwrMap = new Map();
            response.productSnippet?.results?.map(value => {
                pwrMap.set(value?.page_id, value?.rollup);
            });
            return pwrMap;
        }));
    }
    updatePowerReviewsProduct(pageIds) {
        this.store.dispatch(ProductActions.UpdatePowerReviewsProduct({
            pageIds,
        }));
        return this.store.select(selectPowerReviewResponse).pipe(first(pwr => pwr?.loadingState === LoadingState.loaded ||
            pwr?.loadingState === LoadingState.error), map(response => {
            const pwrMap = new Map();
            response.productSnippet?.results?.map(value => {
                pwrMap.set(value?.page_id, value?.rollup);
            });
            return pwrMap;
        }));
    }
    loadPowerReviewsProductFromStore() {
        return this.store.select(selectPowerReviewResponse).pipe(first(pwr => pwr?.loadingState === LoadingState.loaded ||
            pwr?.loadingState === LoadingState.error), map(response => {
            const pwrMap = new Map();
            response.productSnippet?.results?.map(value => {
                pwrMap.set(value?.page_id, value?.rollup);
            });
            return pwrMap;
        }));
    }
}
PowerReviewProductStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PowerReviewProductStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
PowerReviewProductStoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PowerReviewProductStoreService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PowerReviewProductStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class ProductStoreService {
    constructor(store) {
        this.store = store;
    }
    loadProductOrderabilities(productNumbers, shouldRetry = false, watermark, forceReload = false) {
        this.store.dispatch(ProductActions.LoadProductOrderabilities({
            productNumbers,
            watermark,
            forceReload,
            shouldRetry,
        }));
    }
    loadProductOrderability(productNumber, shouldRetry = false, watermark, forceReload = false) {
        this.loadProductOrderabilities([productNumber], shouldRetry, watermark, forceReload);
    }
}
ProductStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ProductStoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductStoreService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const selectCustomerRebateState = createFeatureSelector(CUSTOMER_REBATE_KEY);
const selectCustomerRebates = createSelector(selectCustomerRebateState, state => state.customerRebates);
const isCustomerRebateStateReady = createSelector(selectCustomerRebateState, state => state.loadingState !== LoadingState.loading);

class CustomerRebateService {
    constructor(store) {
        this.store = store;
    }
    loadCustomerRebateTakeoutOptions() {
        this.store.dispatch(RebateTakeoutOptionsActions.FetchRebateTakeoutOptions());
    }
    getCustomerRebates() {
        this.store.dispatch(CustomerRebateActions.FetchCustomerRebates());
        return this.store.select(selectCustomerRebates);
    }
}
CustomerRebateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CustomerRebateService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
CustomerRebateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CustomerRebateService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CustomerRebateService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

class ProductStateService {
    constructor(store, myProductsState, webSocketService, merchandisingStoreService, productSummaryStoreService, productInventoryStoreService, productContractStoreService, productDetailStoreService, productScoopStoreService, revenueManagementStoreService, searchStoreService, searchStoreServiceV2, opportunityStoreService, typeaheadStoreService, sneakPeekStoreService, nonUSFProductFacadeService, externalVendorFacadeService, betterBuysFacadeService, cabService, sellerShowcaseService, panAppState, powerReviewProductStoreService, browserStorageService, productStoreService, customerRebateService) {
        this.store = store;
        this.myProductsState = myProductsState;
        this.webSocketService = webSocketService;
        this.merchandisingStoreService = merchandisingStoreService;
        this.productSummaryStoreService = productSummaryStoreService;
        this.productInventoryStoreService = productInventoryStoreService;
        this.productContractStoreService = productContractStoreService;
        this.productDetailStoreService = productDetailStoreService;
        this.productScoopStoreService = productScoopStoreService;
        this.revenueManagementStoreService = revenueManagementStoreService;
        this.searchStoreService = searchStoreService;
        this.searchStoreServiceV2 = searchStoreServiceV2;
        this.opportunityStoreService = opportunityStoreService;
        this.typeaheadStoreService = typeaheadStoreService;
        this.sneakPeekStoreService = sneakPeekStoreService;
        this.nonUSFProductFacadeService = nonUSFProductFacadeService;
        this.externalVendorFacadeService = externalVendorFacadeService;
        this.betterBuysFacadeService = betterBuysFacadeService;
        this.cabService = cabService;
        this.sellerShowcaseService = sellerShowcaseService;
        this.panAppState = panAppState;
        this.powerReviewProductStoreService = powerReviewProductStoreService;
        this.browserStorageService = browserStorageService;
        this.productStoreService = productStoreService;
        this.customerRebateService = customerRebateService;
    }
    initialize() {
        this.panAppState
            .getDistinctSelectedCustomer()
            .pipe(filter(customer => customer?.selected), mergeMap(customer => combineLatest([of(customer), this.isInventoryEligible$()]).pipe(take(1))))
            .subscribe(([selectedCustomer, isInventoryEligible]) => {
            this.store.dispatch(NonUSFProductActions.resetStore());
            this.store.dispatch(ExternalVendorActions.resetStore());
            this.store.dispatch(BetterBuysActions.resetStore());
            this.store.dispatch(CustomerRebateActions.ResetStore());
            this.store.dispatch(RebateTakeoutOptionsActions.ResetStore());
            this.store.dispatch(PersonalizationActions.ResetStore());
            // storage
            this.store.dispatch(ProductActions.initializeStorage());
            this.betterBuysFacadeService.loadBetterBuys(selectedCustomer);
            this.customerRebateService.loadCustomerRebateTakeoutOptions();
            if (isInventoryEligible) {
                this.loadExternalVendors();
                this.loadNonUSFProducts();
            }
            // Resets
            this.store.dispatch(MyProductActions.ResetStore());
            this.store.dispatch(ProductChangesActions.ResetStore());
            this.store.dispatch(ProductSummaryActions.ResetStore());
            this.store.dispatch(ProductInventoryActions.ResetStore());
            this.store.dispatch(ProductContractActions.ResetStore());
            this.store.dispatch(CustomerPillActions.ResetStore());
            this.store.dispatch(SearchActions.ResetStore());
            this.store.dispatch(SearchActionsV2.ResetStore());
            this.store.dispatch(TypeaheadActions.ResetStore());
            this.store.dispatch(BrowseCategoryActions.ResetStore());
            this.store.dispatch(MslProductActions.ResetStore());
            this.store.dispatch(MerchandisingActions.ResetStore());
            this.store.dispatch(RevenueManagementActions.ResetStore());
            this.store.dispatch(CustomersAlsoBoughtActions.ResetStore());
            this.store.dispatch(SneakPeekActions.ResetStore());
            this.store.dispatch(SellerShowcaseActions.ResetStore());
        });
        // delayed operations that we want to be trigger only once
        /*
        TODO this feature will be re-enabled in the future #vsm1FjYN3cksLW1qI1g7 once we have test availability
        this.panAppState.user$
          .pipe(
            withLatestFrom(this.panAppState.customer$),
            filter(([user]) => !!user?.assignedCustomers),
            debounceTime(5000),
          )
          .subscribe(([user, customer]) => {
            // Resets
            this.store.dispatch(ProductChangesActions.RequestChanges());
            // TODO Disabled because of excessive/traffic load
            // this.store.dispatch(ProductSummaryActions.RequestChanges());
          });
    
         */
    }
    initializeStorage(user, customer) {
        const dbInfo = [];
        dbInfo.push({
            dbName: `${user.userId}_${customer.divisionNumber}_${customer.customerNumber}_${customer.departmentNumber}_${PRODUCTS_DOMAIN_NAME}`,
            objectStoreNames: PRODUCT_STORAGE_KEYS,
        });
        return this.browserStorageService.initialize({ dbInfo });
    }
    writeToOfflineStorage(user, customer, storeEntityName, state) {
        // eslint-disable-next-line max-len
        const dbName = `${user?.userId}_${customer?.divisionNumber}_${customer?.customerNumber}_${customer?.departmentNumber}_${PRODUCTS_DOMAIN_NAME}`;
        return this.browserStorageService.indexDB(dbName, storeEntityName, '0', state, MessageTypes.saveBrowserStorage);
    }
    getFromOfflineStorage(user, customer, storeEntityName) {
        // eslint-disable-next-line max-len
        const dbName = `${user?.userId}_${customer?.divisionNumber}_${customer?.customerNumber}_${customer?.departmentNumber}_${PRODUCTS_DOMAIN_NAME}`;
        return this.browserStorageService.loadFromOfflineStorage(dbName, storeEntityName);
    }
    // Store service wrappers
    // Summary
    loadProductSummaries(productNumbers) {
        this.productSummaryStoreService.loadProductSummaries(productNumbers);
    }
    loadProductSummary(productNumber) {
        this.productSummaryStoreService.loadProductSummary(productNumber);
    }
    loadProductSummary$(productNumber) {
        return this.store.select(isProductSummaryStateReady).pipe(first(ready => ready), map(ready => {
            this.productSummaryStoreService.loadProductSummary(productNumber);
            return ready;
        }));
    }
    loadProductSummaries$(productNumbers) {
        return this.store.select(isProductSummaryStateReady).pipe(first(ready => ready), map(ready => {
            this.productSummaryStoreService.loadProductSummaries(productNumbers);
            return ready;
        }));
    }
    // Inventory
    loadProductInventories(productNumbers) {
        this.productInventoryStoreService.loadProductInventories(productNumbers);
    }
    loadProductInventory(productNumber) {
        this.productInventoryStoreService.loadProductInventory(productNumber);
    }
    loadProductInventory$(productNumber) {
        return this.store.select(isProductInventoryStateReady).pipe(first(ready => ready), map(ready => {
            this.productInventoryStoreService.loadProductInventory(productNumber);
            return ready;
        }));
    }
    loadProductInventories$(productNumbers) {
        return this.store.select(isProductInventoryStateReady).pipe(first(ready => ready), map(ready => {
            this.productInventoryStoreService.loadProductInventories(productNumbers);
            return ready;
        }));
    }
    // Contract
    loadProductContracts(productNumbers) {
        this.productContractStoreService.loadProductContracts(productNumbers);
    }
    loadProductContract(productNumber) {
        this.productContractStoreService.loadProductContract(productNumber);
    }
    loadProductContract$(productNumber) {
        return this.store.select(isProductContractStateReady).pipe(first(ready => ready), map(ready => {
            this.productContractStoreService.loadProductContract(productNumber);
            return ready;
        }));
    }
    loadProductContracts$(productNumbers) {
        return this.store.select(isProductContractStateReady).pipe(first(ready => ready), map(ready => {
            this.productContractStoreService.loadProductContracts(productNumbers);
            return ready;
        }));
    }
    // Product Detail
    loadProductDetail(productNumber) {
        this.productDetailStoreService.loadProductDetail(productNumber);
    }
    loadProductDetails(productNumbers) {
        this.productDetailStoreService.loadProductDetails(productNumbers);
    }
    getProductDetail(productNumber) {
        return this.productDetailStoreService.getProductDetail(productNumber);
    }
    // Product Scoop
    loadProductScoops() {
        return this.productScoopStoreService.loadProductScoops();
    }
    loadRevenueManagement$() {
        return this.revenueManagementStoreService.loadRevenueManagement$();
    }
    loadSneakPeek$() {
        return this.sneakPeekStoreService.loadSneakPeek$();
    }
    // Search
    /**
     * Dispatches the search execution when the search state is ready.
     * Returns an observable that will emit a value of true when the state is ready
     * and the action was dispatched
     *
     * @param request Search request
     */
    loadSearchResponse(request) {
        return this.store.select(isSearchStateReady).pipe(first(ready => ready), map(ready => {
            this.searchStoreService.executeSearch(request);
            return ready;
        }));
    }
    executeSearchV2(request, correlationId, applyDefaultDeliveryMethodFilter) {
        return this.store.select(isSearchStateReadyV2).pipe(first(ready => ready), map(ready => {
            this.searchStoreServiceV2.executeSearch(request, correlationId, applyDefaultDeliveryMethodFilter);
            return ready;
        }));
    }
    getSearchResponse() {
        return this.searchStoreService.getSearchResponse();
    }
    getAllSearches() {
        return this.store.select(selectAllSearches);
    }
    createOpportunity(request) {
        return this.opportunityStoreService.createProductOpportunity(request);
    }
    searchedTerms(searchText) {
        return this.getAllSearches().pipe(mergeMap(searches => searches), filter(search => search.headerDetails?.headerText?.toLowerCase() ===
            searchText?.toLowerCase()));
    }
    getTypeahead(searchTerm) {
        return this.store.select(isTypeaheadStateReady).pipe(first(ready => ready), switchMap(() => this.typeaheadStoreService.getTypeahead(searchTerm)));
    }
    // Product
    loadProducts(productNumbers) {
        this.productStoreService.loadProductOrderabilities(productNumbers);
    }
    loadProductsWithRetry(productNumbers) {
        this.productStoreService.loadProductOrderabilities(productNumbers, true);
    }
    loadProduct(productNumber) {
        this.productStoreService.loadProductOrderability(productNumber);
    }
    loadSlimProducts(productNumbers, shouldRetry = false) {
        this.store.dispatch(ProductActions.LoadSlimProducts({ productNumbers, shouldRetry }));
    }
    loadProducts$(productNumbers) {
        return this.store.select(isProductSummaryStateReady).pipe(first(ready => ready), map(ready => {
            this.productStoreService.loadProductOrderabilities(productNumbers);
            return ready;
        }));
    }
    loadProduct$(productNumber) {
        return this.store.select(isProductSummaryStateReady).pipe(first(ready => ready), map(ready => {
            this.productStoreService.loadProductOrderability(productNumber);
            return ready;
        }));
    }
    loadPowerReviewsProduct$(pageIds) {
        return this.powerReviewProductStoreService.loadPowerReviewsProduct(pageIds);
    }
    updatePowerReviewsProduct$(pageIds) {
        return this.powerReviewProductStoreService.updatePowerReviewsProduct(pageIds);
    }
    loadPowerReviewsProductFromStore$() {
        return this.powerReviewProductStoreService.loadPowerReviewsProductFromStore();
    }
    // Non USF Products
    loadNonUSFProducts() {
        this.nonUSFProductFacadeService.loadNonUSFProducts();
    }
    //External Vendors
    loadExternalVendors() {
        this.externalVendorFacadeService.loadVendors();
    }
    showInventory$() {
        return this.store.select(selectShowInventory);
    }
    loadCabProductNumbers$(cabRequest) {
        return this.cabService.loadCustomersAlsoBought$(cabRequest);
    }
    isInventoryEligible$() {
        return combineLatest([
            this.showInventory$(),
            this.panAppState.feature$(['split_global_inventory']),
            this.panAppState.accessToken$,
        ]).pipe(filter(([f, f1, accessToken]) => {
            if (accessToken) {
                return true;
            }
            else {
                return false;
            }
        }), map(([showInventoryCCA, globalSplitFlag]) => showInventoryCCA && globalSplitFlag));
    }
    // Seller Incentive Showcase Products
    loadSellerShowcase$() {
        return this.sellerShowcaseService.loadSellerShowcase$();
    }
    sendPersonalizationEvent(personalizationType, productNumber) {
        this.merchandisingStoreService.sendPersonalizationEvent(personalizationType, productNumber);
    }
}
ProductStateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductStateService, deps: [{ token: i1.Store }, { token: MyProductFacadeService }, { token: i3.WebSocketService }, { token: MerchandisingStoreService }, { token: ProductSummaryStoreService }, { token: ProductInventoryStoreService }, { token: ProductContractStoreService }, { token: ProductDetailStoreService }, { token: ProductScoopStoreService }, { token: RevenueManagementStoreService }, { token: SearchStoreService }, { token: SearchStoreServiceV2 }, { token: OpportunityStoreService }, { token: TypeaheadStoreService }, { token: SneakPeekStoreService }, { token: NonUSFProductFacadeService }, { token: ExternalVendorFacadeService }, { token: BetterBuysFacadeService }, { token: CustomersAlsoBoughtStoreService }, { token: SellerShowcaseService }, { token: i5.PanAppState }, { token: PowerReviewProductStoreService }, { token: i1$1.BrowserStorageService }, { token: ProductStoreService }, { token: CustomerRebateService }], target: i0.ɵɵFactoryTarget.Injectable });
ProductStateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductStateService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductStateService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: MyProductFacadeService }, { type: i3.WebSocketService }, { type: MerchandisingStoreService }, { type: ProductSummaryStoreService }, { type: ProductInventoryStoreService }, { type: ProductContractStoreService }, { type: ProductDetailStoreService }, { type: ProductScoopStoreService }, { type: RevenueManagementStoreService }, { type: SearchStoreService }, { type: SearchStoreServiceV2 }, { type: OpportunityStoreService }, { type: TypeaheadStoreService }, { type: SneakPeekStoreService }, { type: NonUSFProductFacadeService }, { type: ExternalVendorFacadeService }, { type: BetterBuysFacadeService }, { type: CustomersAlsoBoughtStoreService }, { type: SellerShowcaseService }, { type: i5.PanAppState }, { type: PowerReviewProductStoreService }, { type: i1$1.BrowserStorageService }, { type: ProductStoreService }, { type: CustomerRebateService }]; } });

/* eslint-disable prefer-arrow/prefer-arrow-functions */
const browseCategoryResponseAdapter = createEntityAdapter({
    selectId: category => category.selectorId,
});
const initialBrowseCategoryResponseState = browseCategoryResponseAdapter.getInitialState({ loadingState: LoadingState.init, category: [] });
// eslint-disable-next-line no-underscore-dangle
const _browseCategoryReducer = createReducer(initialBrowseCategoryResponseState, 
//Set this elements to be on a 'loading' state
on(BrowseCategoryActions.FetchBrowseResponse, (state, { request }) => {
    const { searchFilterProperties } = request;
    const updateChange = {
        loadingState: LoadingState.loading,
        selectorId: searchFilterProperties ? searchFilterProperties : '0',
    };
    return browseCategoryResponseAdapter.upsertOne(updateChange, state);
}), 
// On failure the entity will go back to not be loading and not loaded
// this will allow for further requests to retry to fetch this entity
// Reset store
on(BrowseCategoryActions.ResetStore, () => initialBrowseCategoryResponseState), on(BrowseCategoryActions.FetchBrowseResponse, (state, { request }) => {
    const updateChange = {
        loadingState: LoadingState.loading,
        selectorId: request?.searchFilterProperties || '0',
        category: [],
    };
    return browseCategoryResponseAdapter.upsertOne(updateChange, state);
}), on(BrowseCategoryActions.FetchBrowseResponseSuccess, (state, { result, requestParams }) => {
    const { searchFilterProperties } = requestParams;
    const category = result?.map(cat => ({
        description: cat.description,
        dimensionId: cat.dimensionId,
    })) ?? [];
    const updateChange = {
        loadingState: LoadingState.loaded,
        category,
        selectorId: searchFilterProperties ? searchFilterProperties : '0',
    };
    return browseCategoryResponseAdapter.upsertOne(updateChange, state);
}), on(BrowseCategoryActions.FetchBrowseResponseFail, (state, { result }) => {
    const updateChange = {
        loadingState: LoadingState.error,
        selectorId: result.selectorId,
    };
    return browseCategoryResponseAdapter.upsertOne(updateChange, state);
}));
function browseCategoryReducer(state, action) {
    return _browseCategoryReducer(state, action);
}

const selectBrowseCategoryResponseState = createFeatureSelector(BROWSE_CATEGORIES_KEY);
const selectBrowseCategoryResponse = state => browseCategoryResponseAdapter
    .getSelectors(selectBrowseCategoryResponseState)
    .selectAll(state);
const selectCategoryList = (state) => browseCategoryResponseAdapter.getSelectors(selectBrowseCategoryResponseState);
const selectBrowseResponse = (selectorId) => createSelector(selectAllBrowse, browseCategories => {
    selectorId = selectorId || '0';
    const category = browseCategories.find(cat => cat.selectorId === selectorId);
    return category;
});
const { selectEntities: selectListGroupEntities, selectAll: selectAllBrowse, } = browseCategoryResponseAdapter.getSelectors(selectBrowseCategoryResponseState);

class BrowseCategoryStoreService {
    constructor(store) {
        this.store = store;
    }
    executeBrowse(request) {
        this.store.dispatch(BrowseCategoryActions.LoadBrowseResponse({ request }));
    }
    getCategories(selectorId) {
        return this.store.select(selectBrowseResponse(selectorId));
    }
}
BrowseCategoryStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowseCategoryStoreService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
BrowseCategoryStoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowseCategoryStoreService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowseCategoryStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const ANALYTICS_ACTIONS = {
    track: createAction('[NGRXTrace - Tracking] - Track Action', props()),
    /**
     * Product Detail Module
     */
    trackProductDetailPageLoad: createAction('[NgRxProduct - Analytics] Track Product Detail Page Load', props()),
    trackProductDetailFeatureLoad: createAction('[NgRxProduct - Analytics] Track Product Detail Feature Load', props()),
    trackSubstituteProductsInPDPage: createAction('[NgRxProduct - Analytics] Track Substitute Products in PDP', props()),
    trackSellerDetailPageLoad: createAction('[NgRxProduct - Analytics] Track Product Seller Detail Page Load', props()),
    trackLinkClick: createAction('[NgRXProduct - Analytics] Track User Clicks on Page Link', props()),
    /**
     * Search Module
     */
    trackCatalogPageLoad: createAction('[NgRxProduct - Analytics] Track Catalog Detail Page Load', props()),
    trackCatalogSubcategoryPageLoad: createAction('[NgRxProduct - Analytics] Track Catalog Subcategory Page Load', props()),
    trackCatalogCategoryPageLoad: createAction('[NgRxProduct - Analytics] Track Catalog Category Page Load', props()),
    trackCatalogBrowsePageLoad: createAction('[NgRxProduct - Analytics] Track Catalog Browse Page Load', props()),
    trackCatalogCategoryBrowsePageLoad: createAction('[NgRxProduct - Analytics] Track Catalog Category Browse Page Load', props()),
    trackSearchResultPageLoad: createAction('[NgRxProduct - Analytics] Track Search Result Page Load', props()),
    trackSearchSubmit: createAction('[NgRxProduct - Analytics] Track Search Submit', props()),
    trackTypeAheadSearchSubmit: createAction('[NgRxProduct - Analytics] Track TypeAhead Search Submit', props()),
    trackSearchResultClick: createAction('[NgRxProduct - Analytics] Track Search Result Click', props()),
    trackMoreSearchResultsLoading: createAction('[NgRxProduct - Analytics] Track More Search Results Loading', props()),
    trackRatingsAndReviewUserEngagement: createAction('[NgRxProduct - Analytics] Track Ratings and Review User Engagement', props()),
    trackReviewProductsSubmit: createAction('[NgRxProduct - Analytics] Track Review Submission', props()),
    trackReviewProductLoad: createAction('[NgRxProduct - Analytics] Track Review Product Load', props()),
    trackQAProductLoad: createAction('[NgRxProduct - Analytics] Track Ask a Question Page Load', props()),
    trackSubmitQuestionEvent: createAction('[NgRxProduct - Analytics] Track Submit Question Event', props()),
    trackTMNoteDetailPageLoad: createAction('[NgRxProduct - Analytics] Track TM Note Detail Page Load', props()),
    trackSearchFilterApply: createAction('[NgRxProduct - Analytics] Track Search Filter Apply', props()),
    trackQuestionAndAnswerUserEngagement: createAction('[NgRXProduct - Analytics] Track Question and Answer User Engagement', props()),
    trackOpportunityCancel: createAction('[NgRXProduct - Analytics] Track Opportunity Cancel', props()),
    trackCreateOpportunityClickButton: createAction('[NgRxProduct - Analytics] Track Create Opportunity Button', props()),
    trackUserSubmitOpportunityEvent: createAction('[NgRXProduct - Analytics] Track User Submit Opportunity Event ', props()),
    trackContinueOpportunityClickButton: createAction('[NgRXProduct - Analytics] Track User Click Continue Opportunity Button Event', props()),
    trackClickScoopBanner: createAction('[NgRXProduct - Analytics] Track User Clicks on Scoop Banner', props()),
    trackClickRebatesBanner: createAction('[NgRXProduct - Analytics] Track User Clicks on Rebates Banner', props()),
    trackClickRebatesLink: createAction('[NgRXProduct - Analytics] Track User Clicks on Rebates Link', props()),
    trackClickServeYouHeader: createAction('[NgRXProduct - Analytics] Track User Clicks on Serve You Header', props()),
    trackClickWhileSuppliesLastBanner: createAction('[NgRXProduct - Analytics] Track User Clicks on While Supplies Last Banner', props()),
    trackAllDealsPageLoaded: createAction('[NgRXProduct - Analytics] Track Page Loaded for All Available Deals Page', props()),
    trackSearchForSubs: createAction('[NgRXProduct - Analytics] Track User Search for Product Substiutes', props()),
    trackVisualCartridgeBanner: createAction('[NgRXProduct - Analytics] Track User Clicks on Marketing Banner', props()),
    trackUserOpenCPRModel: createAction('[NgRXProduct - Analytics] Track User Open CPR Modal', props()),
    trackUpdateCustomPricingRationaleClickButton: createAction('[NgRXProduct - Analytics] Track User Click Update Custom Pricing Rationale Button Event', props()),
    trackPDPShare: createAction('[NgRXProduct - Analytics] Track When User Share PDP', props()),
    trackPrintPDP: createAction('[NgRXProduct - Analytics] Track When User Print PDP', props()),
    trackPrintProductComparison: createAction('[NgRXProduct - Analytics] Track When User Print Product Comparison Page', props()),
    trackDownloadProductComparison: createAction('[NgRXProduct - Analytics] Track When User Download Product Comparison Page', props()),
    trackTogglingUsFoodsDeliveredProductsOn: createAction('[NgRXProduct - Analytics] Track When Users Turning The US Foods Direct Toggle On', props()),
    trackTogglingUsFoodsDeliveredProductsOff: createAction('[NgRXProduct - Analytics] Track When Users Turning The US Foods Direct Toggle Off', props()),
    trackUserStartingProductCompare: createAction('[NgRXProduct - Analytics] Track When Users Click On Product Compare button', props()),
    trackProductCompareSubmit: createAction('[NgRXProduct - Analytics] Track When Users Submit a Product Comparison', props()),
    trackUserStartingProductCompareOnSearchForSubs: createAction('[NgRXProduct - Analytics] Track When Users Click On Product Compare Button On Search For Subs', props()),
    trackProductComparePageLoad: createAction('[NgRxProduct - Analytics] Track Product Compare Page Load', props()),
    trackUserProductCompareEllipsisMenu: createAction('[NgRxProduct - Analytics] Track Users Comparing Products From The Ellipsis Menu', props()),
    trackSubmitOrderStart: createAction('[NgRxProduct - Analytics] - Track Submit Order Start', props()),
    trackPDPView: createAction('[NgRxProduct - Personalization] Track PDP view', props()),
    trackBetterBuysPageLoad: createAction('[NgRxProduct - Analytics] Track Better Buys Page Load', props()),
    trackBetterBuysAccept: createAction('[NgRxProduct - Analytics] Track Better Buys Accept Swap', props()),
    trackBetterBuysReject: createAction('[NgRxProduct - Analytics] Track Better Buys Reject Swap', props()),
    trackProductCompare: createAction('[NgRxProduct - Analytics] Track Product Compare Click Event on Better Buys Page', props()),
    trackUserCloseQuickViewModel: createAction('[NgRXProduct - Analytics] Track User Close Quick View Modal', props()),
    trackProductWorkWellWithViewButton: createAction('[NgRXProduct - Analytics] Track User Click On Product Works Well With View Button', props()),
    trackBannerDealsForYouDisplayed: createAction('[NgRXProduct - Analytics] Track Display of Banner Deals For You', props()),
    trackBannerDealsForYouView: createAction('[NgRXProduct - Analytics] Track User Views Banner Deals For You', props()),
    trackBannerDealsForYouClick: createAction('[NgRXProduct - Analytics] Track User Click On Banner Deals For You', props()),
    trackRevManWinbacksView: createAction('[NgRXProduct - Analytics] Track Revenue Management Winbacks View', props()),
    trackUserOpenQuickViewModal: createAction('[NgRXProduct - Analytics] Track User Open Quick View Modal', props()),
    trackEquipmentAndSuppliesHeader: createAction('[NgRXProduct - Analytics] Track clicks on the Equipment & Supplies in the header', props()),
    trackEquipmentAndSuppliesHomeTile: createAction('[NgRXProduct - Analytics] Track clicks on the Equipment & Supplies home page tile', props()),
    trackClickedSearchFilter: createAction('[NgRxProduct - Analytics] Track the Clicked Filter from Left side navigation', props()),
    trackSearchPageSingleBanner: createAction('[NgRxProduct - Analytics] Track Search Page Single Banner', props()),
    trackScoopPageLoad: createAction('[NgRxProduct - Analytics] Track Scoop Page Load', props()),
    trackScoopPreviewBannerClick: createAction('[NgRxProduct - Analytics] Track Scoop Preview Banner Click', props()),
    trackShareViaEmailPDPOpenModal: createAction('[NgRxProduct - Analytics] Track Modal Open for Share PDP Via Email', props()),
    trackShareViaEmailPDP: createAction('[NgRxProduct - Analytics] Track Share PDP Via Email', props()),
    /**
     * Non-US Foods Module
     */
    trackCreateNonUsfProductSuccess: createAction('[NgRxProduct - Analytics] Track Create Non-US Foods Product Success', props()),
    trackManageNonUsfProductPageLoaded: createAction('[NgRxInventory - Analytics] - Track page load of manage non-usf product', props()),
    trackMerchCarouselDisplayed: createAction('[NgRXProduct - Analytics] Track Display of a Merchandising Carousel', props()),
    trackYMANScroll: createAction('[NgRXProduct - Analytics] Track Scroll of You may also need Carousel', props()),
    trackCABScroll: createAction('[NgRXProduct - Analytics] Track Scroll of Customers Also Bought Carousel', props()),
    trackSellerQuickViewPageLoad: createAction('[NgRxProduct - Analytics] Track Seller Quick View Page Load', props()),
    trackSellerIncentivesPageLoad: createAction('[NgRxProduct - Analytics] Track Seller Incentives Page Load', props()),
    trackDirectInfoClick: createAction('[NgRxProduct - Analytics] Track Direct Info Click', props()),
    trackProductImageClick: createAction('[NgRxProduct - Analytics] Track Product Image Click', props()),
    trackRebatesPageLoad: createAction('[NgRxProduct - Analytics] Track Rebates Page Load', props()),
    trackBecomeACustomerButtonClick: createAction('[NgRxProduct - Analytics] Track Become a Customer Button Click', props()),
};

class ProductAnalyticsService {
    constructor(store) {
        this.store = store;
    }
    track(tracking) {
        this.store.dispatch(ANALYTICS_ACTIONS.track({ tracking }));
    }
    /**
     * Product Detail Page
     */
    trackProductDetailPageLoad(productNumber, attributes, betterBuyProductNumber, betterBuyProductAttributeList, price) {
        const tracking = {
            analytics: {
                data: {
                    pageName: `${PAGE_NAME_BASE_PATH.r4}${PAGE_DETAIL.productDetails}`,
                    event: EVENT.pageLoaded,
                    capability: TEAM_CAPABILITY,
                    productNumber,
                    attributes,
                    betterBuyProductNumber,
                    betterBuyProductAttributeList,
                    price,
                },
                transformFunc: productDetailPageLoad,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackProductDetailPageLoad({ tracking }));
    }
    trackProductDetailFeatureLoad(productNumber, attributes, customersAlsoBought, mayAlsoNeed, productRating, questionCount, betterBuyProductNumber, betterBuyProductAttributeList) {
        const tracking = {
            analytics: {
                data: {
                    pageName: `${PAGE_NAME_BASE_PATH.r4}${PAGE_DETAIL.productDetails}`,
                    event: EVENT.featureLoaded,
                    capability: TEAM_CAPABILITY,
                    productNumber,
                    attributes,
                    customersAlsoBought,
                    mayAlsoNeed,
                    productRating,
                    questionCount,
                    betterBuyProductNumber,
                    betterBuyProductAttributeList,
                },
                transformFunc: productDetailPageLoad,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackProductDetailFeatureLoad({ tracking }));
    }
    trackProductDetailPageView(productNumber) {
        const tracking = {
            personalization: {
                events: [
                    {
                        pageName: `${PAGE_NAME_BASE_PATH.r4}${PAGE_DETAIL.productDetails}`,
                        pageType: MONETATE_ENGINE_NAMES.pageView,
                        pageValue: `${PAGE_NAME_BASE_PATH.r4}${PAGE_DETAIL.productDetails}`.replace(/\s/g, ''),
                        addProductDetails: MONETATE_ENGINE_NAMES.productDetailView,
                        productNumber,
                    },
                ],
                transformFunc: productDetailPageLoadMonetate,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackPDPView({ tracking }));
    }
    /**
     * Product Detail Page
     */
    trackSellerDetailPageLoad(productNumber, attributes) {
        const tracking = {
            analytics: {
                data: {
                    pageName: `${PAGE_NAME_BASE_PATH.r4}${PAGE_DETAIL.sellerDetails}`,
                    event: EVENT.pageLoaded,
                    capability: TEAM_CAPABILITY,
                    productNumber,
                    attributes,
                },
                transformFunc: productDetailPageLoad,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackSellerDetailPageLoad({ tracking }));
    }
    trackSubstituteProductsInPDP(product, parentProduct, isSellerCustomer) {
        const pageComplement = isSellerCustomer
            ? PAGE_DETAIL.sellerDetails
            : PAGE_DETAIL.productDetails;
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.pageLoaded,
                    pageName: `${PAGE_NAME_BASE_PATH.r4}${pageComplement}`,
                    capability: TEAM_CAPABILITY,
                    product,
                    parentProduct,
                },
                transformFunc: substituteProductsInPDP,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackSubstituteProductsInPDPage({ tracking }));
    }
    trackLinkClick(linkText) {
        const tracking = {
            analytics: {
                data: {
                    capability: TEAM_CAPABILITY,
                    event: EVENT.linkClick,
                    link: {
                        text: linkText,
                    },
                },
                transformFunc: linkClickTransform,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackLinkClick({ tracking }));
    }
    /**
     * Click on Scoop Banner
     */
    trackClickScoopBanner() {
        const tracking = {
            analytics: {
                data: {
                    capability: TEAM_CAPABILITY,
                    activePromo: {
                        promoId: 'scoop',
                    },
                    event: EVENT.promoBannerClick,
                },
                transformFunc: scoopBannerClick,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackClickScoopBanner({ tracking }));
    }
    /**
     * Click on Rebates Banner
     */
    trackClickRebatesBanner() {
        const tracking = {
            analytics: {
                data: {
                    capability: TEAM_CAPABILITY,
                    activePromo: {
                        promoId: 'customer incentive',
                    },
                    event: EVENT.promoBannerClick,
                },
                transformFunc: rebatesBannerClick,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackClickRebatesBanner({ tracking }));
    }
    /**
     * Click on Rebates Link
     */
    trackClickRebatesLink() {
        const tracking = {
            analytics: {
                data: {
                    capability: TEAM_CAPABILITY,
                    button: {
                        buttonName: 'customer incentive:header',
                    },
                    event: EVENT.buttonClick,
                },
                transformFunc: rebatesDropdownClick,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackClickRebatesLink({ tracking }));
    }
    /**
     * Click on Serve You Header
     */
    trackClickServeYouHeader() {
        const tracking = {
            analytics: {
                data: {
                    capability: TEAM_CAPABILITY,
                    button: {
                        buttonName: BUTTON_NAME.serveYou,
                    },
                    activePromo: {
                        promoId: 'serveYou',
                    },
                    event: EVENT.buttonClick,
                },
                transformFunc: serveYouHeaderClick,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackClickServeYouHeader({ tracking }));
    }
    /**
     * While Supplies Last banner click
     */
    trackClickWhileSuppliesLastBanner() {
        const tracking = {
            analytics: {
                data: {
                    capability: TEAM_CAPABILITY,
                    activePromo: {
                        promoId: 'while supplies last',
                    },
                    event: EVENT.promoBannerClick,
                },
                transformFunc: whileSuppliesLastClick,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackClickWhileSuppliesLastBanner({ tracking }));
    }
    trackSearchPageSingleBanner(promoId) {
        const tracking = {
            analytics: {
                data: {
                    capability: TEAM_CAPABILITY,
                    activePromo: {
                        promoId,
                    },
                    event: EVENT.promoBannerClick,
                },
                transformFunc: searchPageSingleBanner,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackSearchPageSingleBanner({ tracking }));
    }
    /**
     * Click on Cartridge Banner
     */
    trackVisualCartridgeBanner(bannerImageId) {
        const tracking = {
            analytics: {
                data: {
                    capability: TEAM_CAPABILITY,
                    event: EVENT.promoBannerClick,
                    bannerImageId,
                },
                transformFunc: cartridgeBannerClickTrack,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackVisualCartridgeBanner({ tracking }));
    }
    /**
     * All Available Deals Page: Page Load
     */
    trackAllDealsPageLoaded(productsArray) {
        const tracking = {
            analytics: {
                data: {
                    capability: TEAM_CAPABILITY,
                    page: {
                        pageName: DEALS.pageName,
                    },
                    event: EVENT.pageLoaded,
                    products: productsArray,
                },
                transformFunc: allDealsPageLoaded,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackAllDealsPageLoaded({ tracking }));
    }
    /**
     * Catalog Search Shop Products
     */
    /**
     * Search Catalog Page
     */
    trackSearchCatalogPageLoad() {
        const tracking = {
            analytics: {
                data: {
                    pageName: `${PAGE_NAME_BASE_PATH.r4}${PAGE_DETAIL.catalogDetails}`,
                    event: EVENT.pageLoaded,
                    capability: TEAM_CAPABILITY,
                },
                transformFunc: catalogPageLoad,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackCatalogPageLoad({ tracking }));
    }
    /**
     * Catalog Category Page
     */
    trackCatalogCategoryPageLoad(category) {
        const tracking = {
            analytics: {
                data: {
                    pageName: `${PAGE_NAME_BASE_PATH.r4}${PAGE_DETAIL.catalogDetails}`,
                    event: EVENT.pageLoaded,
                    capability: TEAM_CAPABILITY,
                    category,
                },
                transformFunc: catalogCategoryPageLoad,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackCatalogCategoryPageLoad({ tracking }));
    }
    /**
     * Catalog Subcategory Page
     */
    trackCatalogSubcategoryPageLoad(filters) {
        const tracking = {
            analytics: {
                data: {
                    pageName: `${PAGE_NAME_BASE_PATH.r4}${PAGE_DETAIL.catalogDetails}`,
                    event: EVENT.pageLoaded,
                    capability: TEAM_CAPABILITY,
                    filters,
                },
                transformFunc: catalogSubcategoryPageLoad,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackCatalogSubcategoryPageLoad({ tracking }));
    }
    /**
     *  Search Catalog Browse Page
     */
    trackSearchCatalogBrowsePageLoad(variantValue) {
        const tracking = {
            analytics: {
                data: {
                    pageName: `${PAGE_NAME_BASE_PATH.r4}${PAGE_DETAIL.catalogDetails}`,
                    pageVariant: `${GUEST_LANDING_PAGE.variantName} ${variantValue}`,
                    event: EVENT.pageLoaded,
                    capability: TEAM_CAPABILITY,
                },
                transformFunc: catalogPageLoad,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackCatalogBrowsePageLoad({ tracking }));
    }
    /**
     * Catalog Category Browse Page
     */
    trackCatalogCategoryBrowsePageLoad(category) {
        const tracking = {
            analytics: {
                data: {
                    pageName: `${PAGE_NAME_BASE_PATH.r4}${PAGE_DETAIL.catalogDetails}`,
                    event: EVENT.pageLoaded,
                    capability: TEAM_CAPABILITY,
                    category,
                },
                transformFunc: catalogCategoryBrowsePageLoad,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackCatalogCategoryBrowsePageLoad({ tracking }));
    }
    /**
     * Search Result Page
     */
    trackSearchResultPageLoad(bannerImageIds, filters, searchType, products, searchTerm, isNewSearchEnabled, totalCounts) {
        const tracking = {
            analytics: {
                data: {
                    pageName: `${PAGE_NAME_BASE_PATH.r4}${SEARCH_PAGE_TRACK.searchResults}`,
                    event: EVENT.pageLoaded,
                    capability: TEAM_CAPABILITY,
                    filters,
                    bannerImageIds,
                    searchType,
                    products,
                    searchEngine: isNewSearchEnabled
                        ? SEARCH_ENGINE.coveo
                        : SEARCH_ENGINE.endeca,
                    searchTerm,
                    totalCount: totalCounts,
                },
                transformFunc: searchResultPageLoad,
            },
            personalization: {
                events: [
                    {
                        pageName: `${PAGE_NAME_BASE_PATH.r4}${SEARCH_PAGE_TRACK.searchResults}`,
                        pageType: MONETATE_EVENT_NAMES.setPageType,
                        pageValue: `${PAGE_NAME_BASE_PATH.r4}${SEARCH_PAGE_TRACK.searchResults}`.replace(/\s/g, ''),
                        addProducts: MONETATE_EVENT_NAMES.addProducts,
                        searchTerm,
                    },
                ],
                transformFunc: searchResultPageLoadMonetate,
            },
        };
        tracking.analytics.data.filters = filters;
        this.store.dispatch(ANALYTICS_ACTIONS.trackSearchResultPageLoad({ tracking }));
    }
    /**
     * Search Submit
     */
    trackSearchSubmit(searchRequest, searchType, isNewSearchEnabled) {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.searchSubmit,
                    capability: TEAM_CAPABILITY,
                    searchRequest,
                    searchType,
                    activePromo: {
                        promo: '',
                    },
                    searchEngine: isNewSearchEnabled
                        ? SEARCH_ENGINE.coveo
                        : SEARCH_ENGINE.endeca,
                },
                transformFunc: searchSubmitFn,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackSearchSubmit({ tracking }));
    }
    /**
     * Search TypeAhead
     */
    trackTypeAheadSearch(typeAheadTrackingData) {
        const { category, term, rank, searchDimensionId } = typeAheadTrackingData;
        const isProductTypeAheadSearch = category === TypeaheadGroupNames.products;
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.searchSubmit,
                    capability: TEAM_CAPABILITY,
                    searchType: SEARCH_TYPE$1.typeAhead,
                    term,
                    rank,
                    dimensionId: searchDimensionId,
                },
                transformFunc: isProductTypeAheadSearch
                    ? productTypeAheadSubmitFn
                    : searchTypeAheadSubmitFn,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackTypeAheadSearchSubmit({ tracking }));
    }
    /**
     * Search Result Click
     */
    trackSearchResultClick(productNumber, divisionNumber, attributes, resultRank, price) {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.searchResultClick,
                    capability: TEAM_CAPABILITY,
                    attributes,
                    productNumber,
                    divisionNumber,
                    resultRank,
                    price,
                },
                transformFunc: searchResultClickFn,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackSearchResultClick({ tracking }));
    }
    /**
     * More Search Results Loading
     */
    trackMoreSearchResultsLoading(products) {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.loadMore,
                    capability: TEAM_CAPABILITY,
                    products,
                },
                transformFunc: searchResultsLoadMoreTransform,
            },
            personalization: {
                events: [
                    {
                        pageName: `${PAGE_NAME_BASE_PATH.r4}${SEARCH_PAGE_TRACK.searchResults}`,
                        pageType: MONETATE_EVENT_NAMES.setPageType,
                        pageValue: `${PAGE_NAME_BASE_PATH.r4}${SEARCH_PAGE_TRACK.searchResults}`.replace(/\s/g, ''),
                        addProducts: MONETATE_EVENT_NAMES.addProducts,
                    },
                ],
                transformFunc: searchResultPageLoadMonetate,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackMoreSearchResultsLoading({ tracking }));
    }
    /**
     * Ratings And Reviews User Engagement
     */
    trackRatingsAndReviewsUserEngagement(divisionNumber, productNumber, productAttributes) {
        const tracking = this.generateGenericTrackingData(EVENT.reviewEngagement, divisionNumber, productNumber, productAttributes);
        this.store.dispatch(ANALYTICS_ACTIONS.trackRatingsAndReviewUserEngagement({ tracking }));
    }
    trackReviewProductsSubmit(divisionNumber, productNumber, productAttributes, productRating) {
        const tracking = this.generateGenericTrackingData(EVENT.reviewSubmit, divisionNumber, productNumber, productAttributes);
        tracking.analytics.data.products[0].productRating = productRating;
        this.store.dispatch(ANALYTICS_ACTIONS.trackReviewProductsSubmit({ tracking }));
    }
    trackReviewProductsLoad(divisionNumber, productNumber, productAttributes) {
        const tracking = this.generateGenericTrackingData(EVENT.pageLoaded, divisionNumber, productNumber, productAttributes);
        tracking.analytics.data = {
            ...tracking.analytics.data,
            page: {
                pageName: `${PAGE_NAME_BASE_PATH.r4}${REVIEW_PRODUCT.writeReview}`,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackReviewProductLoad({ tracking }));
    }
    trackQAPageLoad(divisionNumber, productNumber, productAttributes) {
        const tracking = this.generateGenericTrackingData(EVENT.pageLoaded, divisionNumber, productNumber, productAttributes);
        tracking.analytics.data = {
            ...tracking.analytics.data,
            page: {
                pageName: `${PAGE_NAME_BASE_PATH.r4}${REVIEW_PRODUCT.askQuestion}`,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackQAProductLoad({ tracking }));
    }
    trackSubmitQuestionEvent(divisionNumber, productNumber, productAttributes) {
        const tracking = this.generateGenericTrackingData(EVENT.submitQuestion, divisionNumber, productNumber, productAttributes);
        this.store.dispatch(ANALYTICS_ACTIONS.trackSubmitQuestionEvent({ tracking }));
    }
    trackTMNoteDetailPageLoad(tmNoteID, products) {
        const tracking = this.generateGenericTrackingData(EVENT.pageLoaded, products);
        tracking.analytics.transformFunc = tmNoteDetailPageLoadTransform;
        tracking.analytics.data = {
            ...tracking.analytics.data,
            page: {
                pageName: `${PAGE_NAME_BASE_PATH.r4}${TM_NOTES_DETAIL_PAGE.tmNotesDetailPage}`,
            },
            promo: {
                list: tmNoteID,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackTMNoteDetailPageLoad({ tracking }));
    }
    trackSearchFilterApply(filters) {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.filtersApplied,
                    capability: TEAM_CAPABILITY,
                    filters: this.searchFilterApplyTransformer(filters),
                },
                transformFunc: searchFilterApplyTransform,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackSearchFilterApply({ tracking }));
    }
    searchFilterApplyTransformer(filters) {
        const result = filters.split(' ').join('');
        const index = [0, ...this.indexFinder(['=', ',', ';'], result)];
        return this.transformerConverter(index, result);
    }
    transformerConverter(index, filters) {
        index.forEach(i => {
            const filtered = filters.charAt(i).toLowerCase();
            filters = filters
                .substring(0, i)
                .concat(filtered)
                .concat(filters.substring(i + 1));
        });
        return filters;
    }
    indexFinder(keys, filters) {
        const index = [];
        for (let i = 0; i < filters.length; i++) {
            if (keys.includes(filters[i])) {
                index.push(i + 1);
            }
        }
        return index;
    }
    /**
     * Track search for substitutes
     */
    trackSearchForSubs(products, searchTerm) {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.searchSubmit,
                    capability: TEAM_CAPABILITY,
                    promo: '',
                    activePromo: '',
                    search: {
                        term: searchTerm,
                        searchType: SEARCH_TYPE$1.searchForSubs,
                    },
                    products,
                },
                transformFunc: searchForSubsTransform,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackSearchForSubs({ tracking }));
    }
    trackQAEngagement(divisionNumber, productNumber, productAttributes) {
        const tracking = this.generateGenericTrackingData(EVENT.qaEngangement, divisionNumber, productNumber, productAttributes);
        tracking.analytics.data = {
            ...tracking.analytics.data,
            event: EVENT.qaEngangement,
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackQuestionAndAnswerUserEngagement({ tracking }));
    }
    trackOpportunityCancel(divisionNumber, productNumber, productAttributes) {
        const tracking = this.generateGenericTrackingData(EVENT.opportunityCancel, divisionNumber, productNumber, productAttributes);
        tracking.analytics.data = {
            ...tracking.analytics.data,
            modal: {
                name: 'create opportunity',
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackOpportunityCancel({ tracking }));
    }
    trackCreateOpportunityClickButton(divisionNumber, productNumber, productAttributes) {
        const tracking = this.generateGenericTrackingData(EVENT.opportunityOpen, divisionNumber, productNumber, productAttributes);
        tracking.analytics.data = {
            ...tracking.analytics.data,
            modal: {
                name: `${CREATE_OPPORTUNITY.modalName}`,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackCreateOpportunityClickButton({ tracking }));
    }
    trackUserSubmitOpportunityEvent(productData) {
        const tracking = this.generateGenericTrackingData(EVENT.opportunitySubmit, productData?.divisionNumber, productData?.productNumber, productData?.productAttributes);
        tracking.analytics.data = {
            ...tracking.analytics.data,
            pageName: productData?.pageName,
            publishNote: productData?.publishNote,
        };
        if (!!tracking?.analytics) {
            tracking.analytics.transformFunc = userSubmitOpportunity;
        }
        this.store.dispatch(ANALYTICS_ACTIONS.trackUserSubmitOpportunityEvent({ tracking }));
    }
    trackOpportunityContinue(divisionNumber, productNumber, productAttributes) {
        const tracking = this.generateGenericTrackingData(EVENT.opportunityContinue, divisionNumber, productNumber, productAttributes);
        tracking.analytics.data = {
            ...tracking.analytics.data,
            publish: {
                featureName: 'create opportunity',
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackContinueOpportunityClickButton({ tracking }));
    }
    trackCustomPricingRationaleUpdate(divisionNumber, productNumber, productAttributes, priceType, pageName) {
        const tracking = this.generateGenericTrackingData(EVENT.customPricingRationaleUpdate, divisionNumber, productNumber, productAttributes);
        const products = Array.isArray(tracking.analytics.data.products)
            ? tracking.analytics.data.products
            : [];
        const prods = [];
        products.forEach(product => {
            const prod = {
                ...product,
                cpr: CUSTOM_PRICING_RATIONALE.cprFlag,
                setPriceType: priceType,
            };
            prods.push(prod);
        });
        tracking.analytics.data.products = prods;
        tracking.analytics.data = {
            ...tracking.analytics.data,
            publish: {
                featureName: 'custom pricing rationale',
            },
            ...(pageName && { page: { pageName } }),
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackUpdateCustomPricingRationaleClickButton({
            tracking,
        }));
    }
    trackPDPShare(divisionNumber, productNumber, productAttributes) {
        const tracking = this.generateGenericTrackingData(EVENT.sharePrintDownload, divisionNumber, productNumber, productAttributes);
        tracking.analytics.data = {
            ...tracking.analytics.data,
            file: {
                type: SHARE_TYPE.fileType,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackPDPShare({ tracking }));
    }
    trackPrintPDP() {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.sharePrintDownload,
                    capability: TEAM_CAPABILITY,
                    fileType: FILE_TYPE.fileType,
                },
                transformFunc: generatePrintPDPData,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackPrintPDP({ tracking }));
    }
    trackPrintProductComparison() {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.sharePrintDownload,
                    capability: TEAM_CAPABILITY,
                    file: PRODUCT_COMPARISON_PRINT,
                },
                transformFunc: generateShareComparisonEvents,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackPrintProductComparison({ tracking }));
    }
    trackDownloadProductComparison() {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.sharePrintDownload,
                    capability: TEAM_CAPABILITY,
                    file: PRODUCT_COMPARISON_DOWNLOAD,
                },
                transformFunc: generateShareComparisonEvents,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackDownloadProductComparison({ tracking }));
    }
    trackUserOpenCPRModel(divisionNumber, productNumber, productAttributes) {
        const tracking = this.generateGenericTrackingData(EVENT.cprModalOpen, divisionNumber, productNumber, productAttributes);
        tracking.analytics.data = {
            ...tracking.analytics.data,
            modal: {
                name: CPR.modalName,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackUserOpenCPRModel({ tracking }));
    }
    trackTogglingUsFoodsDeliveredProductsOn() {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.buttonClick,
                    capability: TEAM_CAPABILITY,
                    button: {
                        name: BUTTON_NAME.buttonOn,
                    },
                },
                transformFunc: searchFilterToggleOn,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackTogglingUsFoodsDeliveredProductsOn({ tracking }));
    }
    trackTogglingUsFoodsDeliveredProductsOff() {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.buttonClick,
                    capability: TEAM_CAPABILITY,
                    button: {
                        name: BUTTON_NAME.buttonOff,
                    },
                },
                transformFunc: searchFilterToggleOff,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackTogglingUsFoodsDeliveredProductsOff({ tracking }));
    }
    trackUserStartingProductCompare() {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.buttonClick,
                    capability: TEAM_CAPABILITY,
                    button: {
                        buttonName: BUTTON_NAME.compareProducts,
                    },
                },
                transformFunc: productCompareStart,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackUserStartingProductCompare({ tracking }));
    }
    trackProductCompareSubmit(productTrackingData) {
        const tracking = this.generateGenericTrackingData(EVENT.compareSubmit, productTrackingData);
        this.store.dispatch(ANALYTICS_ACTIONS.trackProductCompareSubmit({ tracking }));
    }
    trackUserStartingProductCompareOnSearchForSubs(divisionNumber, productNumber, productAttributes) {
        const tracking = this.generateGenericTrackingData(EVENT.buttonClick, divisionNumber, productNumber, productAttributes);
        if (tracking.analytics) {
            tracking.analytics.transformFunc = productCompareStartOnSearchForSubs;
        }
        this.store.dispatch(ANALYTICS_ACTIONS.trackUserStartingProductCompareOnSearchForSubs({
            tracking,
        }));
    }
    trackProductComparePageLoad(productsData) {
        const tracking = {
            analytics: {
                data: {
                    pageName: `${PAGE_NAME_BASE_PATH.r4}${PAGE_PRODUCT_COMPARE.productCompare}`,
                    event: EVENT.pageLoaded,
                    capability: TEAM_CAPABILITY,
                    products: productsData,
                },
                transformFunc: productComparePageLoad,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackProductComparePageLoad({ tracking }));
    }
    trackUserProductCompareEllipsisMenu(productData) {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.compareSubmit,
                    capability: TEAM_CAPABILITY,
                    productData,
                },
                transformFunc: userProductCompareEllipsisMenu,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackUserProductCompareEllipsisMenu({ tracking }));
    }
    trackMonetateSubmitOrder(order) {
        const tracking = {
            personalization: {
                events: [{ order }],
                transformFunc: submitOrderStartMonetateTracing,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackSubmitOrderStart({ tracking }));
    }
    trackProductWorksWellWithButton(productsData) {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.featureEngagement,
                    capability: TEAM_CAPABILITY,
                    engagementType: ENGAGEMENT.engagementType,
                    productsData,
                },
                transformFunc: productWorksWellWithButton,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackProductWorkWellWithViewButton({ tracking }));
    }
    trackBannerDealsDisplayed() {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.featureLoaded,
                    capability: TEAM_CAPABILITY,
                    engagement: { engagementType: 'deals for you displayed' },
                },
                transformFunc: bannerDealsDisplayed,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackBannerDealsForYouDisplayed({ tracking }));
    }
    trackBannerDealsView() {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.featureEngagement,
                    capability: TEAM_CAPABILITY,
                    promo: { list: 'deals for you' },
                    engagement: { engagementType: 'banner view' },
                },
                transformFunc: bannerDealsView,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackBannerDealsForYouView({ tracking }));
    }
    trackBannerDealsClick() {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.promoBannerClick,
                    capability: TEAM_CAPABILITY,
                    activePromo: ACTIVE_PROMO,
                },
                transformFunc: bannerDealsClick,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackBannerDealsForYouClick({ tracking }));
    }
    trackRevManWinbacksView(products) {
        const tracking = this.generateGenericTrackingData(EVENT.featureEngagement, products);
        if (!!tracking?.analytics) {
            tracking.analytics.transformFunc = revManWinbacksView;
        }
        this.store.dispatch(ANALYTICS_ACTIONS.trackRevManWinbacksView({ tracking }));
    }
    trackBetterBuysPageLoad() {
        const tracking = {
            analytics: {
                data: {
                    pageName: `${PAGE_NAME_BASE_PATH.r4}${BETTER_BUYS.betterBuys}`,
                    event: EVENT.pageLoaded,
                    capability: LISTS_CAPABILITY,
                },
                transformFunc: betterBuysPageLoad,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackBetterBuysPageLoad({ tracking }));
    }
    trackUserOpenQuickViewModel(divisionNumber, productNumber, productAttributes, inStock, alternative) {
        const tracking = this.generateGenericTrackingData(EVENT.modalOpen, divisionNumber, productNumber, productAttributes);
        const products = Array.isArray(tracking.analytics.data.products)
            ? tracking.analytics.data.products
            : [];
        products.forEach(product => {
            if (!inStock) {
                product.exceptionView = '1';
                if (alternative) {
                    product.exceptionNumberOfSubs = 1;
                }
                else {
                    product.exceptionNumberOfSubs = 0;
                }
            }
            product.primaryProduct = 1;
        });
        tracking.analytics.data.products = products;
        tracking.analytics.data = {
            ...tracking.analytics.data,
            modal: {
                name: 'product quick view',
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackUserOpenQuickViewModal({ tracking }));
    }
    trackUserCloseQuickViewModel(divisionNumber, productNumber, productAttributes) {
        const tracking = this.generateGenericTrackingData(EVENT.modalClosed, divisionNumber, productNumber, productAttributes);
        tracking.analytics.data = {
            ...tracking.analytics.data,
            modal: {
                name: 'product quick view',
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackUserCloseQuickViewModel({ tracking }));
    }
    trackBetterBuysAccept(acceptSwapTrackingInfo) {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.acceptSwap,
                    acceptSwapTrackingInfo,
                },
                transformFunc: betterBuysAccept,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackBetterBuysAccept({ tracking }));
    }
    trackBetterBuysReject(rejectSwapTrackingInfo) {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.rejectSwap,
                    rejectSwapTrackingInfo,
                },
                transformFunc: betterBuysReject,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackBetterBuysReject({ tracking }));
    }
    trackProductCompare(productData) {
        const tracking = {
            analytics: {
                data: {
                    capability: TEAM_CAPABILITY,
                    event: EVENT.productCompare,
                    productData,
                },
                transformFunc: trackProductCompareTransformer,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackProductCompare({ tracking }));
    }
    trackEquipmentAndSupplies(trackEquipmentAndSupplies) {
        const tracking = {
            analytics: {
                data: {
                    capability: TEAM_CAPABILITY,
                    event: EVENT.buttonClick,
                    button: {
                        buttonName: trackEquipmentAndSupplies.buttonName,
                    },
                    activePromo: {
                        promoId: trackEquipmentAndSupplies.promoId,
                    },
                },
                transformFunc: equipmentAndSuppliesTransform,
            },
        };
        if (trackEquipmentAndSupplies.buttonName.includes('header')) {
            this.store.dispatch(ANALYTICS_ACTIONS.trackEquipmentAndSuppliesHeader({ tracking }));
        }
        else {
            this.store.dispatch(ANALYTICS_ACTIONS.trackEquipmentAndSuppliesHomeTile({ tracking }));
        }
    }
    trackClickedSearchFilter(clickedFilter) {
        const tracking = {
            analytics: {
                data: {
                    capability: TEAM_CAPABILITY,
                    event: EVENT.filterClick,
                    filterName: clickedFilter,
                },
                transformFunc: (trackingData) => trackingData.data,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackClickedSearchFilter({ tracking }));
    }
    trackScoopPageLoad() {
        const tracking = {
            analytics: {
                data: {
                    capability: TEAM_CAPABILITY,
                    event: EVENT.pageLoaded,
                    pageName: `${PAGE_NAME_BASE_PATH.r4}${SCOOP.pageName}`,
                },
                transformFunc: scoopPageLoad,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackScoopPageLoad({ tracking }));
    }
    trackSellerIncentivesPageLoad() {
        const tracking = {
            analytics: {
                data: {
                    capability: TEAM_CAPABILITY,
                    event: EVENT.pageLoaded,
                    page: {
                        pageName: `${PAGE_NAME_BASE_PATH.r4}${SELLER_INCENTIVES.pageName}`,
                    },
                },
                transformFunc: (trackingData) => trackingData.data,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackSellerIncentivesPageLoad({ tracking }));
    }
    trackShareViaEmailPDPOpenModal() {
        const tracking = {
            analytics: {
                data: {
                    capability: ALERTS_CAPABILITY,
                    event: EVENT.modalOpen,
                    modalName: 'share pdp',
                },
                transformFunc: shareViaEmailPDPOpenModal,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackShareViaEmailPDPOpenModal({ tracking }));
    }
    trackShareViaEmailPDP() {
        const tracking = {
            analytics: {
                data: {
                    capability: ALERTS_CAPABILITY,
                    event: EVENT.sharePrintDownload,
                    fileType: SHARE_TYPE.fileType,
                },
                transformFunc: shareViaEmailPDP,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackShareViaEmailPDP({ tracking }));
    }
    trackMerchCarouselLoaded(productsArray, engagementType) {
        const data = {
            capability: TEAM_CAPABILITY,
            event: EVENT.featureLoaded,
            products: productsArray,
        };
        if (engagementType) {
            data.engagement = { engagementType };
        }
        const tracking = {
            analytics: {
                data,
                transformFunc: (trackingData) => trackingData.data,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackMerchCarouselDisplayed({ tracking }));
    }
    trackCreateNonUsfProductSuccess(divisionApn, nonUsfProductName, nonUsfProductPrice) {
        const tracking = {
            analytics: {
                data: {
                    capability: LISTS_CAPABILITY,
                    event: EVENT.addNonUsfProduct,
                    divisionApn,
                    nonUsfProductName,
                    nonUsfProductPrice,
                },
                transformFunc: createNonUsfProductTransform,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackCreateNonUsfProductSuccess({ tracking }));
    }
    trackManageNonUsfProductPageLoaded(nonUsfProducts) {
        const tracking = {
            analytics: {
                data: {
                    capability: TEAM_CAPABILITY,
                    event: EVENT.pageLoaded,
                    pageName: `${PAGE_NAME_BASE_PATH.r4}${NON_USF_PRODUCTS.manageNonUsf}`,
                    products: nonUsfProducts,
                },
                transformFunc: pageLoadNonUsfProductsTransform,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackManageNonUsfProductPageLoaded({ tracking }));
    }
    trackYMANScroll(products) {
        const tracking = {
            analytics: {
                data: {
                    capability: TEAM_CAPABILITY,
                    event: EVENT.pdpEngagement,
                    engagement: { engagementType: 'you may also need scroll' },
                    products,
                    page: {
                        pageName: `${PAGE_NAME_BASE_PATH.r4}${PAGE_DETAIL.productDetails}`,
                    },
                },
                transformFunc: pdpYMANCABSCroll,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackYMANScroll({ tracking }));
    }
    trackCABScroll(products) {
        const tracking = {
            analytics: {
                data: {
                    capability: TEAM_CAPABILITY,
                    event: EVENT.pdpEngagement,
                    engagement: { engagementType: 'customers also bought scroll' },
                    products,
                    page: {
                        pageName: `${PAGE_NAME_BASE_PATH.r4}${PAGE_DETAIL.productDetails}`,
                    },
                },
                transformFunc: pdpYMANCABSCroll,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackCABScroll({ tracking }));
    }
    trackScoopPreviewBannerClick() {
        const tracking = {
            analytics: {
                data: {
                    capability: TEAM_CAPABILITY,
                    event: EVENT.promoBannerClick,
                    activePromo: SCOOP_PREVIEW,
                },
                transformFunc: (trackingData) => trackingData.data,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackScoopPreviewBannerClick({ tracking }));
    }
    trackSellerQuickViewPageLoad(divisionNumber, productNumber, productAttributes) {
        const tracking = this.generateGenericTrackingData(EVENT.pageLoaded, divisionNumber, productNumber, productAttributes);
        tracking.analytics.data = {
            ...tracking.analytics.data,
            page: {
                pageName: `${PAGE_NAME_BASE_PATH.r4}${PAGE_DETAIL.sellerQuickView}`,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackSellerQuickViewPageLoad({ tracking }));
    }
    trackRebatesPageLoad(takeOutOption) {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.pageLoaded,
                    capability: TEAM_CAPABILITY,
                    pageName: `${PAGE_NAME_BASE_PATH.r4}${PAGE_DETAIL.customerIncentive} ${takeOutOption}`,
                },
                transformFunc: pageLoadedTransform,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackRebatesPageLoad({ tracking }));
    }
    trackBecomeACustomerButtonClick(variantValue) {
        const tracking = {
            analytics: {
                data: {
                    event: EVENT.buttonClick,
                    capability: TEAM_CAPABILITY,
                    buttonName: `${GUEST_LANDING_PAGE.becomeACustomer} ${variantValue}`,
                },
                transformFunc: buttonClickTransform,
            },
        };
        this.store.dispatch(ANALYTICS_ACTIONS.trackBecomeACustomerButtonClick({ tracking }));
    }
    trackDirectInfoClick(divisionNumber, productNumber, productAttributes, resultRank) {
        this.trackProductClickEvent(EVENT.directInfoClick, divisionNumber, productNumber, productAttributes, resultRank, ANALYTICS_ACTIONS.trackDirectInfoClick);
    }
    trackProductImageClick(divisionNumber, productNumber, productAttributes, resultRank) {
        this.trackProductClickEvent(EVENT.productImageClick, divisionNumber, productNumber, productAttributes, resultRank, ANALYTICS_ACTIONS.trackProductImageClick);
    }
    trackProductClickEvent(event, divisionNumber, productNumber, productAttributes, resultRank, action) {
        const tracking = this.generateGenericTrackingData(event, divisionNumber, productNumber, productAttributes);
        if (!!resultRank) {
            tracking.analytics.data = {
                ...tracking.analytics.data,
                results: {
                    resultRank,
                },
            };
        }
        tracking.analytics.transformFunc = productClickTransform;
        this.store.dispatch(action({ tracking }));
    }
    generateGenericTrackingData(event, divisionNumberOrProductArray, productNumber, productAttributes) {
        const products = Array.isArray(divisionNumberOrProductArray)
            ? divisionNumberOrProductArray
            : [
                {
                    divisionApn: `${divisionNumberOrProductArray}-${productNumber}`,
                    attributes: productAttributes,
                },
            ];
        return {
            analytics: {
                data: {
                    event,
                    capability: TEAM_CAPABILITY,
                    products,
                },
                transformFunc: (trackingData) => trackingData.data,
            },
        };
    }
}
ProductAnalyticsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductAnalyticsService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ProductAnalyticsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductAnalyticsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductAnalyticsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

/* eslint-disable prefer-arrow/prefer-arrow-functions */
const browseHierarchyAdapter = createEntityAdapter({
    selectId: browseHierarchy => browseHierarchy.classCode,
});
const notReadyBrowseHierarchyState = browseHierarchyAdapter.getInitialState({
    loadingState: LoadingState.notReady,
});
const initialBrowseHierarchyState = browseHierarchyAdapter.getInitialState({
    loadingState: LoadingState.init,
    updatedTS: '',
});
// eslint-disable-next-line no-underscore-dangle
const _browseHierarchyReducer = createReducer(notReadyBrowseHierarchyState, 
//
on(BrowseHierarchyActions.ResetStore, () => initialBrowseHierarchyState), 
//
on(BrowseHierarchyActions.FetchBrowseHierarchy, (state, action) => ({
    ...state,
    loadingState: LoadingState.loading,
})), on(BrowseHierarchyActions.FetchBrowseHierarchySuccess, (state, { result }) => browseHierarchyAdapter.upsertMany(result.map(productClass => ({ ...productClass })), {
    ...state,
    loadingState: LoadingState.loaded,
    updatedTS: new Date().toISOString(),
})), on(BrowseHierarchyActions.FetchBrowseHierarchyFail, state => ({
    ...state,
    loadingState: LoadingState.error,
})));
function browseHierarchyReducer(state, action) {
    return _browseHierarchyReducer(state, action);
}

const dateIsOlderThan = (lapse, type, date) => dayjs(new Date()).diff(date, type) > lapse;

const { selectAll: selectAll$3 } = browseHierarchyAdapter.getSelectors();
const selectBrowseHierarchyState = createFeatureSelector(BROWSE_HIERARCHY_KEY);
const selectAllBrowseHierarchyCategories = createSelector(selectBrowseHierarchyState, selectAll$3);
const selectShouldFetchBrowseHierarchy = () => createSelector(selectBrowseHierarchyState, state => {
    if (state?.loadingState !== LoadingState.loaded &&
        state?.loadingState !== LoadingState.loading) {
        return true;
    }
    if (state?.loadingState !== LoadingState.loading &&
        dateIsOlderThan(24, 'hour', dayjs(state.updatedTS))) {
        return true;
    }
});
const selectBrowseCategoriesVM = createSelector(selectBrowseHierarchyState, state => {
    const categories = Object.values(state.entities).map(cat => ({ ...cat }));
    return { state: state?.loadingState, categories };
});

class BrowseHierarchyFacadeService {
    constructor(store) {
        this.store = store;
    }
    loadBrowseCategories() {
        this.store.dispatch(BrowseHierarchyActions.LoadBrowseHierarchy({
            tracking: traceHelperUtil.startTrace(TRACE_CONTEXT.browseHierarchy),
        }));
    }
    getBrowseHierarchyViewModel() {
        return this.store.select(selectBrowseCategoriesVM);
    }
}
BrowseHierarchyFacadeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowseHierarchyFacadeService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
BrowseHierarchyFacadeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowseHierarchyFacadeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowseHierarchyFacadeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const selectPersonalizationState = createFeatureSelector(PERSONALIZATION_KEY);
const isPersonalizationStateReady = createSelector(selectPersonalizationState, state => state.loadingState === LoadingState.init);
const selectPersonalizationResponseByType = (personalizationType) => createSelector(selectPersonalizationState, (personalizationState) => personalizationState?.entities[personalizationType]);

class PersonalizationService {
    constructor(store) {
        this.store = store;
    }
    getPersonalization(personalizationType) {
        this.store.dispatch(PersonalizationActions.GetPersonalizationEvent({ personalizationType }));
        return this.store.select(selectPersonalizationResponseByType(personalizationType));
    }
}
PersonalizationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PersonalizationService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
PersonalizationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PersonalizationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PersonalizationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

/* eslint-disable prefer-arrow/prefer-arrow-functions */
const myProductAdapter = createEntityAdapter({
    selectId: myProductState => myProductState.productNumber,
});
const initialMyProductState = myProductAdapter.getInitialState({
    loadingState: LoadingState.init,
});
const notReadyMyProductState = myProductAdapter.getInitialState({
    loadingState: LoadingState.notReady,
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _myProductReducer = createReducer(initialMyProductState, 
// Reset store
on(MyProductActions.ResetStore, () => notReadyMyProductState), 
// Register new elements
on(MyProductActions.UpdateMyProducts, (state, { productNumbers }) => {
    const updateChanges = productNumbers.map(productNumber => ({
        productNumber,
    }));
    return myProductAdapter.upsertMany(updateChanges, state);
}), 
// Load from storage
on(MyProductActions.LoadFromStorageSuccess, (state, { changes }) => {
    const myProductStateEntities = {};
    for (const key in changes.entities) {
        if (changes.entities.hasOwnProperty(key)) {
            myProductStateEntities[key] = { ...changes.entities[key] };
        }
    }
    if (Object.keys(myProductStateEntities).length > 0) {
        const newState = { ...changes, entities: myProductStateEntities };
        return {
            ...state,
            ...newState,
        };
    }
    else {
        return initialMyProductState;
    }
}));
function myProductReducer(state, action) {
    return _myProductReducer(state, action);
}

/* eslint-disable prefer-arrow/prefer-arrow-functions */
const productChangesAdapter = createEntityAdapter({
    selectId: productChange => productChange.type,
});
const notReadyProductChangeState = productChangesAdapter.getInitialState({
    loadingState: LoadingState.notReady,
});
const initialProductChangeState = productChangesAdapter.getInitialState({
    loadingState: LoadingState.init,
});
// eslint-disable-next-line no-underscore-dangle
const _productChangesReducer = createReducer(notReadyProductChangeState, 
// Reset store
on(ProductChangesActions.ResetStore, () => initialProductChangeState), on(ProductChangesActions.UpdateWatermark, (state, { changeType, watermark }) => {
    const changeState = {
        type: changeType,
        watermark,
    };
    return productChangesAdapter.upsertOne(changeState, state);
}), on(ProductChangesActions.LoadFromStorageSuccess, (state, { changes }) => {
    const productChangeEntities = {};
    for (const key in changes.entities) {
        if (changes.entities.hasOwnProperty(key)) {
            productChangeEntities[key] = { ...changes.entities[key] };
        }
    }
    if (Object.keys(productChangeEntities).length > 0) {
        const newState = { ...changes, entities: productChangeEntities };
        return {
            ...state,
            ...newState,
        };
    }
    else {
        return initialProductChangeState;
    }
}));
function productChangesReducer(state, action) {
    return _productChangesReducer(state, action);
}

/* eslint-disable prefer-arrow/prefer-arrow-functions */
const PRODUCT_KEY = 'products';
const productAdapter = createEntityAdapter({
    selectId: product => product.productNumber,
});
const initialProductState$1 = productAdapter.getInitialState({
    // additional entity state properties
    loaded: false,
});
// eslint-disable-next-line no-underscore-dangle
const _productReducer$1 = createReducer(initialProductState$1, on(ProductActions.LoadProductsSuccess, (state, action) => {
    const productsState = action.products.map(product => ({
        ...product,
        loaded: true,
        loading: false,
    }));
    return productAdapter.upsertMany(productsState, {
        ...state,
        loaded: true,
    });
}), on(ProductActions.ProductCreated, (state, action) => {
    const productState = action.product;
    return productAdapter.addOne(productState, state);
}), on(ProductActions.ProductUpdated, (state, action) => {
    const productState = action.product;
    return productAdapter.upsertOne(productState, state);
}), on(ProductActions.ProductsUpdated, (state, action) => {
    const productState = action.products;
    return productAdapter.upsertMany(productState, state);
}), on(ProductActions.ProductDeleted, (state, action) => {
    const key = action.productNumber;
    return productAdapter.removeOne(key, state);
}), on(ProductActions.FetchProductOrderabilities, (state, { productNumbers }) => {
    const updateChanges = productNumbers.map(productNumber => ({
        loadingState: LoadingState.loading,
        productNumber,
    }));
    return productAdapter.upsertMany(updateChanges, state);
}), on(ProductActions.initializeStorageFail, (state, action) => ({
    ...state,
    loading: false,
    loaded: false,
    error: action.error,
})));
function productReducer(state, action) {
    return _productReducer$1(state, action);
}

/* eslint-disable prefer-arrow/prefer-arrow-functions */
const customerPillAdapter = createEntityAdapter({
    selectId: customerPillState => customerPillState?.productNumber,
});
const notReadyCustomerPillState = customerPillAdapter.getInitialState({ loadingState: LoadingState.notReady });
const initialCustomerPillState = customerPillAdapter.getInitialState({ loadingState: LoadingState.init });
// eslint-disable-next-line no-underscore-dangle
const _customerPillReducer = createReducer(notReadyCustomerPillState, on(CustomerPillActions.ResetStore, () => initialCustomerPillState), on(ProductActions.FetchProductOrderabilities, (state, { productNumbers }) => {
    const loadingCustomerPills = productNumbers.map(productNumber => ({
        productNumber,
        loadingState: LoadingState.loading,
    }));
    return customerPillAdapter.upsertMany(loadingCustomerPills, state);
}), on(CustomerPillActions.FetchCustomerPillsSuccess, (state, { requestedProductNumbers, customerPills, customization }) => {
    const loadedCustomerPillNumbers = new Set();
    const loadedCustomerPills = customerPills?.map(customerPill => {
        loadedCustomerPillNumbers.add(customerPill?.productNumber);
        return {
            ...customerPill,
            loadingState: LoadingState.loaded,
        };
    });
    const notFoundCustomerPills = [];
    requestedProductNumbers.forEach(productNumber => {
        if (!loadedCustomerPillNumbers.has(productNumber)) {
            notFoundCustomerPills.push({
                customization,
                productNumber,
                loadingState: LoadingState.notFound,
            });
        }
    });
    return customerPillAdapter.upsertMany([...notFoundCustomerPills, ...loadedCustomerPills], state);
}), on(ProductActions.LoadSlimProductsSuccess, (state, { requestedProductNumbers, products }) => {
    const customerPillsToInsert = [];
    products.forEach(product => {
        const currentCustomerPill = state.entities[product?.productNumber];
        if (currentCustomerPill?.loadingState !== LoadingState.loaded &&
            currentCustomerPill?.loadingState !== LoadingState.notFound) {
            customerPillsToInsert.push({
                productNumber: product?.productNumber,
                loadingState: product?.notFound
                    ? LoadingState.notFound
                    : LoadingState.slim,
            });
        }
    });
    return customerPillAdapter.upsertMany(customerPillsToInsert, state);
}), on(CustomerPillActions.FetchCustomerPillsFail, (state, { productNumbers, customization }) => {
    const errorCustomerPills = productNumbers.map(productNumber => ({
        customization,
        productNumber,
        loadingState: LoadingState.error,
    }));
    return customerPillAdapter.upsertMany(errorCustomerPills, state);
}));
function customerPillReducer(state, action) {
    return _customerPillReducer(state, action);
}

/* eslint-disable prefer-arrow/prefer-arrow-functions */
const PRODUCT_SUMMARY_DETAIL_KEY = 'productSummaryDetail';
const productSummaryDetailAdapter = createEntityAdapter({
    selectId: product => product.productNumber,
});
const initialProductState = productSummaryDetailAdapter.getInitialState({
    // additional entity state properties
    loaded: false,
});
// eslint-disable-next-line no-underscore-dangle
const _productReducer = createReducer(initialProductState, on(ProductActions.LoadProductDetailSuccess, (state, action) => {
    const productState = action.product;
    state = productSummaryDetailAdapter.removeAll(state);
    return productSummaryDetailAdapter.upsertOne(productState, state);
}), on(ProductActions.LoadProductDetailFailure, (state, action) => {
    const productNumber = action.productNumber;
    state = productSummaryDetailAdapter.removeAll(state);
    return {
        ...state,
        ids: [productNumber],
        entities: { [productNumber]: null },
        loaded: false,
    };
}));
function productSummaryDetailReducer(state, action) {
    return _productReducer(state, action);
}

/* eslint-disable prefer-arrow/prefer-arrow-functions */
const searchResponseAdapterV2 = createEntityAdapter({
    selectId: searchResponseV2 => searchResponseV2.correlationId,
});
const notReadySearchResponseStateV2 = searchResponseAdapterV2.getInitialState({ loadingState: searchLoadingState.notReady });
const initialSearchResponseStateV2 = searchResponseAdapterV2.getInitialState({ loadingState: searchLoadingState.init });
// eslint-disable-next-line no-underscore-dangle
const _searchResponseReducerV2 = createReducer(notReadySearchResponseStateV2, on(SearchActionsV2.ResetStore, () => initialSearchResponseStateV2), on(SearchActionsV2.FetchSearchResponse, (state, { request, correlationId }) => {
    const loadingState = state.entities.hasOwnProperty(correlationId) &&
        request.startIndex > state.entities[correlationId].startIndex
        ? searchLoadingState.loadingNextPage
        : searchLoadingState.loading;
    const updateChange = {
        correlationId,
        ...request,
        currentOffset: request.startIndex,
        loadingState,
    };
    return searchResponseAdapterV2.upsertOne(updateChange, state);
}), on(SearchActionsV2.FetchSearchResponseSuccess, (state, { response, correlationId }) => {
    let productNumbers = [];
    if (state.entities.hasOwnProperty(correlationId) &&
        state.entities[correlationId].loadingState ===
            searchLoadingState.loadingNextPage) {
        productNumbers = state.entities[correlationId].productNumbers;
    }
    const updateChange = {
        correlationId,
        ...response,
        currentOffset: response.startIndex,
        productNumbers: [...productNumbers, ...response.productNumbers],
        loadingState: searchLoadingState.loaded,
    };
    return searchResponseAdapterV2.upsertOne(updateChange, state);
}), on(SearchActionsV2.FetchSearchResponseFail, (state, { response, correlationId }) => {
    const updateChange = {
        correlationId,
        totalCount: response.totalCount,
        loadingState: searchLoadingState.error,
    };
    return searchResponseAdapterV2.upsertOne(updateChange, state);
}));
function searchResponseReducerV2(state, action) {
    return _searchResponseReducerV2(state, action);
}

/* eslint-disable prefer-arrow/prefer-arrow-functions */
const productScoopAdapter = createEntityAdapter({
    selectId: productScoop => productScoop.productNumber,
});
const initialProductScoopState = productScoopAdapter.getInitialState({ loadingState: LoadingState.init });
// eslint-disable-next-line no-underscore-dangle
const _productScoopReducer = createReducer(initialProductScoopState, 
//Set this elements to be on a 'loading' state
on(ProductScoopActions.loadProductScoops, (state, action) => ({
    ...state,
    loadingState: LoadingState.loading,
})), on(ProductScoopActions.loadProductScoopsSuccess, (state, action) => {
    const productScoopState = action.productScoops.map(productScoop => ({
        ...productScoop,
        loadingState: LoadingState.loaded,
    }));
    if (productScoopState.length > 0) {
        return productScoopAdapter.upsertMany(productScoopState, {
            ...state,
            loadingState: LoadingState.loaded,
            watermark: new Date().toISOString(),
        });
    }
    else {
        return {
            ...state,
            loadingState: LoadingState.loaded,
            watermark: new Date().toISOString(),
        };
    }
}), on(ProductScoopActions.loadFromStorageSuccess, (state, action) => ({
    ...state,
    ...action.productScoops,
})), on(ProductScoopActions.loadProductScoopsFail, (state, action) => ({
    ...state,
    loadingState: LoadingState.error,
    error: action.error,
})), on(ProductScoopActions.loadFromStorageSuccess, (state, productContractState) => ({
    ...state,
    productContractState,
})));
function productScoopReducer(state, action) {
    return _productScoopReducer(state, action);
}

const mslProductAdapter = createEntityAdapter({
    selectId: mslProduct => mslProduct?.productNumber,
});
const initialMslProductState = mslProductAdapter.getInitialState({
    loaded: false,
    loading: true,
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _mslProductReducer = createReducer(initialMslProductState, on(MslProductActions.insertMslProducts, (state, action) => mslProductAdapter.upsertMany(action.mslProducts, {
    ...state,
    loaded: true,
    loading: false,
})), on(MslProductActions.ResetStore, (state, action) => initialMslProductState));
const mslProductReducer = (state, action) => _mslProductReducer(state, action);

const merchandisingAdapter = createEntityAdapter({
    selectId: merchandisingState => merchandisingState.experience,
});
const notReadyMerchandisingState = merchandisingAdapter.getInitialState({ loadingState: LoadingState.notReady });
const initialMerchandisingState = merchandisingAdapter.getInitialState({ loadingState: LoadingState.init });
// eslint-disable-next-line no-underscore-dangle
const _merchandisingReducer = createReducer(notReadyMerchandisingState, on(MerchandisingActions.ResetStore, () => initialMerchandisingState), on(MerchandisingActions.FetchMerchandisingResult, (state, { customer, request }) => {
    const updateChange = request?.experiences?.map(experience => ({
        experience: experience?.name,
        products: [],
        loadingState: LoadingState.loading,
        customerNumber: customer?.customerNumber,
        departmentNumber: customer?.departmentNumber,
        divisionNumber: customer?.divisionNumber,
    }));
    return merchandisingAdapter.upsertMany(updateChange, state);
}), on(MerchandisingActions.FetchMerchandisingResultSuccess, (state, { request, response }) => {
    const experienceToResponseMap = new Map(response?.map(merchandisingResponse => [
        merchandisingResponse?.experience,
        merchandisingResponse,
    ]));
    const updateChange = request?.experiences?.map(experience => {
        if (experienceToResponseMap?.has(experience?.name)) {
            return {
                ...state?.entities[experience?.name],
                ...experienceToResponseMap.get(experience?.name),
                loadingState: LoadingState.loaded,
            };
        }
        else {
            return {
                ...state?.entities[experience?.name],
                experience: experience?.name,
                products: [],
                loadingState: LoadingState.notFound,
            };
        }
    });
    return merchandisingAdapter.upsertMany(updateChange, state);
}), on(MerchandisingActions.FetchMerchandisingResultFail, (state, { request }) => {
    const updateChange = request?.experiences?.map(experience => ({
        ...state?.entities[experience?.name],
        experience: experience?.name,
        products: [],
        loadingState: LoadingState.error,
    }));
    return merchandisingAdapter.upsertMany(updateChange, state);
}));
const merchandisingReducer = (state, action) => _merchandisingReducer(state, action);

const notReadyRevenueManagementState = {
    loadingState: LoadingState.notReady,
};
const initialRevenueManagementState = {
    loadingState: LoadingState.init,
};
const revenueManagementReducer = createReducer(notReadyRevenueManagementState, on(RevenueManagementActions.ResetStore, () => initialRevenueManagementState), on(RevenueManagementActions.FetchRevenueManagementResult, _ => ({
    loadingState: LoadingState.loading,
})), on(RevenueManagementActions.FetchRevenueManagementResultSuccess, (_, { response }) => ({
    revenueManagementArray: response,
    loadingState: LoadingState.loaded,
})), on(RevenueManagementActions.FetchRevenueManagementResultFail, _ => ({
    revenueManagementArray: [],
    loadingState: LoadingState.error,
})));

const cabAdaptor = createEntityAdapter({
    selectId: cabProduct => cabProduct.productNumber,
});
const notReadyCabResponseState = cabAdaptor.getInitialState({
    loadingState: LoadingState.notReady,
});
const initialCabState = cabAdaptor.getInitialState({
    loadingState: LoadingState.init,
});
// eslint-disable-next-line no-underscore-dangle
const _cabReducer = createReducer(notReadyCabResponseState, 
// Reset store
on(CustomersAlsoBoughtActions.ResetStore, () => initialCabState), on(CustomersAlsoBoughtActions.FetchCabProductNumbers, (state, action) => {
    const cabState = {
        loadingState: LoadingState.loading,
        productNumber: action.request.productNumber,
        products: [],
    };
    return cabAdaptor.upsertOne(cabState, state);
}), on(CustomersAlsoBoughtActions.FetchCabProductNumbersSuccess, (state, action) => {
    const cabState = {
        loadingState: LoadingState.loaded,
        productNumber: action.request.productNumber,
        products: action.response.products,
        variant: action.response.variant ?? '',
    };
    return cabAdaptor.upsertOne(cabState, state);
}), on(CustomersAlsoBoughtActions.FetchCabProductNumbersFail, (state, action) => {
    const cabState = {
        loadingState: LoadingState.error,
        productNumber: action.request.productNumber,
        products: [],
    };
    return cabAdaptor.upsertOne(cabState, state);
}));
// eslint-disable-next-line no-underscore-dangle
const customersAlsoBoughtReducer = (state, action) => _cabReducer(state, action);

const notReadySneakPeekState = {
    loadingState: LoadingState.notReady,
};
const initialSneakPeekState = {
    loadingState: LoadingState.init,
};
const sneakPeekReducer = createReducer(notReadySneakPeekState, on(SneakPeekActions.ResetStore, () => initialSneakPeekState), on(SneakPeekActions.FetchSneakPeekResult, _ => ({
    loadingState: LoadingState.loading,
})), on(SneakPeekActions.FetchSneakPeekResultSuccess, (_, { response }) => ({
    sneakPeeks: response,
    loadingState: LoadingState.loaded,
})), on(SneakPeekActions.FetchSneakPeekResultFail, _ => ({
    sneakPeeks: [],
    loadingState: LoadingState.error,
})));

const selectMyProductState = createFeatureSelector(MY_PRODUCTS_KEY);
const { selectAll: selectAll$2 } = myProductAdapter.getSelectors();
const selectAllMyProducts = createSelector(selectMyProductState, selectAll$2);
const selectMyProducts = createSelector(selectAllMyProducts, myProducts => {
    if (!!myProducts) {
        const productNumbers = myProducts.map(myProduct => myProduct.productNumber);
        return new Set(productNumbers);
    }
    return new Set();
});

const selectCustomerPillState = createFeatureSelector(CUSTOMER_PILL_KEY);
const selectCustomerPillEntities = createSelector(selectCustomerPillState, customerPillAdapter.getSelectors().selectEntities);

const { selectAll: selectAll$1, selectEntities: selectEntities$2 } = productAdapter.getSelectors();
const selectProductsState = createFeatureSelector(PRODUCTS_KEY);
const selectProductSummaryDetailState = createFeatureSelector(PRODUCT_SUMMARY_DETAIL_KEY$1);
const productsLoaded = () => createSelector(selectProductsState, state => state.loaded === true);
const selectProductSummaryDetail = state => productAdapter
    .getSelectors(selectProductSummaryDetailState)
    .selectAll(state)[0];
const productSummaryDetailLoaded = () => createSelector(selectProductSummaryDetailState, state => state?.loaded === true);
const filterMissingProducts = (productNumbers, forceReload) => createSelector(selectProductSummaryEntities, productSummaries => {
    if (forceReload) {
        return productNumbers;
    }
    const pendingProductSummaries = productNumbers.filter(productNumber => {
        const productSummary = productSummaries[productNumber];
        if (!productSummary) {
            // Element wasn't found in the state, so we need to fetch it
            return true;
        }
        // consider subs/replacements being loaded or not.
        if (productSummary.loadingState === LoadingState.loaded) {
            const pendingAlternative = Array.from(getProductAlternates([productSummary])).filter(altProductNumber => !productSummaries[altProductNumber]);
            return pendingAlternative.length > 0;
        }
        // Or element is in hasn't been fetched yet
        return productSummary.loadingState === LoadingState.init;
    });
    return pendingProductSummaries;
});
const filterMissingProductsRefactor = (productNumbers, forceReload) => createSelector(selectProductSummaryEntities, selectProductContractEntities, selectProductInventoryEntities, selectCustomerPillEntities, (productSummaries, productContracts, productInventories, customerPills) => {
    if (forceReload) {
        return productNumbers;
    }
    const pendingProducts = productNumbers.filter(productNumber => {
        const productSummary = productSummaries[productNumber];
        const productInventory = productInventories[productNumber];
        const productContract = productContracts[productNumber];
        const customerPill = customerPills[productNumber];
        if (!productSummary &&
            !productContract &&
            !productInventory &&
            !customerPill) {
            // Element wasn't found in the state, so we need to fetch it
            return true;
        }
        // consider subs/replacements being loaded or not.
        if (productSummary.loadingState === LoadingState.loaded) {
            const pendingAlternative = Array.from(getProductAlternates([productSummary])).filter(altProductNumber => !productSummaries[altProductNumber]);
            return pendingAlternative.length > 0;
        }
        // Or element is in hasn't been fetched yet
        return (productSummary.loadingState === LoadingState.init ||
            productSummary.loadingState === LoadingState.error ||
            productSummary.loadingState === LoadingState.slim ||
            productInventory.loadingState === LoadingState.init ||
            productInventory.loadingState === LoadingState.error ||
            productInventory.loadingState === LoadingState.slim ||
            productContract?.loadingState === LoadingState.init ||
            productContract?.loadingState === LoadingState.error ||
            productContract?.loadingState === LoadingState.slim ||
            customerPill?.loadingState === LoadingState.init ||
            customerPill?.loadingState === LoadingState.error ||
            customerPill?.loadingState === LoadingState.slim);
    });
    return pendingProducts;
});
const getLoadingStateOfProducts = (productNumbers) => createSelector(selectProductSummaryEntities, selectProductInventoryEntities, selectProductContractEntities, selectCustomerPillEntities, (productSummaries, productInventories, productContracts, customerPills) => {
    let loadingStateToReturn;
    let loadedCount = 0;
    let slimCount = 0;
    for (const productNumber of productNumbers) {
        const summary = productSummaries[productNumber];
        const inventory = productInventories[productNumber];
        const contract = productContracts[productNumber];
        const customerPill = customerPills[productNumber];
        if (isSlim(summary, inventory, contract, customerPill)) {
            slimCount += 1;
        }
        if (isError(summary, inventory, contract, customerPill)) {
            loadingStateToReturn = LoadingState.error;
            break;
        }
        if (isLoading(summary, inventory, contract, customerPill)) {
            loadingStateToReturn = LoadingState.loading;
            break;
        }
        if (isLoaded(summary, inventory, contract, customerPill)) {
            loadedCount += 1;
        }
    }
    if (loadedCount === productNumbers.length) {
        loadingStateToReturn = LoadingState.loaded;
    }
    else if (slimCount + loadedCount === productNumbers.length) {
        loadingStateToReturn = LoadingState.slim;
    }
    return loadingStateToReturn;
});
const isLoading = (summary, inventory, contract, customerPill) => !summary ||
    summary.loadingState === LoadingState.loading ||
    summary.loadingState === LoadingState.init ||
    !inventory ||
    inventory.loadingState === LoadingState.loading ||
    inventory.loadingState === LoadingState.init ||
    !contract ||
    contract.loadingState === LoadingState.loading ||
    contract.loadingState === LoadingState.init ||
    !customerPill ||
    customerPill.loadingState === LoadingState.loading ||
    customerPill.loadingState === LoadingState.init;
const isLoaded = (summary, inventory, contract, customerPill) => (summary?.loadingState === LoadingState.loaded ||
    summary?.loadingState === LoadingState.notFound) &&
    (inventory?.loadingState === LoadingState.loaded ||
        inventory?.loadingState === LoadingState.notFound) &&
    (contract?.loadingState === LoadingState.loaded ||
        contract?.loadingState === LoadingState.notFound) &&
    (customerPill?.loadingState === LoadingState.loaded ||
        customerPill?.loadingState === LoadingState.notFound);
const isError = (summary, inventory, contract, customerPill) => summary?.loadingState === LoadingState.error ||
    inventory?.loadingState === LoadingState.error ||
    contract?.loadingState === LoadingState.error ||
    customerPill?.loadingState === LoadingState.error;
const isSlim = (summary, inventory, contract, customerPill) => summary?.loadingState === LoadingState.slim ||
    inventory?.loadingState === LoadingState.slim ||
    contract?.loadingState === LoadingState.slim ||
    customerPill?.loadingState === LoadingState.slim;
const { selectIds: selectProductIds, selectEntities: selectProductEntities, selectAll: selectAllProducts, selectTotal: selectTotalProducts, } = productAdapter.getSelectors(selectProductsState);

const { selectEntities: selectEntities$1 } = productChangesAdapter.getSelectors();
const selectProductChangesState = createFeatureSelector(PRODUCT_CHANGES_KEY);
const selectProductChangeEntities = createSelector(selectProductChangesState, selectEntities$1);
const selectProductChangeWatermark = (changeType) => createSelector(selectProductChangeEntities, entities => {
    if (!!entities[changeType]) {
        return entities[changeType].watermark;
    }
    return null;
});

const selectProductScoopState = createFeatureSelector(PRODUCT_SCOOP_KEY);
const { selectAll, selectEntities } = productScoopAdapter.getSelectors();
const selectAllProductScoops = createSelector(selectProductScoopState, selectAll);
const selectProductScoopEntities = createSelector(selectProductScoopState, selectEntities);
const productScoopLoaded = () => createSelector(selectProductScoopState, (productScoops) => productScoops.loadingState === LoadingState.loaded);
const allProductScoops = () => createSelector(selectProductScoopState, (productScoops) => Object.values(productScoops));
const productScoopWatermark = () => createSelector(selectProductScoopState, (productScoopState) => productScoopState.watermark);

const selectMslProductState = createFeatureSelector(MSL_PRODUCT_KEY);
const selectMslProductStateLoaded = createSelector(selectMslProductState, (mslProductState) => mslProductState.loaded);
const selectMslProductStateEntities = createSelector(selectMslProductState, (mslProductState) => mslProductState.entities);

const selectMerchandisingState = createFeatureSelector(MERCHANDISING_KEY);
const isMerchandisingStateReady = createSelector(selectMerchandisingState, state => state.loadingState === LoadingState.init);
const selectMerchandisingResponseById = (experience) => createSelector(selectMerchandisingState, (merchandisingState) => merchandisingState?.entities[experience]);
const selectMerchandisingResponseByIds = (experiences) => createSelector(selectMerchandisingState, (merchandisingState) => experiences?.map(experience => merchandisingState?.entities[experience]));

const pimClassAdapter = createEntityAdapter({
    selectId: (pim) => pim.pimClass.code
});
const initialPIMClassState = pimClassAdapter.getInitialState({
    error: undefined,
    loaded: false,
    loading: false,
    watermark: new Date('January 1, 1970').toISOString()
});
// eslint-disable-next-line no-underscore-dangle
const _pimClasstReducer = createReducer(initialPIMClassState, on(PIMClassActions.load, (state, action) => ({
    ...state,
    loading: true,
    loaded: false,
})), on(PIMClassActions.loadSuccess, (state, action) => pimClassAdapter.upsertMany(action.pimHierarchy.map(pimClass => pimClass), {
    ...state,
    loading: false,
    loaded: true,
    watermark: new Date().toISOString()
})), on(PIMClassActions.loadFail, (state, action) => ({
    ...state,
    loading: false,
    loaded: true,
    error: action.error
})));
const pimClassReducer = (state, action) => _pimClasstReducer(state, action);

const selectPIMClassState = createFeatureSelector(PIM_CLASS_KEY);
const selectPIMClassById = (classId) => createSelector(selectPIMClassState, (pimClass) => pimClass.entities[classId]);
const selectPIMWatermark = () => (createSelector(selectPIMClassState, (pimClass) => pimClass.watermark));
const { selectIds: selectPIMClassIds, selectEntities: selectPIMClassEntities, selectAll: selectAllIPIMClasses, selectTotal: selectTotalPIMClasses, } = pimClassAdapter.getSelectors(selectPIMClassState);

const selectRebateTakeoutOptionsState = createFeatureSelector(REBATE_TAKEOUT_OPTIONS_KEY);
const selectRebateTakeoutOptions = createSelector(selectRebateTakeoutOptionsState, state => state.takeoutOptions);
const isRebateTakeoutOptionsStateReady = createSelector(selectRebateTakeoutOptionsState, state => state.loadingState !== LoadingState.loading);

const NGRX_PRODUCT_LIB_CONFIG = new InjectionToken('NGRX_PROUCT_LIB_CONFIG');

/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-shadow */
class ProductOpportunityHttpClientService {
    constructor(config, http, platformService) {
        this.config = config;
        this.http = http;
        this.platformService = platformService;
    }
    doPost(url, body, traceContext) {
        const requestOptions = {
            headers: new HttpHeaders({
                'consumer-id': 'ecomr4',
                'content-type': 'application/json',
                'trace-context': traceContext,
            }),
        };
        return defer(() => this.platformService.getPlatformAndDeviceInfo()).pipe(mergeMap(state => this.http
            .post(url, body, { ...requestOptions })
            .pipe(timeout(60000), catchError(this.handleError))));
    }
    createOpportunity(body, traceContext) {
        const url = `${this.config.apiUrl}/opportunity`;
        return this.doPost(url, body, traceContext).pipe(catchError(this.handleError));
    }
    handleError(error) {
        console.error('0xjKmW8Vlq - NgRx Product Opportunity Service call failed', error);
        return throwError(error);
    }
}
ProductOpportunityHttpClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductOpportunityHttpClientService, deps: [{ token: NGRX_PRODUCT_LIB_CONFIG }, { token: i1$2.HttpClient }, { token: i5.PlatformService }], target: i0.ɵɵFactoryTarget.Injectable });
ProductOpportunityHttpClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductOpportunityHttpClientService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductOpportunityHttpClientService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PRODUCT_LIB_CONFIG]
                }] }, { type: i1$2.HttpClient }, { type: i5.PlatformService }]; } });

class OpportunityEffects {
    constructor(actions$, httpService) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.createOpportunity$ = createEffect(() => this.actions$.pipe(ofType(OpportunityActions.CreateProductOpportunity), switchMap(action => this.httpService
            .createOpportunity(action.request, traceHelperUtil.populateTraceHeader(action))
            .pipe(map(response => OpportunityActions.CreateProductOpportunitySuccess({
            id: action.id,
            request: action.request,
            result: response,
            tracking: traceHelperUtil.endTrace(TRACE_CONTEXT.createOpportunity),
        })), catchError(e => of(OpportunityActions.CreateProductOpportunityFail({
            id: action.id,
            request: action.request,
            error: e,
            tracking: traceHelperUtil.endTrace(TRACE_CONTEXT.createOpportunity),
        })))))));
    }
}
OpportunityEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OpportunityEffects, deps: [{ token: i1$3.Actions }, { token: ProductOpportunityHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable });
OpportunityEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OpportunityEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: OpportunityEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ProductOpportunityHttpClientService }]; } });

/* eslint-disable @typescript-eslint/no-shadow */
class ProductHttpClientService {
    constructor(config, http) {
        this.config = config;
        this.http = http;
    }
    get(path, watermark) {
        const url = watermark
            ? `${this.config.apiUrl}/${path}?watermark=${watermark}`
            : `${this.config.apiUrl}/${path}`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }
    getWithParams(path, params) {
        const url = `${this.config.apiUrl}/${path}`;
        return this.http
            .get(url, { params })
            .pipe(catchError(this.handleError));
    }
    getAsync(path, watermark, batchSize) {
        let url = `${this.config.apiUrl}/${path}?batchSize=${batchSize}`;
        if (watermark) {
            url += `&watermark=${watermark}`;
        }
        return this.http.get(url).pipe(catchError(this.handleError));
    }
    post(path, body) {
        const url = `${this.config.apiUrl}/${path}`;
        return this.http.post(url, body).pipe(catchError(this.handleError));
    }
    postSingleObject(path, body) {
        const url = `${this.config.apiUrl}/${path}`;
        return this.http.post(url, body).pipe(catchError(this.handleError));
    }
    postWithWatermark(path, body, apiVersion, productContractVersion, watermark) {
        let requestOptions = {};
        if (!!productContractVersion) {
            requestOptions = {
                headers: new HttpHeaders({
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'ProductContract-Version': productContractVersion,
                }),
            };
        }
        const url = watermark
            ? `${this.config.apiBaseUrl}/v${apiVersion}/${path}?watermark=${watermark}`
            : `${this.config.apiBaseUrl}/v${apiVersion}/${path}`;
        return this.http
            .post(url, body, { ...requestOptions })
            .pipe(catchError(this.handleError));
    }
    getSummaryChanges(watermark) {
        const path = 'productchanges/summaryChanges';
        const url = watermark
            ? `${this.config.apiUrl}/${path}?watermark=${watermark}`
            : `${this.config.apiUrl}/${path}`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }
    getObject(path, productContractVersion, watermark) {
        let requestOptions = {};
        if (!!productContractVersion) {
            requestOptions = {
                headers: new HttpHeaders({
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'ProductContract-Version': productContractVersion,
                }),
            };
        }
        const url = watermark
            ? `${this.config.apiUrl}/${path}?watermark=${watermark}`
            : `${this.config.apiUrl}/${path}`;
        return this.http.get(url, { ...requestOptions });
    }
    postObject(path, body) {
        const url = `${this.config.apiUrl}/${path}`;
        return this.http.post(url, body).pipe(catchError(this.handleError));
    }
    handleError(error) {
        console.error('1mR7TgKzLJ - NgRx Product HTTP Service call failed', error);
        return throwError(error);
    }
}
ProductHttpClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductHttpClientService, deps: [{ token: NGRX_PRODUCT_LIB_CONFIG }, { token: i1$2.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
ProductHttpClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductHttpClientService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductHttpClientService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PRODUCT_LIB_CONFIG]
                }] }, { type: i1$2.HttpClient }]; } });

const NG_RX_PRODUCT_EVENTS = {
    namespace: 'Product',
    eventType: [
        'ProductEvent',
        ProductInventoryEventTypes.ProductInventoryEvent,
        ProductScoopEventTypes.ProductScoopEvent,
        ProductContractEventTypes.ProductContractEvent,
        /*
        TODO this feature will be re-enabled in the future #vsm1FjYN3cksLW1qI1g7 once we have test availability
        MyProductChangesEventTypes.MyProductChangesEvent,
         */
    ],
};

class ProductClientWebSocketService {
    constructor(webSocketService, store) {
        this.webSocketService = webSocketService;
        this.store = store;
        this.subscriptions = [];
        /**
         * @remarks
         * This method maps the incoming websocket event action defined in @usf/list-types
         * to the appropriate NgRx action.  New events can be added to the map without impacting performance
         *
         * @param eventAction - The action attached to the websocket event
         * @param eventData - The data attached to the websocket event
         * @returns The NgRx action that will interact with the store
         */
        this.handleEvent = (eventAction, eventData) => ({
            ['ProductsUpdated']: ProductActions.ProductsUpdated({
                products: eventData.data.messageContent,
            }),
            [ProductSummaryEventActions.ProductSummaryDeleted]: ProductActions.ProductDeleted({ productNumber: eventData.data.messageContent[0]?.listId }),
            [ProductInventoryEventActions.ProductInventoryUpdated]: ProductInventoryActions.FetchProductInventoriesAsync({
                productNumbers: [
                    Number(eventData.data.messageContent[0]?.productNumber),
                ],
            }),
            /**
             * ProductScoops do not receive any params, logic is set on product-domain-api
             */
            [ProductScoopEventActions.ProductScoopUpdated]: ProductScoopActions.loadProductScoopsAsync(),
            [ProductContractEventActions.ProductContractUpdated]: ProductContractActions.FetchProductContractsAsync(),
            /**
             * My Product Changes
             * List item added
             */
            [MyProductChangesEventActions.ListItemAdded]: ProductChangesActions.ListItemsAddedReceived({
                watermark: eventData.data.watermark,
                content: eventData.data.messageContent,
            }),
            // Product summary CDC change events
            // TODO Disabled because of excessive/traffic load
            // [ProductSummaryEventActions.ProductSummaryCreated]: ProductSummaryActions.LoadProductSummariesChanges(
            //   {
            //     watermark: eventData.data.watermark,
            //     content: eventData.data.messageContent,
            //   },
            // ),
            // [ProductSummaryEventActions.ProductSummaryUpdated]: ProductSummaryActions.LoadProductSummariesChanges(
            //   {
            //     watermark: eventData.data.watermark,
            //     content: eventData.data.messageContent,
            //   },
            // ),
        }[eventAction]);
    }
    initialize() {
        // if there is no websocket service injected, return out
        // eslint-disable-next-line @typescript-eslint/dot-notation
        if (!this.webSocketService['baseUrl']) {
            // eslint-disable-next-line no-console
            console.info('%c** no websocket injected for product domain **', 'color: black; font-weight:bold;');
            return;
        }
        // subscribe to the websocket events scoped to the product domain
        // panamax websocket service lib will handle keeping the websocket connection
        // alive and will update the isConnected$ observable accordingly
        this.subscriptions.push(this.webSocketService?.isConnected$.subscribe(connected => {
            if (connected) {
                // Register websocket namespace and event types to subscribe to
                this.webSocketService.register([NG_RX_PRODUCT_EVENTS]);
                this.subscriptions.push(this.webSocketService?.getTopicData().subscribe((event) => {
                    let eventData;
                    try {
                        if (this.isObject(JSON.parse(event?.data))) {
                            eventData = JSON.parse(event?.data);
                            // dispatch appropriate NgRx action based on incoming websocket event action
                            if (eventData?.data?.namespace === 'Product' &&
                                eventData?.data?.eventAction) {
                                if (eventData?.data?.messageContent) {
                                    this.store.dispatch(this.handleEvent(eventData?.data?.eventAction, eventData));
                                }
                            }
                        }
                        else {
                            eventData = null;
                        }
                    }
                    catch (e) { }
                }));
            }
            else {
                // eslint-disable-next-line no-console
                console.info('%c** product domain web socket disconnected. **', 'color: red; font-weight:bold;');
            }
        }));
    }
    isObject(obj) {
        return obj === Object(obj);
    }
}
ProductClientWebSocketService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductClientWebSocketService, deps: [{ token: i3.WebSocketService }, { token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ProductClientWebSocketService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductClientWebSocketService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductClientWebSocketService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i3.WebSocketService }, { type: i1.Store }]; } });

class ProductEffects {
    constructor(actions$, httpService, productStateService, panAppState, store) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.productStateService = productStateService;
        this.panAppState = panAppState;
        this.store = store;
        this.globalContractsVersion2FlagName = 'split_global_contracts_v2';
        this.initialzeStorage$ = createEffect(() => this.actions$.pipe(ofType(ProductActions.initializeStorage), withLatestFrom(this.panAppState.user$, this.panAppState.customer$), filter(([_, user, customer]) => !!user && !!customer), concatMap(([_, user, customer]) => this.productStateService.initializeStorage(user, customer).pipe(mergeMap(product => [
        // ProductContractActions.loadFromStorage(),
        // ProductInventoryActions.loadFromStorage(),
        // ProductScoopActions.loadFromStorage(),
        ]), catchError((error) => of(ProductActions.initializeStorageFail({ error })))))));
        this.loadProductsOnList$ = createEffect(() => this.actions$.pipe(ofType(ProductActions.LoadProductsOnList), concatMap(action => this.httpService
            .post('productsummary', action.productIds)
            .pipe(map(products => ProductActions.LoadProductsSuccess({ products }))))));
        this.loadProductDetail$ = createEffect(() => this.actions$.pipe(ofType(ProductActions.LoadProductDetail), concatMap(action => this.httpService
            .post('productsummary', [action.productNumber])
            .pipe(map(products => {
            if (products != null && products[0] != null) {
                return ProductActions.LoadProductDetailSuccess({
                    product: products[0],
                });
            }
            return ProductActions.LoadProductDetailFailure({
                productNumber: action.productNumber,
            });
        }, catchError(() => of(ProductActions.LoadProductDetailFailure({
            productNumber: action.productNumber,
        }))))))));
        this.loadSlimProducts$ = createEffect(() => this.actions$.pipe(ofType(ProductActions.LoadSlimProducts), mergeMap(action => combineLatest([
            of(action),
            this.store.select(filterMissingProductsRefactor(action.productNumbers, false)),
            this.panAppState.session$,
        ]).pipe(take(1))), filter(([action, missingProductNumbers, previousSession]) => missingProductNumbers.length > 0), mergeMap(([action, missingProductNumbers, previousSession]) => this.httpService
            .postWithWatermark('products/slim', {
            productNumbers: missingProductNumbers,
        }, 1)
            .pipe(withLatestFrom(this.panAppState.session$), filter(([productResponse, session]) => {
            const sessionDidNotChange = previousSession?.selectedCustomerId ===
                session?.selectedCustomerId &&
                previousSession?.division === session?.division &&
                previousSession?.department === session?.department &&
                previousSession?.ecomUserId === session?.ecomUserId;
            return sessionDidNotChange;
        }), switchMap(([productResponse, session]) => [
            ProductActions.LoadSlimProductsSuccess({
                requestedProductNumbers: missingProductNumbers,
                products: productResponse.items,
            }),
        ]), retryBackoff({
            initialInterval: 1000,
            maxRetries: 3,
            maxInterval: 10000,
            shouldRetry: () => action.shouldRetry,
        }), catchError(() => of(ProductActions.LoadSlimProductsFail({
            productNumbers: missingProductNumbers,
        })))))));
        this.loadProductOrderabilities$ = createEffect(() => this.actions$.pipe(ofType(ProductActions.LoadProductOrderabilities), concatLatestFrom(action => this.store.select(filterMissingProductsRefactor(action.productNumbers, action.forceReload))), filter(([action, missingProductNumbers]) => missingProductNumbers.length > 0), map(([action, missingProductNumbers]) => ProductActions.FetchProductOrderabilities({
            productNumbers: missingProductNumbers,
            shouldRetry: action.shouldRetry,
        }))));
        this.fetchProductOrderabilities$ = createEffect(() => this.actions$.pipe(ofType(ProductActions.FetchProductOrderabilities), mergeMap(action => combineLatest([
            of(action),
            this.panAppState.getCustomization$().pipe(take(1)),
        ])), map(([action, customization]) => ({ ...action, customization })), withLatestFrom(this.panAppState.session$, this.panAppState.feature$([this.globalContractsVersion2FlagName])), mergeMap(([action, previousSession, globalContractsVersion2FlagEnabled]) => this.httpService
            .postWithWatermark('products', {
            productNumbers: action.productNumbers,
            customization: action.customization,
        }, 2, globalContractsVersion2FlagEnabled ? '2' : '1', action.watermark)
            .pipe(withLatestFrom(this.store.select(selectProductScoopEntities), this.panAppState.session$), filter(([productResponse, scoops, session]) => {
            const sessionDidNotChange = previousSession?.selectedCustomerId ===
                session?.selectedCustomerId &&
                previousSession?.division === session?.division &&
                previousSession?.department === session?.department &&
                previousSession?.ecomUserId === session?.ecomUserId;
            return sessionDidNotChange;
        }), switchMap(([productResponse, scoops, session]) => {
            const requestedProductNumbers = productResponse.items.map(product => product.productNumber);
            productResponse.items.forEach(product => {
                if (!!product.summary) {
                    product.summary = {
                        ...product.summary,
                        propertiesArray: Array.from(getPropertiesSet(product, scoops)),
                    };
                }
            });
            return [
                ProductActions.LoadProductOrderabilitiesSuccess({
                    requestedProductNumbers,
                    products: productResponse.items,
                    watermark: action.watermark,
                }),
                ProductSummaryActions.FetchProductSummariesSuccess({
                    requestedProductNumbers,
                    productSummaries: productResponse.items
                        .map(product => product.summary)
                        .filter(summary => !!summary),
                }),
                ProductInventoryActions.FetchProductInventoriesSuccess({
                    requestedProductNumbers,
                    productInventories: productResponse.items
                        .map(product => product.inventory)
                        .filter(inventory => !!inventory),
                }),
                ProductContractActions.FetchProductContractsSuccess({
                    requestedProductNumbers,
                    productContracts: productResponse.items
                        .map(product => product.contract)
                        .filter(contract => !!contract),
                }),
                CustomerPillActions.FetchCustomerPillsSuccess({
                    requestedProductNumbers,
                    customerPills: productResponse.items
                        .map(product => product.customerPill)
                        .filter(customerPill => !!customerPill),
                    customization: action.customization,
                }),
            ];
        }), retryBackoff({
            initialInterval: 1000,
            maxRetries: 3,
            maxInterval: 10000,
            shouldRetry: () => action.shouldRetry,
        }), catchError(() => of(ProductSummaryActions.FetchProductSummariesFail({
            productNumbers: action.productNumbers,
        }), ProductInventoryActions.FetchProductInventoriesFail({
            productNumbers: action.productNumbers,
        }), ProductContractActions.FetchProductContractsFail({
            productNumbers: action.productNumbers,
        }), CustomerPillActions.FetchCustomerPillsFail({
            productNumbers: action.productNumbers,
            customization: action.customization,
        })))))));
    }
}
ProductEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductEffects, deps: [{ token: i1$3.Actions }, { token: ProductHttpClientService }, { token: ProductStateService }, { token: i5.PanAppState }, { token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
ProductEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ProductHttpClientService }, { type: ProductStateService }, { type: i5.PanAppState }, { type: i1.Store }]; } });

const waitFor = (signal$) => (source$) => new Observable(observer => {
    // combineLatest emits the first value only when
    // both source and signal emitted at least once
    combineLatest([source$, signal$.pipe(first())]).subscribe(([v]) => observer.next(v));
});

/* eslint-disable @typescript-eslint/no-shadow */
class ProductInventoryHttpClientService {
    constructor(config, http) {
        this.config = config;
        this.http = http;
    }
    get(path, watermark) {
        const url = watermark
            ? `${this.config.apiUrl}/${path}?watermark=${watermark}`
            : `${this.config.apiUrl}/${path}`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }
    getWithParams(path, params) {
        const url = `${this.config.apiUrl}/${path}`;
        return this.http
            .get(url, { params })
            .pipe(catchError(this.handleError));
    }
    getAsync(path, watermark, batchSize) {
        let url = `${this.config.apiUrl}/${path}?batchSize=${batchSize}`;
        if (watermark) {
            url += `&watermark=${watermark}`;
        }
        return this.http.get(url).pipe(catchError(this.handleError));
    }
    post(path, body) {
        const url = `${this.config.apiUrl}/${path}`;
        return this.http.post(url, body).pipe(catchError(this.handleError));
    }
    handleError(error) {
        console.error('9NtLKW7VgH - NgRx Product Inventory Service call failed', error);
        return throwError(error);
    }
}
ProductInventoryHttpClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductInventoryHttpClientService, deps: [{ token: NGRX_PRODUCT_LIB_CONFIG }, { token: i1$2.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
ProductInventoryHttpClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductInventoryHttpClientService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductInventoryHttpClientService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PRODUCT_LIB_CONFIG]
                }] }, { type: i1$2.HttpClient }]; } });

class ProductInventoryEffects {
    constructor(store, actions$, httpService, productStateService, panAppState) {
        this.store = store;
        this.actions$ = actions$;
        this.httpService = httpService;
        this.productStateService = productStateService;
        this.panAppState = panAppState;
        //Identify product inventories that have not being loaded yet
        this.loadProductInventories$ = createEffect(() => this.actions$.pipe(ofType(ProductInventoryActions.LoadProductInventories), concatLatestFrom(action => this.store.select(selectPendingProductInventories(action.productNumbers))), filter(([action, missingProductNumbers]) => missingProductNumbers.length > 0), map(([action, missingProductNumbers]) => ProductInventoryActions.FetchProductInventories({
            productNumbers: missingProductNumbers,
        }))));
        //Retrieve missing product inventories
        this.fetchProductInventories$ = createEffect(() => this.actions$.pipe(ofType(ProductInventoryActions.FetchProductInventories), mergeMap(({ productNumbers }) => this.httpService
            .post('productinventory', productNumbers)
            .pipe(map((productInventories) => ProductInventoryActions.FetchProductInventoriesSuccess({
            requestedProductNumbers: productNumbers,
            productInventories,
        })), catchError(() => of(ProductInventoryActions.FetchProductInventoriesFail({
            productNumbers,
        })))))));
        this.fetchProductInventoriesAsyc$ = createEffect(() => this.actions$.pipe(ofType(ProductInventoryActions.FetchProductInventoriesAsync), mergeMap(({ productNumbers }) => this.httpService
            .post('productinventory', productNumbers)
            .pipe(switchMap((productInventories) => [
            ProductInventoryActions.writeToOfflineStorage({
                productInventories,
            }),
            ProductInventoryActions.FetchProductInventoriesSuccess({
                requestedProductNumbers: productNumbers,
                productInventories,
            }),
        ]), catchError(() => of(ProductInventoryActions.FetchProductInventoriesFail({
            productNumbers,
        })))))));
        this.writeToOfflineStorage$ = createEffect(() => this.actions$.pipe(ofType(ProductInventoryActions.writeToOfflineStorage), withLatestFrom(this.panAppState.user$, this.panAppState.customer$), distinctUntilChanged(), concatMap(([action, user, customer]) => this.productStateService.writeToOfflineStorage(user, customer, PRODUCT_INVENTORY_KEY, action.productInventories))), { dispatch: false });
        this.loadFromStorage$ = createEffect(() => this.actions$.pipe(ofType(ProductInventoryActions.loadFromStorage), waitFor(this.panAppState.user$), withLatestFrom(this.panAppState.user$, this.panAppState.customer$), filter(([_, user, customer]) => !!user && !!customer), concatMap(([_, user, customer]) => this.productStateService
            .getFromOfflineStorage(user, customer, PRODUCT_INVENTORY_KEY)
            .pipe(map(productInventoryState => ProductInventoryActions.loadFromStorageSuccess({
            productInventoryState,
        })), catchError((error) => of(ProductInventoryActions.loadFromStorageFail({ error })))))));
    }
}
ProductInventoryEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductInventoryEffects, deps: [{ token: i1.Store }, { token: i1$3.Actions }, { token: ProductInventoryHttpClientService }, { token: ProductStateService }, { token: i5.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
ProductInventoryEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductInventoryEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductInventoryEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: i1$3.Actions }, { type: ProductInventoryHttpClientService }, { type: ProductStateService }, { type: i5.PanAppState }]; } });

class PimClassEffects {
    constructor(actions$, httpService, panAppState) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.panAppState = panAppState;
        this.pimClassFeatureFlag = 'split_global_assign_pim_nonUsfProduct';
        /**
         * Returns actions to populate the store based on successful/error api response
         *
         * @remarks
         * Responsible for making api call to fetch PIM Class information
         */
        this.loadPimHierarchy$ = createEffect(() => this.actions$.pipe(ofType(PIMClassActions.load), mergeMap(action => combineLatest([
            of(action),
            this.panAppState.feature$([this.pimClassFeatureFlag]).pipe(take(1))
        ])), switchMap(([action, pimClassSplitFlag]) => {
            if (pimClassSplitFlag) {
                return this.httpService.get('products/pimHierarchy').pipe(map(nonUSFProducts => PIMClassActions.loadSuccess({
                    pimHierarchy: nonUSFProducts,
                })), catchError((error) => of(PIMClassActions.loadFail({ error }))));
            }
            else {
                return of(PIMClassActions.loadFail({
                    error: 'User is not allowed to see PIM Data',
                }));
            }
        })));
    }
}
PimClassEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PimClassEffects, deps: [{ token: i1$3.Actions }, { token: ProductHttpClientService }, { token: i5.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
PimClassEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PimClassEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PimClassEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ProductHttpClientService }, { type: i5.PanAppState }]; } });

/* eslint-disable @typescript-eslint/no-shadow */
class ProductContractHttpClientService {
    constructor(config, http) {
        this.config = config;
        this.http = http;
    }
    get(path, watermark) {
        const url = watermark
            ? `${this.config.apiUrl}/${path}?watermark=${watermark}`
            : `${this.config.apiUrl}/${path}`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }
    getWithParams(path, params) {
        const url = `${this.config.apiUrl}/${path}`;
        return this.http
            .get(url, { params })
            .pipe(catchError(this.handleError));
    }
    getAsync(path, watermark, batchSize) {
        let url = `${this.config.apiUrl}/${path}?batchSize=${batchSize}`;
        if (watermark) {
            url += `&watermark=${watermark}`;
        }
        return this.http.get(url).pipe(catchError(this.handleError));
    }
    post(path, body) {
        const url = `${this.config.apiUrl}/${path}`;
        return this.http.post(url, body).pipe(catchError(this.handleError));
    }
    handleError(error) {
        console.error('3nL6UjV7Gq - NgRx Product Contract Service call failed', error);
        return throwError(error);
    }
}
ProductContractHttpClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductContractHttpClientService, deps: [{ token: NGRX_PRODUCT_LIB_CONFIG }, { token: i1$2.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
ProductContractHttpClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductContractHttpClientService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductContractHttpClientService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PRODUCT_LIB_CONFIG]
                }] }, { type: i1$2.HttpClient }]; } });

class ProductContractEffects {
    constructor(store, actions$, httpService, productStateService, panAppState) {
        this.store = store;
        this.actions$ = actions$;
        this.httpService = httpService;
        this.productStateService = productStateService;
        this.panAppState = panAppState;
        //Identify product contracts that have not being loaded yet
        this.loadProductContracts$ = createEffect(() => this.actions$.pipe(ofType(ProductContractActions.LoadProductContracts), concatLatestFrom(action => this.store.select(selectPendingProductContracts(action.productNumbers))), filter(([action, missingProductNumbers]) => missingProductNumbers.length > 0), map(([action, missingProductNumbers]) => ProductContractActions.FetchProductContracts({
            productNumbers: missingProductNumbers,
        }))));
        //Retrieve missing product contracts
        this.fetchProductContracts$ = createEffect(() => this.actions$.pipe(ofType(ProductContractActions.FetchProductContracts), mergeMap(({ productNumbers }) => this.httpService
            .post('productcontract', productNumbers)
            .pipe(map((productContracts) => ProductContractActions.FetchProductContractsSuccess({
            requestedProductNumbers: productNumbers,
            productContracts,
        })), catchError(() => of(ProductContractActions.FetchProductContractsFail({
            productNumbers,
        })))))));
        this.fetchProductContractsAsync$ = createEffect(() => this.actions$.pipe(ofType(ProductContractActions.FetchProductContractsAsync), mergeMap(() => this.httpService.get('productcontract').pipe(switchMap((productContracts) => {
            const productNumbers = productContracts.map(productContract => productContract.productNumber);
            return [
                ProductContractActions.writeToOfflineStorage({
                    productContracts,
                }),
                ProductContractActions.FetchProductContractsSuccess({
                    requestedProductNumbers: productNumbers,
                    productContracts,
                }),
            ];
        }), catchError(() => of(ProductContractActions.FetchProductContractsFailAsync()))))));
        this.writeToOfflineStorage$ = createEffect(() => this.actions$.pipe(ofType(ProductContractActions.writeToOfflineStorage), withLatestFrom(this.panAppState.user$, this.panAppState.customer$), distinctUntilChanged(), concatMap(([action, user, customer]) => this.productStateService.writeToOfflineStorage(user, customer, PRODUCT_CONTRACT_KEY, action.productContracts))), { dispatch: false });
        this.loadFromStorage$ = createEffect(() => this.actions$.pipe(ofType(ProductContractActions.loadFromStorage), waitFor(this.panAppState.user$), withLatestFrom(this.panAppState.user$, this.panAppState.customer$), filter(([_, user, customer]) => !!user && !!customer), concatMap(([_, user, customer]) => this.productStateService
            .getFromOfflineStorage(user, customer, PRODUCT_CONTRACT_KEY)
            .pipe(map(productContractState => ProductContractActions.loadFromStorageSuccess({
            productContractState,
        })), catchError((error) => of(ProductContractActions.loadFromStorageFail({ error })))))));
    }
}
ProductContractEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductContractEffects, deps: [{ token: i1.Store }, { token: i1$3.Actions }, { token: ProductContractHttpClientService }, { token: ProductStateService }, { token: i5.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
ProductContractEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductContractEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductContractEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: i1$3.Actions }, { type: ProductContractHttpClientService }, { type: ProductStateService }, { type: i5.PanAppState }]; } });

/* eslint-disable no-shadow */
class ProductDetailHttpClientService {
    constructor(config, http) {
        this.config = config;
        this.http = http;
    }
    get(path, watermark) {
        const url = watermark
            ? `${this.config.apiUrl}/${path}?watermark=${watermark}`
            : `${this.config.apiUrl}/${path}`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }
    getWithParams(path, params) {
        const url = `${this.config.apiUrl}/${path}`;
        return this.http
            .get(url, { params })
            .pipe(catchError(this.handleError));
    }
    getAsync(path, watermark, batchSize) {
        let url = `${this.config.apiUrl}/${path}?batchSize=${batchSize}`;
        if (watermark) {
            url += `&watermark=${watermark}`;
        }
        return this.http.get(url).pipe(catchError(this.handleError));
    }
    post(path, body) {
        const url = `${this.config.apiUrl}/${path}`;
        return this.http.post(url, body).pipe(catchError(this.handleError));
    }
    handleError(error) {
        console.error('4yK2HmZ3Pt - NgRx Product Detail Service call failed', error);
        return throwError(error);
    }
}
ProductDetailHttpClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductDetailHttpClientService, deps: [{ token: NGRX_PRODUCT_LIB_CONFIG }, { token: i1$2.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
ProductDetailHttpClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductDetailHttpClientService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductDetailHttpClientService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PRODUCT_LIB_CONFIG]
                }] }, { type: i1$2.HttpClient }]; } });

class ProductDetailEffects {
    constructor(actions$, httpService) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.loadProductDetail$ = createEffect(() => this.actions$.pipe(ofType(ProductActions.FetchProductDetail), mergeMap(action => this.httpService
            .get('productdetail?productNumber=' + action.productNumber)
            .pipe(map(product => ProductActions.FetchProductDetailSuccess({
            productNumber: action?.productNumber,
            product,
        })), catchError(() => of(ProductActions.FetchProductDetailFailure({
            productNumber: action?.productNumber,
        })))))));
        this.loadProductDetails$ = createEffect(() => this.actions$.pipe(ofType(ProductActions.FetchProductDetails), mergeMap(action => this.httpService
            .post('productdetail', action.productNumbers)
            .pipe(map(response => ProductActions.FetchProductDetailsSuccess({
            productNumbers: action?.productNumbers,
            products: response?.items,
        })), catchError(() => of(ProductActions.FetchProductDetailsFailure({
            productNumbers: action.productNumbers,
        })))))));
    }
}
ProductDetailEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductDetailEffects, deps: [{ token: i1$3.Actions }, { token: ProductDetailHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable });
ProductDetailEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductDetailEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductDetailEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ProductDetailHttpClientService }]; } });

const PRODUCT_CHANGE_TYPES = {
    listItemChange: 'listItemChange',
    productSummaryChange: 'productSummaryChange',
    productInventoryChange: 'productInventoryChange',
    productContractChange: 'productContractChange',
    productScoopChange: 'productScoopChange',
};

class ProductSummaryEffects {
    constructor(store, actions$, httpService, productSummaryStoreService, panAppState) {
        this.store = store;
        this.actions$ = actions$;
        this.httpService = httpService;
        this.productSummaryStoreService = productSummaryStoreService;
        this.panAppState = panAppState;
        this.loadProductSummaries$ = createEffect(() => this.actions$.pipe(ofType(ProductSummaryActions.LoadProductSummaries), concatLatestFrom(action => this.store.select(selectPendingProductSummaries(action.productNumbers))), map(([action, missingProductNumbers]) => ({
            missingProductNumbers,
            skipAlternatives: action.skipAlternatives,
        })), filter(({ missingProductNumbers }) => missingProductNumbers.length > 0), map(({ missingProductNumbers, skipAlternatives }) => ProductSummaryActions.FetchProductSummaries({
            productNumbers: missingProductNumbers,
            skipAlternatives,
        }))));
        //Retrieve missing product summaries
        this.fetchProductSummaries$ = createEffect(() => this.actions$.pipe(ofType(ProductSummaryActions.FetchProductSummaries), mergeMap(({ productNumbers, skipAlternatives }) => this.httpService
            .post('productsummary', productNumbers)
            .pipe(switchMap((productSummaries) => {
            if (skipAlternatives) {
                return [
                    ProductSummaryActions.FetchProductSummariesSuccess({
                        requestedProductNumbers: productNumbers,
                        productSummaries,
                    }),
                ];
            }
            const alternativeProductNumbers = getProductAlternates(productSummaries);
            return [
                // Main result
                ProductSummaryActions.FetchProductSummariesSuccess({
                    requestedProductNumbers: productNumbers,
                    productSummaries,
                }),
                // Actions for related products
                ProductSummaryActions.LoadProductSummaries({
                    productNumbers: alternativeProductNumbers,
                    skipAlternatives: true,
                }),
                ProductInventoryActions.LoadProductInventories({
                    productNumbers: alternativeProductNumbers,
                }),
                ProductContractActions.LoadProductContracts({
                    productNumbers: alternativeProductNumbers,
                }),
            ];
        }), catchError(() => of(ProductSummaryActions.FetchProductSummariesFail({
            productNumbers,
        })))))));
        /*
        TODO this feature will be re-enabled in the future #vsm1FjYN3cksLW1qI1g7 once we have test availability
        loadFromOfflineStorage$ = createEffect(() =>
          this.actions$.pipe(
            ofType(ProductSummaryActions.ResetStore),
            withLatestFrom(
              this.panAppState.user$,
              this.panAppState.customer$,
              // this.store.select(selectProductChangesState),
            ),
            distinctUntilChanged(),
            concatMap(([_, user, customer]) =>
              this.productSummaryStoreService
                .getFromOfflineStorage<IProductSummaryState>(
                  user,
                  customer,
                  PRODUCT_SUMMARY_KEY,
                )
                .pipe(
                  map(summaries =>
                    ProductSummaryActions.LoadFromStorageSuccess({
                      summaries,
                    }),
                  ),
                  catchError(error => {
                    console.error('Could not load product summary storage');
                    return of(
                      ProductSummaryActions.LoadFromStorageFail({
                        error,
                      }),
                    );
                  }),
                ),
            ),
          ),
        );
      
        writeToOfflineStorage$ = createEffect(
          () =>
            this.actions$.pipe(
              ofType(ProductSummaryActions.FetchProductSummariesSuccess),
              withLatestFrom(
                this.panAppState.user$,
                this.panAppState.customer$,
                this.store.select(selectProductSummariesState),
                this.store.select(selectMyProducts),
              ),
              distinctUntilChanged(),
              concatMap(([_, user, customer, summaries, myProducts]) => {
                // Store only products that exist on the 'myProducts' list
                const filteredProductSummaryState = {
                  ...summaries,
                  ids: (summaries.ids as number[])
                    .map(id => +id)
                    .filter(id => myProducts.has(id)),
                  entities: Object.keys(summaries.entities)
                    .filter(id => myProducts.has(id))
                    .reduce((entities, id) => {
                      entities[id] = summaries.entities[id];
                      return entities;
                    }, {}),
                };
                return this.productSummaryStoreService.writeToOfflineStorage(
                  user,
                  customer,
                  PRODUCT_SUMMARY_KEY,
                  filteredProductSummaryState,
                );
              }),
            ),
          { dispatch: false },
        );
         */
        this.requestChanges$ = createEffect(() => this.actions$.pipe(ofType(ProductSummaryActions.RequestChanges), concatLatestFrom(action => this.store.select(selectProductChangeWatermark(PRODUCT_CHANGE_TYPES.productSummaryChange))), distinctUntilChanged(), mergeMap(([_, watermark]) => this.httpService.getSummaryChanges(watermark).pipe(
        // Success
        map(() => []), 
        // Failure
        catchError((error) => [])))), { dispatch: false });
        this.loadProductSummaryChanges$ = createEffect(() => this.actions$.pipe(ofType(ProductSummaryActions.LoadProductSummariesChanges), map(({ watermark }) => ProductChangesActions.UpdateWatermark({
            changeType: PRODUCT_CHANGE_TYPES.productSummaryChange,
            watermark,
        }))));
    }
}
ProductSummaryEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductSummaryEffects, deps: [{ token: i1.Store }, { token: i1$3.Actions }, { token: ProductHttpClientService }, { token: ProductSummaryStoreService }, { token: i5.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
ProductSummaryEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductSummaryEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductSummaryEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: i1$3.Actions }, { type: ProductHttpClientService }, { type: ProductSummaryStoreService }, { type: i5.PanAppState }]; } });

const SEARCH_PATH = 'search?worksWellWith=true';
const BROWSE_PATH = 'browse';

/* eslint-disable no-shadow */
class SearchHttpClientService {
    constructor(config, http, platformService) {
        this.config = config;
        this.http = http;
        this.platformService = platformService;
    }
    get(path, watermark) {
        const url = watermark
            ? `${this.config.apiUrl}/${path}?watermark=${watermark}`
            : `${this.config.apiUrl}/${path}`;
        return this.http.get(url);
    }
    getWithParams(path, params) {
        const url = `${this.config.apiUrl}/${path}`;
        return this.http.get(url, { params });
    }
    getAsync(path, watermark, batchSize) {
        let url = `${this.config.apiUrl}/${path}?batchSize=${batchSize}`;
        if (watermark) {
            url += `&watermark=${watermark}`;
        }
        return this.http.get(url);
    }
    doPost(url, body, traceContext, apiVersion) {
        const requestOptions = {
            headers: new HttpHeaders({
                'content-type': 'application/json',
                'consumer-id': 'ecomr4',
                'trace-context': traceContext,
            }),
        };
        if (!!apiVersion) {
            requestOptions.headers = requestOptions.headers.append('Api-Version', apiVersion);
        }
        return defer(() => this.platformService.getPlatformAndDeviceInfo()).pipe(mergeMap(state => {
            body = {
                ...body,
                appType: state.context,
                source: `r4_${this.platformService.platformType}`,
            };
            return this.http
                .post(url, body, { ...requestOptions })
                .pipe(catchError(this.handleError));
        }));
    }
    post(path, body, traceContext, apiVersion) {
        const url = `${this.config.apiUrl}/${path}`;
        return this.doPost(url, body, traceContext, apiVersion).pipe(catchError(this.handleError));
    }
    typeahead(searchText, traceContext) {
        const url = `${this.config.apiUrl}/typeahead`;
        return this.doPost(url, { searchText }, traceContext).pipe(catchError(this.handleError));
    }
    handleError(error) {
        console.error('3nh1mK7LJz - NgRx Product Search Service call failed', error);
        return throwError(error);
    }
}
SearchHttpClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchHttpClientService, deps: [{ token: NGRX_PRODUCT_LIB_CONFIG }, { token: i1$2.HttpClient }, { token: i5.PlatformService }], target: i0.ɵɵFactoryTarget.Injectable });
SearchHttpClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchHttpClientService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchHttpClientService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PRODUCT_LIB_CONFIG]
                }] }, { type: i1$2.HttpClient }, { type: i5.PlatformService }]; } });

class SearchEffects {
    constructor(actions$, httpService, store, panAppState) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.store = store;
        this.panAppState = panAppState;
        this.globalContractsVersion2FlagName = 'split_global_contracts_v2';
        this.searchProducts$ = createEffect(() => this.actions$.pipe(ofType(SearchActions.FetchSearchResponse), mergeMap(action => combineLatest([
            of(action),
            this.panAppState
                .feature$([this.globalContractsVersion2FlagName])
                .pipe(take(1)),
            this.panAppState.getCustomization$().pipe(take(1)),
        ])), switchMap(([action, globalContractsVersion2FlagEnabled, customization]) => this.httpService
            .post(SEARCH_PATH, {
            ...action.request,
            customization,
        }, traceHelperUtil.populateTraceHeader(action), globalContractsVersion2FlagEnabled ? '2' : '1')
            .pipe(withLatestFrom(this.store.select(selectProductScoopEntities)), mergeMap(([response, scoops]) => {
            if ((!!response.productNumbers &&
                response.productNumbers.length > 0) ||
                (!!response.topSellerProductNumbers &&
                    response.topSellerProductNumbers.length > 0)) {
                // This represents all existent products in the search response including
                // both base products ans substitutes/replacements
                // and any products that were not found
                const [products, productNumbers,] = response.productSummaries.reduce(([a, b], summary, idx, arr) => {
                    const product = {
                        summary,
                        productNumber: summary.productNumber,
                        inventory: response.productInventories.filter(inventory => inventory.productNumber === summary.productNumber)[0],
                        contract: response.productContracts.filter(contract => contract.productNumber === summary.productNumber)[0],
                        customerPill: response?.customerPills?.filter(customerPill => customerPill?.productNumber ===
                            summary?.productNumber)[0],
                        notFound: false,
                    };
                    product.summary = {
                        ...product.summary,
                        propertiesArray: Array.from(getPropertiesSet(product, scoops)),
                    };
                    arr[idx] = product.summary;
                    a.push(product);
                    b.push(product.productNumber);
                    return [a, b];
                }, [[], []]);
                let returnedProductNumbers = [...productNumbers];
                if (!!response.productNumbers) {
                    returnedProductNumbers = returnedProductNumbers.concat(response.productNumbers);
                }
                if (!!response.alternativeProductNumbers) {
                    returnedProductNumbers = returnedProductNumbers.concat(response.alternativeProductNumbers);
                }
                if (!!response.topSellerProductNumbers) {
                    returnedProductNumbers = returnedProductNumbers.concat(response.topSellerProductNumbers);
                }
                const obtainedProductNumbers = [
                    ...new Set([...returnedProductNumbers]),
                ];
                return [
                    // Main result
                    SearchActions.FetchSearchResponseSuccess({
                        result: response,
                        tracking: {
                            tracing: {
                                data: {
                                    traceContext: TRACE_CONTEXT.search,
                                    isStartOfTrace: false,
                                    isEndOfTrace: false,
                                },
                            },
                        },
                    }),
                    // Actions for products
                    ProductActions.LoadProductOrderabilitiesSuccess({
                        requestedProductNumbers: obtainedProductNumbers,
                        products,
                    }),
                    ProductSummaryActions.FetchProductSummariesSuccess({
                        requestedProductNumbers: obtainedProductNumbers,
                        productSummaries: response.productSummaries,
                    }),
                    ProductInventoryActions.FetchProductInventoriesSuccess({
                        requestedProductNumbers: obtainedProductNumbers,
                        productInventories: response.productInventories,
                    }),
                    ProductContractActions.FetchProductContractsSuccess({
                        requestedProductNumbers: obtainedProductNumbers,
                        productContracts: response.productContracts,
                    }),
                    CustomerPillActions.FetchCustomerPillsSuccess({
                        requestedProductNumbers: obtainedProductNumbers,
                        customerPills: response.customerPills,
                        customization,
                    }),
                ];
            }
            else {
                return [
                    // Main result
                    SearchActions.FetchSearchResponseSuccess({
                        result: response,
                        tracking: {
                            tracing: {
                                data: {
                                    traceContext: TRACE_CONTEXT.search,
                                    isStartOfTrace: false,
                                    isEndOfTrace: false,
                                },
                            },
                        },
                    }),
                ];
            }
        }), catchError(e => of(SearchActions.FetchSearchResponseFail({
            result: { totalNumberOfRecords: 0 },
            tracking: {
                tracing: {
                    data: {
                        traceContext: TRACE_CONTEXT.search,
                        isStartOfTrace: false,
                        isEndOfTrace: false,
                    },
                },
            },
        })))))));
    }
}
SearchEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchEffects, deps: [{ token: i1$3.Actions }, { token: SearchHttpClientService }, { token: i1.Store }, { token: i5.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
SearchEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: SearchHttpClientService }, { type: i1.Store }, { type: i5.PanAppState }]; } });

class ProductScoopEffects {
    /* For websocket implementation
    loadProductScoopsSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ProductScoopActions.loadProductScoopsSuccess),
        filter((action) => action.productScoops.length > 0),
        map((action) => ProductScoopActions.writeToOfflineStorage())
      )
    );*/
    constructor(actions$, httpService, store, productStateService, panAppState) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.store = store;
        this.productStateService = productStateService;
        this.panAppState = panAppState;
        this.loadProductScoops$ = createEffect(() => this.actions$.pipe(ofType(ProductScoopActions.loadProductScoops), mergeMap(() => this.httpService.get('productscoop').pipe(first(), map(productScoops => ProductScoopActions.loadProductScoopsSuccess({
            productScoops,
        })), catchError((error) => of(ProductScoopActions.loadProductScoopsFail({ error })))))));
        this.loadProductScoopsAsync$ = createEffect(() => this.actions$.pipe(ofType(ProductScoopActions.loadProductScoopsAsync), mergeMap(() => this.httpService.get('productscoop').pipe(first(), switchMap(productScoops => [
            ProductScoopActions.writeToOfflineStorage({
                productScoops,
            }),
            ProductScoopActions.loadProductScoopsSuccess({
                productScoops,
            }),
        ]), catchError((error) => of(ProductScoopActions.loadProductScoopsFail({ error })))))));
        this.writeToOfflineStorage$ = createEffect(() => this.actions$.pipe(ofType(ProductScoopActions.writeToOfflineStorage), withLatestFrom(this.panAppState.user$, this.panAppState.customer$), distinctUntilChanged(), concatMap(([action, user, customer]) => this.productStateService.writeToOfflineStorage(user, customer, PRODUCT_SCOOP_KEY, action))), { dispatch: false });
        this.loadFromStorage$ = createEffect(() => this.actions$.pipe(ofType(ProductScoopActions.loadFromStorage), waitFor(this.panAppState.user$), withLatestFrom(this.panAppState.user$, this.panAppState.customer$), filter(([_, user, customer]) => !!user && !!customer), concatMap(([_, user, customer]) => this.productStateService
            .getFromOfflineStorage(user, customer, PRODUCT_SCOOP_KEY)
            .pipe(map(productScoops => ProductScoopActions.loadFromStorageSuccess({
            productScoops,
        })), catchError((error) => of(ProductScoopActions.loadFromStorageFail({ error })))))));
    }
}
ProductScoopEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductScoopEffects, deps: [{ token: i1$3.Actions }, { token: ProductHttpClientService }, { token: i1.Store }, { token: ProductStateService }, { token: i5.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
ProductScoopEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductScoopEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductScoopEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ProductHttpClientService }, { type: i1.Store }, { type: ProductStateService }, { type: i5.PanAppState }]; } });

class BrowseCategoryEffects {
    constructor(actions$, httpService, store) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.store = store;
        //Identify product summaries that have not being loaded yet
        this.loadBrowseResponse$ = createEffect(() => this.actions$.pipe(ofType(BrowseCategoryActions.LoadBrowseResponse), concatLatestFrom(action => this.store.select(selectBrowseResponse(action.request?.searchFilterProperties || '0'))), filter(([action, browse]) => browse?.loadingState !== LoadingState.loaded), map(([action]) => BrowseCategoryActions.FetchBrowseResponse({
            request: action.request,
            tracking: {
                tracing: {
                    data: {
                        traceContext: 'browse',
                        isStartOfTrace: false,
                        isEndOfTrace: true,
                    },
                },
            },
        }))));
        this.browseCategoriesCatalog$ = createEffect(() => this.actions$.pipe(ofType(BrowseCategoryActions.FetchBrowseResponse), switchMap(action => this.httpService
            .post(BROWSE_PATH, action.request, traceHelperUtil.populateTraceHeader(action))
            .pipe(map(response => BrowseCategoryActions.FetchBrowseResponseSuccess({
            result: response.browseCategories,
            requestParams: action.request,
            tracking: {
                tracing: {
                    data: {
                        traceContext: 'browse',
                        isStartOfTrace: false,
                        isEndOfTrace: true,
                    },
                },
            },
        })), catchError(e => of(BrowseCategoryActions.FetchBrowseResponseFail({
            result: {
                loadingState: LoadingState.error,
                selectorId: action.request?.searchFilterProperties
                    ? action.request?.searchFilterProperties
                    : '0',
            },
            tracking: {
                tracing: {
                    data: {
                        traceContext: 'browse',
                        isStartOfTrace: false,
                        isEndOfTrace: true,
                    },
                },
            },
        })))))));
    }
}
BrowseCategoryEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowseCategoryEffects, deps: [{ token: i1$3.Actions }, { token: SearchHttpClientService }, { token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
BrowseCategoryEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowseCategoryEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowseCategoryEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: SearchHttpClientService }, { type: i1.Store }]; } });

class SearchTypeaheadEffects {
    constructor(store, actions$, httpService) {
        this.store = store;
        this.actions$ = actions$;
        this.httpService = httpService;
        // If pending fetch
        this.loadTypeahead$ = createEffect(() => this.actions$.pipe(ofType(TypeaheadActions.LoadTypeahead), concatLatestFrom(action => this.store.select(selectPendingTypeahead(action.searchTerm))), filter(([action, isPending]) => isPending), map(([action]) => TypeaheadActions.FetchTypeahead({
            searchTerm: action.searchTerm,
            tracking: {
                tracing: {
                    data: {
                        traceContext: TRACE_CONTEXT.typeahead,
                        isStartOfTrace: false,
                        isEndOfTrace: true,
                        attributes: action.tracking?.tracing?.data?.attributes,
                    },
                },
            },
        }))));
        // Execute fetch call
        this.fetchTypeahead$ = createEffect(() => this.actions$.pipe(ofType(TypeaheadActions.FetchTypeahead), mergeMap(({ searchTerm, tracking }) => this.httpService
            .typeahead(searchTerm, traceHelperUtil.populateTraceHeader({ tracking }))
            .pipe(switchMap(response => [
            TypeaheadActions.FetchTypeaheadSuccess({
                result: response,
                tracking,
            }),
        ]), catchError(() => of(TypeaheadActions.FetchTypeaheadFail({ searchTerm, tracking })))))));
    }
}
SearchTypeaheadEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchTypeaheadEffects, deps: [{ token: i1.Store }, { token: i1$3.Actions }, { token: SearchHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable });
SearchTypeaheadEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchTypeaheadEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchTypeaheadEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: i1$3.Actions }, { type: SearchHttpClientService }]; } });

/* eslint-disable @typescript-eslint/no-shadow */
class ProductChangesHttpClientService {
    constructor(config, http) {
        this.config = config;
        this.http = http;
    }
    getListChanges(watermark) {
        const path = 'productchanges/listChanges';
        const url = watermark
            ? `${this.config.apiUrl}/${path}?watermark=${watermark}`
            : `${this.config.apiUrl}/${path}`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }
    handleError(error) {
        console.error('2vW8hXkZ1N - NgRx Product Changes Service call failed', error);
        return throwError(error);
    }
}
ProductChangesHttpClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductChangesHttpClientService, deps: [{ token: NGRX_PRODUCT_LIB_CONFIG }, { token: i1$2.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
ProductChangesHttpClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductChangesHttpClientService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductChangesHttpClientService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PRODUCT_LIB_CONFIG]
                }] }, { type: i1$2.HttpClient }]; } });

class ProductChangesEffects {
    constructor(store, actions$, productChangesClient, productStateService, panAppState) {
        this.store = store;
        this.actions$ = actions$;
        this.productChangesClient = productChangesClient;
        this.productStateService = productStateService;
        this.panAppState = panAppState;
        this.requestChanges$ = createEffect(() => this.actions$.pipe(ofType(ProductChangesActions.RequestChanges), mergeMap(() => [
            // REQUEST LIST RELATED CHANGES
            ProductChangesActions.RequestListItemChanges(),
            // Other cross domain changes request would go here...
        ])));
        this.requestListItemChanges$ = createEffect(() => this.actions$.pipe(ofType(ProductChangesActions.RequestListItemChanges), concatLatestFrom(action => this.store.select(selectProductChangeWatermark(PRODUCT_CHANGE_TYPES.listItemChange))), distinctUntilChanged(), mergeMap(([_, watermark]) => 
        // CALL SERVICE WITH WATERMARK
        this.productChangesClient.getListChanges(watermark).pipe(
        // Success
        map(() => []), 
        // Failure
        catchError((error) => [])))), { dispatch: false });
        this.listItemsAddedReceived$ = createEffect(() => this.actions$.pipe(ofType(ProductChangesActions.ListItemsAddedReceived), mergeMap(action => {
            const watermark = action.watermark;
            // Consolidate list of changes into single collections
            const changes = action.content;
            let summaries = [];
            let inventories = [];
            let productNumbers = [];
            let divisionNumber = null;
            if (!!changes && Array.isArray(changes)) {
                Array.from(changes).forEach(change => {
                    productNumbers = [...productNumbers, change?.data?.productNumbers];
                    summaries = [...summaries, change?.data?.summaries];
                    inventories = [...inventories, change?.data?.inventories];
                    if (!!divisionNumber && !!change?.data?.divisionNumber) {
                        divisionNumber = change?.data?.divisionNumber;
                    }
                });
            }
            return [
                // Update my products
                MyProductActions.UpdateMyProducts({
                    productNumbers,
                }),
                // Update product list item changes watermark local storage
                ProductChangesActions.UpdateWatermark({
                    changeType: PRODUCT_CHANGE_TYPES.listItemChange,
                    watermark,
                }),
                // Update summaries state
                ProductSummaryActions.FetchProductSummariesSuccess({
                    requestedProductNumbers: productNumbers,
                    productSummaries: summaries,
                }),
                // update inventories state
                ProductInventoryActions.FetchProductInventoriesSuccess({
                    requestedProductNumbers: productNumbers,
                    productInventories: inventories,
                }),
            ];
        })));
        this.loadFromOfflineStorage$ = createEffect(() => this.actions$.pipe(ofType(ProductChangesActions.ResetStore), withLatestFrom(this.panAppState.user$, this.panAppState.customer$), distinctUntilChanged(), concatMap(([_, user, customer]) => this.productStateService
            .getFromOfflineStorage(user, customer, PRODUCT_CHANGES_KEY)
            .pipe(map(productChanges => ProductChangesActions.LoadFromStorageSuccess({
            changes: productChanges,
        }))))));
        this.writeToOfflineStorage$ = createEffect(() => this.actions$.pipe(ofType(ProductChangesActions.UpdateWatermark), withLatestFrom(this.panAppState.user$, this.panAppState.customer$, this.store.select(selectProductChangesState)), distinctUntilChanged(), concatMap(([_, user, customer, productChanges]) => this.productStateService.writeToOfflineStorage(user, customer, PRODUCT_CHANGES_KEY, productChanges))), { dispatch: false });
    }
}
ProductChangesEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductChangesEffects, deps: [{ token: i1.Store }, { token: i1$3.Actions }, { token: ProductChangesHttpClientService }, { token: ProductStateService }, { token: i5.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
ProductChangesEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductChangesEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductChangesEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: i1$3.Actions }, { type: ProductChangesHttpClientService }, { type: ProductStateService }, { type: i5.PanAppState }]; } });

/* eslint-disable @typescript-eslint/naming-convention */
class PowerReviewsHttpClientService {
    constructor(http, config) {
        this.http = http;
        this.config = config;
        this.NUMBER_OF_CALL = 10;
        this.RETRY_COUNT = 2;
    }
    get(pageIds) {
        const host = this.config.powerReviewApiUrl;
        const merchantId = this.config.powerReviewMerchantId;
        const locate = this.config.powerReviewLocale;
        const apiKey = this.config.powerReviewApiKey;
        const urls = this.splitIdsEveryTenRecords(pageIds).map(value => {
            const url = `${host}m/${merchantId}/l/${locate}/product/${value}/snippet?apikey=${apiKey}`;
            return this.http
                .get(url)
                .pipe(retry(this.RETRY_COUNT), catchError(this.handleError));
        });
        return forkJoin(urls).pipe(map(([first, second, third, fourth, fifth]) => {
            const mergeResult = first?.results.concat(second?.results, third?.results, fourth?.results, fifth?.results);
            first = {
                ...first,
                results: mergeResult,
            };
            return first;
        }));
    }
    handleError(error) {
        console.error('1yK6HmZ9Pt - NgRx Power Reviews Service call failed', error);
        return throwError(error);
    }
    splitIdsEveryTenRecords(pageIds) {
        const pageIdsArray = pageIds.split(',');
        const arrayReturn = [];
        for (let i = 0; i < pageIdsArray.length; i += this.NUMBER_OF_CALL) {
            arrayReturn.push(pageIdsArray.slice(i, i + this.NUMBER_OF_CALL).join());
        }
        return arrayReturn;
    }
}
PowerReviewsHttpClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PowerReviewsHttpClientService, deps: [{ token: i1$2.HttpClient }, { token: NGRX_PRODUCT_LIB_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
PowerReviewsHttpClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PowerReviewsHttpClientService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PowerReviewsHttpClientService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PRODUCT_LIB_CONFIG]
                }] }]; } });

class PowerReviewProductEffects {
    constructor(actions$, httpService) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.loadPowerReviewsProduct$ = createEffect(() => this.actions$.pipe(ofType(ProductActions.FetchPowerReviewsProduct), filter(value => value?.pageIds.length > 0), switchMap(action => this.httpService.get(action.pageIds).pipe(map(productSnippet => ProductActions.FetchPowerReviewsProductSuccess({ productSnippet })), catchError(error => of(ProductActions.FetchPowerReviewsProductFailure({ error })))))));
        this.updatePowerReviewsProduct$ = createEffect(() => this.actions$.pipe(ofType(ProductActions.UpdatePowerReviewsProduct), filter(value => value?.pageIds.length > 0), switchMap(action => this.httpService.get(action.pageIds).pipe(map(productSnippet => ProductActions.UpdatePowerReviewsProductSuccess({ productSnippet })), catchError(error => of(ProductActions.FetchPowerReviewsProductFailure({ error })))))));
    }
}
PowerReviewProductEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PowerReviewProductEffects, deps: [{ token: i1$3.Actions }, { token: PowerReviewsHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable });
PowerReviewProductEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PowerReviewProductEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PowerReviewProductEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: PowerReviewsHttpClientService }]; } });

class NonUSFProductEffects {
    constructor(actions$, httpService, productAnalyticsService) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.productAnalyticsService = productAnalyticsService;
        /**
         * Returns actions to populate the store based on successful/error api response
         *
         * @remarks
         * Responsible for making api call to fetch non USF products
         */
        this.load$ = createEffect(() => this.actions$.pipe(ofType(NonUSFProductActions.load), concatMap(action => this.httpService.get('nonUSFProduct').pipe(map(nonUSFProducts => NonUSFProductActions.loadSuccess({
            nonUSFProducts,
        })), catchError((error) => of(NonUSFProductActions.loadFail({ error })))))));
        /**
         * @remarks
         * Responsible for making api call to create non usf product
         */
        this.create$ = createEffect(() => this.actions$.pipe(ofType(NonUSFProductActions.create), concatMap(action => this.httpService
            .postSingleObject('nonUSFProduct', action.nonUSFProduct)
            .pipe(map(response => NonUSFProductActions.createSuccess({ nonUSFProduct: response })), catchError((error) => [
            NonUSFProductActions.createFail({
                error: error.message,
                nonUSFProduct: action.nonUSFProduct,
            }),
        ])))));
        /**
         * @remarks
         * Responsible for dispatching analytics action if creation was successful
         */
        this.createSuccess$ = createEffect(() => this.actions$.pipe(ofType(NonUSFProductActions.createSuccess), map(action => {
            this.productAnalyticsService.trackCreateNonUsfProductSuccess(action.nonUSFProduct.divisionNumber + '-' + action.nonUSFProduct.productNumber, action.nonUSFProduct.productDescLong, action.nonUSFProduct.price);
        })), { dispatch: false });
        /**
         * @remarks
         * Responsible for making api call to delete non usf product asynchronously
         */
        this.delete$ = createEffect(() => this.actions$.pipe(ofType(NonUSFProductActions.delete), concatMap(action => {
            const requestBody = { productNumber: [] };
            action.nonUSFProducts.forEach(product => {
                requestBody.productNumber.push(product.productNumber);
            });
            return this.httpService
                .post('nonUSFProduct/deleteProduct', requestBody)
                .pipe(switchMap(response => [
                NonUSFProductActions.deleteSuccess({ response }),
            ]), catchError((error) => [
                NonUSFProductActions.deleteFail({
                    error: error.message,
                    nonUSFProducts: action.nonUSFProducts,
                }),
            ]));
        })));
        /**
         * @remarks
         * Responsible for making api call to update non usf product asynchronously
         */
        this.update$ = createEffect(() => this.actions$.pipe(ofType(NonUSFProductActions.update), concatMap(action => this.httpService
            .postSingleObject('nonUSFProduct', action.nonUSFProduct)
            .pipe(map(response => NonUSFProductActions.updateSuccess({ nonUSFProduct: response })), catchError((error) => [
            NonUSFProductActions.updateFail({
                error: error.message,
                nonUSFProduct: action.nonUSFProduct,
            }),
        ])))));
        /**
         * @remarks
         * Responsible for making api call to import non usf products asynchronously
         */
        this.import$ = createEffect(() => this.actions$.pipe(ofType(NonUSFProductActions.import), concatMap(action => this.httpService
            .postSingleObject('nonUSFProduct/import', action.nonUSFProducts)
            .pipe(map(response => NonUSFProductActions.importSuccess({ response })), catchError((error) => of(NonUSFProductActions.importFail({ error: error.message })))))));
    }
}
NonUSFProductEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NonUSFProductEffects, deps: [{ token: i1$3.Actions }, { token: ProductHttpClientService }, { token: ProductAnalyticsService }], target: i0.ɵɵFactoryTarget.Injectable });
NonUSFProductEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NonUSFProductEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NonUSFProductEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ProductHttpClientService }, { type: ProductAnalyticsService }]; } });

class ExternalVendorEffects {
    constructor(actions$, httpService) {
        this.actions$ = actions$;
        this.httpService = httpService;
        /**
         * Returns actions to populate the store based on successful/error api response
         *
         * @remarks
         * Responsible for making api call to fetch external vendors
         * assigned to a particular division/customer association
         */
        this.load$ = createEffect(() => this.actions$.pipe(ofType(ExternalVendorActions.load), concatMap(action => this.httpService.get('externalVendor').pipe(map(externalVendors => ExternalVendorActions.loadSuccess({
            vendors: externalVendors,
        })), catchError((error) => of(ExternalVendorActions.loadFail({ error })))))));
        /**
         * @remarks
         * Responsible for making api call to create an external vendor
         * assigned to a particular division/customer association
         */
        this.createVendor$ = createEffect(() => this.actions$.pipe(ofType(ExternalVendorActions.create), concatMap(action => this.httpService
            .post('externalVendor', action.vendor)
            .pipe(concatMap(response => [
            ExternalVendorActions.createSuccess({ vendor: action.vendor }),
            ExternalVendorActions.domainCallSuccess({
                vendor: {
                    ...action.vendor,
                },
            }),
        ]), catchError((error) => [
            ExternalVendorActions.createFail({
                error: error.message,
                vendor: action.vendor,
            }),
        ])))));
        /**
         * @remarks
         * Responsible for making api call to edit an external vendor
         * assigned to a particular division/customer association
         */
        this.editVendor$ = createEffect(() => this.actions$.pipe(ofType(ExternalVendorActions.edit), concatMap(action => this.httpService
            .post('externalVendor', action.updatedVendor)
            .pipe(concatMap(response => [
            ExternalVendorActions.editSuccess({
                updatedVendor: action.updatedVendor,
            }),
            ExternalVendorActions.domainCallSuccess({
                vendor: {
                    ...action.updatedVendor,
                },
            }),
        ]), catchError((error) => [
            ExternalVendorActions.editFail({
                error: error.message,
                vendor: action.originalVendor,
            }),
        ])))));
        this.delete$ = createEffect(() => this.actions$.pipe(ofType(ExternalVendorActions.delete), concatMap(action => {
            const requestBody = { vendorIds: [] };
            action.vendorIds.forEach(vendorId => {
                requestBody.vendorIds.push(vendorId);
            });
            return this.httpService
                .post('externalVendor/deleteVendor', requestBody)
                .pipe(switchMap(response => [
                ExternalVendorActions.deleteSuccess({
                    vendors: response,
                    vendorIds: action.vendorIds,
                }),
            ]), catchError((error) => [
                ExternalVendorActions.deleteFail({
                    error: error.message,
                    vendors: action.vendors,
                }),
            ]));
        })));
    }
}
ExternalVendorEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ExternalVendorEffects, deps: [{ token: i1$3.Actions }, { token: ProductHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable });
ExternalVendorEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ExternalVendorEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ExternalVendorEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ProductHttpClientService }]; } });

class BetterBuysDtoService {
    constructor() { }
    transformToAnnualSavingsRequestBody(betterBuysResponse) {
        const updatedBetterBuys = [];
        const betterBuys = betterBuysResponse.betterBuys;
        for (const betterBuy of betterBuys) {
            const updatedBetterBuyProductDetails = this.getUpdatedProductDetails(betterBuy.betterBuyProductDetails);
            const updatedOriginalProductDetails = this.getUpdatedProductDetails(betterBuy.originalProductDetails);
            const updatedBetterBuy = {
                ...betterBuy,
                originalProductDetails: updatedOriginalProductDetails,
                betterBuyProductDetails: updatedBetterBuyProductDetails,
            };
            updatedBetterBuys.push(updatedBetterBuy);
        }
        const annualSavingsRequestBody = {
            ...betterBuysResponse,
            betterBuys: updatedBetterBuys,
            priceEffectiveDate: '',
            priceEffectiveTime: '',
        };
        return annualSavingsRequestBody;
    }
    transformToClientModel(betterBuysResponse) {
        const updatedBetterBuys = [];
        for (const betterBuy of betterBuysResponse.betterBuys) {
            const updatedBetterBuy = this.getUpdatedBetterBuy(betterBuy);
            updatedBetterBuys.push(updatedBetterBuy);
        }
        return updatedBetterBuys;
    }
    extractProductDataFromBetterBuysResponse(betterBuysResponse, productScoops) {
        const productNumbers = [];
        const inventories = [];
        const summaries = [];
        const contracts = [];
        const customerPills = [];
        for (const betterBuy of betterBuysResponse.betterBuys) {
            productNumbers.push(betterBuy.originalProductNumber, betterBuy.betterBuyProductNumber);
            if (betterBuy.betterBuyProductDetails.inventory) {
                inventories.push(betterBuy.betterBuyProductDetails.inventory);
            }
            if (betterBuy.originalProductDetails.inventory) {
                inventories.push(betterBuy.originalProductDetails.inventory);
            }
            if (betterBuy.betterBuyProductDetails.contract) {
                contracts.push(betterBuy.betterBuyProductDetails.contract);
            }
            if (betterBuy.originalProductDetails.contract) {
                contracts.push(betterBuy.originalProductDetails.contract);
            }
            // need to include properties here, need to pass in scoops
            if (betterBuy.betterBuyProductDetails.summary) {
                summaries.push({
                    ...betterBuy.betterBuyProductDetails.summary,
                    propertiesArray: Array.from(getPropertiesSet(betterBuy.betterBuyProductDetails, productScoops)),
                });
            }
            if (betterBuy.originalProductDetails.summary) {
                summaries.push({
                    ...betterBuy.originalProductDetails.summary,
                    propertiesArray: Array.from(getPropertiesSet(betterBuy.originalProductDetails, productScoops)),
                });
            }
            if (betterBuy.betterBuyProductDetails.customerPill) {
                customerPills.push(betterBuy.betterBuyProductDetails.customerPill);
            }
            if (betterBuy.originalProductDetails.customerPill) {
                customerPills.push(betterBuy.originalProductDetails.customerPill);
            }
        }
        return {
            productNumbers,
            inventories,
            summaries,
            contracts,
            customerPills,
        };
    }
    getUpdatedProductDetails(product) {
        return {
            productNumber: product.productNumber,
            summary: {
                catchWeightFlag: product.summary?.catchWeightFlag,
                salesPackSize: product.summary?.salesPackSize,
                priceUom: product.summary?.priceUom,
                netWeight: product.summary?.netWeight,
            },
        };
    }
    getUpdatedBetterBuy(betterBuy) {
        return {
            originalProductNumber: betterBuy.originalProductNumber,
            betterBuyProductNumber: betterBuy.betterBuyProductNumber,
            savings: betterBuy.savings,
            annualSavings: betterBuy.annualSavings,
            savingsUom: betterBuy.savingsUom,
            annualUsageCase: betterBuy.annualUsageCase,
            annualUsagePounds: betterBuy.annualUsagePounds,
            annualSavingsUom: betterBuy.annualSavingsUom,
            reasonCode: betterBuy.reasonCode,
            reason: betterBuy.reason,
            savingsPerServing: betterBuy.savingsPerServing,
            savingsPerPound: betterBuy.savingsPerPound,
            savingsPerCase: betterBuy.savingsPerCase,
            annualSavingsBySavingsPerServing: betterBuy.annualSavingsBySavingsPerServing,
            annualSavingsBySavingsPerPound: betterBuy.annualSavingsBySavingsPerPound,
            annualSavingsBySavingsPerCase: betterBuy.annualSavingsBySavingsPerCase,
            oldProductServingCase: betterBuy.oldProductServingCase,
            newProductServingCase: betterBuy.newProductServingCase,
        };
    }
}
BetterBuysDtoService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BetterBuysDtoService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
BetterBuysDtoService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BetterBuysDtoService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BetterBuysDtoService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

class BetterBuysEffects {
    constructor(actions$, httpService, betterBuysDtoService, panAppState, store) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.betterBuysDtoService = betterBuysDtoService;
        this.panAppState = panAppState;
        this.store = store;
        this.betterBuysFeatureFlag = 'split_global_better_buys';
        this.betterBuysContractsV2FeatureFlag = 'split_global_contracts_v2';
        /**
         * Returns actions to populate the store based on successful/error api response
         *
         * @remarks
         * Responsible for making api call to fetch better buys
         * assigned to a particular division/customer association
         */
        this.load$ = createEffect(() => this.actions$.pipe(ofType(BetterBuysActions.load), mergeMap(action => combineLatest([
            of(action),
            this.panAppState.feature$([this.betterBuysFeatureFlag]).pipe(take(1)),
            this.panAppState
                .feature$([this.betterBuysContractsV2FeatureFlag])
                .pipe(take(1)),
            this.panAppState.getCustomization$().pipe(take(1)),
        ])), switchMap(([action, betterBuysSplitFlag, betterBuysContractsV2Flag, customization,]) => {
            let productContractVersion;
            if (betterBuysSplitFlag) {
                if (betterBuysContractsV2Flag) {
                    productContractVersion = '2';
                }
                return combineLatest([
                    this.panAppState.customer$,
                    this.httpService.getObject(`betterBuys?customization=${customization}`, productContractVersion),
                ]).pipe(takeWhile(([customer, bbResponse]) => this.customersAreSameContext(action.selectedCustomer, customer)), switchMap(([customer, betterBuysResponse]) => of(BetterBuysActions.filterOutRestrictedBetterBuys({
                    betterBuysResponse,
                    selectedCustomer: action.selectedCustomer,
                }))), catchError((error) => of(BetterBuysActions.loadFail({ error }))));
            }
            else {
                return of(BetterBuysActions.loadFail({
                    error: 'User is not allowed to see better buys',
                }));
            }
        })));
        this.filterOutRestrictedBetterBuysSuccess$ = createEffect(() => this.actions$.pipe(ofType(BetterBuysActions.filterOutRestrictedBetterBuysSuccess), mergeMap(action => combineLatest([
            of(action),
            this.store.select(selectProductScoopState),
            this.panAppState.getCustomization$().pipe(take(1)),
        ]).pipe(filter(([act, productScoopState, customization]) => productScoopState.loadingState === LoadingState.loaded ||
            productScoopState.loadingState === LoadingState.error), take(1))), switchMap(([action, productScoopState, customization]) => this.getLoadSuccessActions(action.betterBuysResponse, productScoopState, action.selectedCustomer, customization))));
        this.loadSavingsOverTime$ = createEffect(() => this.actions$.pipe(ofType(BetterBuysActions.loadSavingsOverTime), switchMap(action => combineLatest([
            this.panAppState.customer$,
            this.httpService.getObject('betterBuys/savingsOverTime'),
        ]).pipe(takeWhile(([customer, response]) => this.customersAreSameContext(action.selectedCustomer, customer)), switchMap(([cutsomer, response]) => {
            const savingsOverTime = response;
            return [
                BetterBuysActions.loadSavingsOverTimeSuccess({
                    savingsOverTime,
                }),
            ];
        }), catchError((error) => [
            BetterBuysActions.loadSavingsOverTimeFail({ error }),
        ])))));
    }
    customersAreSameContext(customer1, customer2) {
        return (customer1 &&
            customer2 &&
            customer1.customerNumber === customer2.customerNumber &&
            customer1.departmentNumber === customer2.departmentNumber &&
            customer1.divisionNumber === customer2.divisionNumber);
    }
    getLoadSuccessActions(bbResponse, productScoopState, selectedCustomer, customization) {
        const actionsToReturn = [];
        const betterBuysState = this.betterBuysDtoService.transformToClientModel(bbResponse);
        actionsToReturn.push(BetterBuysActions.loadSuccess({
            betterBuys: betterBuysState,
        }));
        const { productNumbers, inventories, summaries, contracts, customerPills } = this.betterBuysDtoService.extractProductDataFromBetterBuysResponse(bbResponse, productScoopState.entities);
        if (bbResponse.betterBuys.length > 0) {
            actionsToReturn.push(ProductSummaryActions.FetchProductSummariesSuccess({
                productSummaries: summaries,
                requestedProductNumbers: productNumbers,
            }), ProductInventoryActions.FetchProductInventoriesSuccess({
                productInventories: inventories,
                requestedProductNumbers: productNumbers,
            }), ProductContractActions.FetchProductContractsSuccess({
                productContracts: contracts,
                requestedProductNumbers: productNumbers,
            }), CustomerPillActions.FetchCustomerPillsSuccess({
                requestedProductNumbers: productNumbers,
                customerPills,
                customization,
            }), BetterBuysActions.loadSavingsOverTime({
                selectedCustomer,
            }));
        }
        return actionsToReturn;
    }
}
BetterBuysEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BetterBuysEffects, deps: [{ token: i1$3.Actions }, { token: ProductHttpClientService }, { token: BetterBuysDtoService }, { token: i5.PanAppState }, { token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
BetterBuysEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BetterBuysEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BetterBuysEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ProductHttpClientService }, { type: BetterBuysDtoService }, { type: i5.PanAppState }, { type: i1.Store }]; } });

const isUsFoodsDeliveredFilterPresent = (searchResponseV2) => {
    let isDeliveryMethodFilter = false;
    searchResponseV2?.facets?.forEach(facet => {
        if (facet.label === 'delivery_method') {
            isDeliveryMethodFilter = facet.values.some(v => v.label === 'us foods delivered');
        }
    });
    return isDeliveryMethodFilter;
};

/* eslint-disable no-shadow */
class SearchHttpClientServiceV2 {
    constructor(config, http) {
        this.config = config;
        this.http = http;
    }
    post(path, body) {
        const url = `${this.config.apiBaseUrl}/v2/${path}`;
        return this.http
            .post(url, body)
            .pipe(catchError(this.handleError));
    }
    handleError(error) {
        return throwError(error);
    }
}
SearchHttpClientServiceV2.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchHttpClientServiceV2, deps: [{ token: NGRX_PRODUCT_LIB_CONFIG }, { token: i1$2.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
SearchHttpClientServiceV2.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchHttpClientServiceV2 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchHttpClientServiceV2, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PRODUCT_LIB_CONFIG]
                }] }, { type: i1$2.HttpClient }]; } });

/* eslint-disable @typescript-eslint/naming-convention */
class SearchEffectsV2 {
    constructor(actions$, httpService, store) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.store = store;
        this.FILTER_DELIVERY_METHOD = 'tenant~delivery_method';
        this.FILTER_DELIVERY_METHOD_US_FOODS_DELIVERED = 'us foods delivered';
        this.searchProducts$ = createEffect(() => this.actions$.pipe(ofType(SearchActionsV2.FetchSearchResponse), switchMap(action => this.httpService.post('search', action.request).pipe(map(response => {
            if (action?.applyDefaultDeliveryMethodFilter &&
                isUsFoodsDeliveredFilterPresent(response) &&
                action?.request?.facetFilters) {
                let facetFilters = { ...action?.request?.facetFilters };
                if (action?.request?.facetFilters[this.FILTER_DELIVERY_METHOD]) {
                    if (!action?.request?.facetFilters[this.FILTER_DELIVERY_METHOD]?.includes(this.FILTER_DELIVERY_METHOD_US_FOODS_DELIVERED)) {
                        facetFilters = {
                            ...facetFilters,
                            'tenant~delivery_method': [
                                ...facetFilters[this.FILTER_DELIVERY_METHOD],
                                this.FILTER_DELIVERY_METHOD_US_FOODS_DELIVERED,
                            ],
                        };
                    }
                }
                else {
                    facetFilters = {
                        ...facetFilters,
                        'tenant~delivery_method': [
                            this.FILTER_DELIVERY_METHOD_US_FOODS_DELIVERED,
                        ],
                    };
                }
                const request = {
                    ...action?.request,
                    facetFilters,
                };
                return SearchActionsV2.FetchSearchResponse({
                    request,
                    correlationId: action?.correlationId,
                    applyDefaultDeliveryMethodFilter: false,
                });
            }
            else {
                return SearchActionsV2.FetchSearchResponseSuccess({
                    response,
                    correlationId: action.correlationId,
                });
            }
        }), catchError(e => of(SearchActionsV2.FetchSearchResponseFail({
            response: { totalCount: 0 },
            correlationId: action.correlationId,
        })))))));
    }
}
SearchEffectsV2.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchEffectsV2, deps: [{ token: i1$3.Actions }, { token: SearchHttpClientServiceV2 }, { token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
SearchEffectsV2.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchEffectsV2 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SearchEffectsV2, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: SearchHttpClientServiceV2 }, { type: i1.Store }]; } });

class MerchandisingEffects {
    constructor(config, actions$, httpService, panAppState) {
        this.config = config;
        this.actions$ = actions$;
        this.httpService = httpService;
        this.panAppState = panAppState;
        this.resetStore$ = createEffect(() => this.actions$.pipe(ofType(MerchandisingActions.ResetStore), mergeMap(action => combineLatest([of(action), this.panAppState.customer$.pipe(take(1))])), switchMap(([action, customer]) => {
            const prefetchedMerchandising = this.config?.prefetchedMerchandising;
            if (prefetchedMerchandising?.length > 0) {
                return of(MerchandisingActions.FetchMerchandisingResult({
                    request: { experiences: prefetchedMerchandising },
                    customer,
                }));
            }
            else {
                return of(MerchandisingActions.NOOP());
            }
        })));
        this.fetchMerchandisingResult$ = createEffect(() => this.actions$.pipe(ofType(MerchandisingActions.FetchMerchandisingResult), mergeMap(action => this.httpService
            .post('merchandising/decision', action.request)
            .pipe(map(response => MerchandisingActions.FetchMerchandisingResultSuccess({
            request: action.request,
            response,
        })), catchError(e => of(MerchandisingActions.FetchMerchandisingResultFail({
            request: action.request,
        })))))));
        this.sendPersonalizationEvent$ = createEffect(() => this.actions$.pipe(ofType(MerchandisingActions.SendPersonalizationEvent), mergeMap(action => this.httpService
            .post('personalization' +
            `?personalizationType=${action.personalizationType}` +
            `&productNumber=${action.productNumber}`, null)
            .pipe(map(_ => MerchandisingActions.NOOP())))));
    }
}
MerchandisingEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MerchandisingEffects, deps: [{ token: NGRX_PRODUCT_LIB_CONFIG }, { token: i1$3.Actions }, { token: ProductHttpClientService }, { token: i5.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
MerchandisingEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MerchandisingEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: MerchandisingEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PRODUCT_LIB_CONFIG]
                }] }, { type: i1$3.Actions }, { type: ProductHttpClientService }, { type: i5.PanAppState }]; } });

class RevenueManagementEffects {
    constructor(actions$, httpService) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.fetchRevenueManagementResult$ = createEffect(() => this.actions$.pipe(ofType(RevenueManagementActions.FetchRevenueManagementResult), switchMap(_ => this.httpService.get('products/revman').pipe(map(response => RevenueManagementActions.FetchRevenueManagementResultSuccess({
            response,
        })), catchError(e => of(RevenueManagementActions.FetchRevenueManagementResultFail()))))));
    }
}
RevenueManagementEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RevenueManagementEffects, deps: [{ token: i1$3.Actions }, { token: ProductHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable });
RevenueManagementEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RevenueManagementEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RevenueManagementEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ProductHttpClientService }]; } });

class BrowseHierarchyHttpClientService {
    constructor(config, http) {
        this.config = config;
        this.http = http;
    }
    getCategories() {
        const url = `${this.config.apiUrl}/browse`;
        return this.http
            .get(url)
            .pipe(catchError(this.handleError));
    }
    handleError(error) {
        console.error('3nh1mK7LJz - NgRx Browse Hierarchy Service call failed', error);
        return throwError(error);
    }
}
BrowseHierarchyHttpClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowseHierarchyHttpClientService, deps: [{ token: NGRX_PRODUCT_LIB_CONFIG }, { token: i1$2.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
BrowseHierarchyHttpClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowseHierarchyHttpClientService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowseHierarchyHttpClientService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PRODUCT_LIB_CONFIG]
                }] }, { type: i1$2.HttpClient }]; } });

class BrowseHierarchyEffects {
    constructor(store, actions$, httpService) {
        this.store = store;
        this.actions$ = actions$;
        this.httpService = httpService;
        this.loadBrowseHierarchy$ = createEffect(() => this.actions$.pipe(ofType(BrowseHierarchyActions.LoadBrowseHierarchy), concatLatestFrom(action => this.store.select(selectShouldFetchBrowseHierarchy())), map(([action]) => BrowseHierarchyActions.FetchBrowseHierarchy({
            tracking: {
                tracing: {
                    data: {
                        traceContext: TRACE_CONTEXT.browseHierarchy,
                        isStartOfTrace: false,
                        isEndOfTrace: true,
                        attributes: action.tracking?.tracing?.data?.attributes,
                    },
                },
            },
        }))));
        this.fetchBrowseHierarchy$ = createEffect(() => this.actions$.pipe(ofType(BrowseHierarchyActions.FetchBrowseHierarchy), mergeMap(({ tracking }) => this.httpService.getCategories().pipe(map(response => BrowseHierarchyActions.FetchBrowseHierarchySuccess({
            result: response,
            tracking,
        })), catchError(() => of(BrowseHierarchyActions.FetchBrowseHierarchyFail({ tracking })))))));
    }
}
BrowseHierarchyEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowseHierarchyEffects, deps: [{ token: i1.Store }, { token: i1$3.Actions }, { token: BrowseHierarchyHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable });
BrowseHierarchyEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowseHierarchyEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: BrowseHierarchyEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: i1$3.Actions }, { type: BrowseHierarchyHttpClientService }]; } });

class SneakPeekEffects {
    constructor(actions$, httpService) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.fetchSneakPeekResult$ = createEffect(() => this.actions$.pipe(ofType(SneakPeekActions.FetchSneakPeekResult), switchMap(_ => this.httpService.post('products/sneakpeek', null).pipe(map(response => SneakPeekActions.FetchSneakPeekResultSuccess({
            response,
        })), catchError(e => of(SneakPeekActions.FetchSneakPeekResultFail()))))));
    }
}
SneakPeekEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SneakPeekEffects, deps: [{ token: i1$3.Actions }, { token: ProductHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable });
SneakPeekEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SneakPeekEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SneakPeekEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ProductHttpClientService }]; } });

class CustomersAlsoBoughtEffects {
    constructor(actions$, httpService) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.fetchCustomersAlsoBoughtProductNumbers$ = createEffect(() => this.actions$.pipe(ofType(CustomersAlsoBoughtActions.FetchCabProductNumbers), switchMap(action => this.httpService
            .postSingleObject('products/cab', action.request)
            .pipe(map(response => CustomersAlsoBoughtActions.FetchCabProductNumbersSuccess({
            request: action.request,
            response,
        })), catchError(() => of(CustomersAlsoBoughtActions.FetchCabProductNumbersFail({
            request: action.request,
        })))))));
    }
}
CustomersAlsoBoughtEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CustomersAlsoBoughtEffects, deps: [{ token: i1$3.Actions }, { token: ProductHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable });
CustomersAlsoBoughtEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CustomersAlsoBoughtEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CustomersAlsoBoughtEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ProductHttpClientService }]; } });

class SellerShowcaseEffects {
    constructor(actions$, httpService) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.fetchSellerShowcaseResult$ = createEffect(() => this.actions$.pipe(ofType(SellerShowcaseActions.FetchSellerShowcaseResult), switchMap(_ => this.httpService
            .post('divisionalpromotions', null)
            .pipe(map(response => SellerShowcaseActions.FetchSellerShowcaseResultSuccess({
            response,
        })), catchError(e => of(SellerShowcaseActions.FetchSellerShowcaseResultFail()))))));
    }
}
SellerShowcaseEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SellerShowcaseEffects, deps: [{ token: i1$3.Actions }, { token: ProductHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable });
SellerShowcaseEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SellerShowcaseEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SellerShowcaseEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ProductHttpClientService }]; } });

const notReadySellerShowcaseState = {
    loadingState: LoadingState.notReady,
};
const initialSellerShowcaseState = {
    loadingState: LoadingState.init,
};
const sellerShowcaseReducer = createReducer(notReadySellerShowcaseState, on(SellerShowcaseActions.ResetStore, () => initialSellerShowcaseState), on(SellerShowcaseActions.FetchSellerShowcaseResult, _ => ({
    loadingState: LoadingState.loading,
})), on(SellerShowcaseActions.FetchSellerShowcaseResultSuccess, (_, { response }) => ({
    divisionalPromotions: response,
    loadingState: LoadingState.loaded,
})), on(SellerShowcaseActions.FetchSellerShowcaseResultFail, _ => ({
    divisionalPromotions: [],
    loadingState: LoadingState.error,
})));

class CustomerRebateEffects {
    constructor(config, actions$, httpService) {
        this.config = config;
        this.actions$ = actions$;
        this.httpService = httpService;
        this.fetchCustomerRebates$ = createEffect(() => this.actions$.pipe(ofType(CustomerRebateActions.FetchCustomerRebates), mergeMap(() => this.httpService
            .postSingleObject('customerrebate/', {})
            .pipe(map(response => CustomerRebateActions.FetchCustomerRebatesSuccess({
            response: { customerRebates: response },
        })), catchError(error => of(CustomerRebateActions.FetchCustomerRebatesFail({
            error,
        })))))));
    }
}
CustomerRebateEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CustomerRebateEffects, deps: [{ token: NGRX_PRODUCT_LIB_CONFIG }, { token: i1$3.Actions }, { token: ProductHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable });
CustomerRebateEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CustomerRebateEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CustomerRebateEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PRODUCT_LIB_CONFIG]
                }] }, { type: i1$3.Actions }, { type: ProductHttpClientService }]; } });

/* eslint-disable prefer-arrow/prefer-arrow-functions */
const notReadyCustomerRebateState = {
    loadingState: LoadingState.notReady,
};
const initialCustomerRebateState = {
    loadingState: LoadingState.init,
};
// eslint-disable-next-line no-underscore-dangle
const _customerRebateReducer = createReducer(notReadyCustomerRebateState, on(CustomerRebateActions.ResetStore, () => ({
    ...initialCustomerRebateState,
})), on(CustomerRebateActions.FetchCustomerRebates, state => ({
    ...state,
    loadingState: LoadingState.loading,
})), on(CustomerRebateActions.FetchCustomerRebatesSuccess, (state, { response }) => ({
    ...state,
    loadingState: LoadingState.loaded,
    customerRebates: response.customerRebates,
})), on(CustomerRebateActions.FetchCustomerRebatesFail, state => ({
    ...state,
    loadingState: LoadingState.error,
    customerRebates: [],
})));
function customerRebateReducer(state, action) {
    return _customerRebateReducer(state, action);
}

class RebateTakeoutOptionsEffects {
    constructor(config, actions$, httpService) {
        this.config = config;
        this.actions$ = actions$;
        this.httpService = httpService;
        this.fetchTakeoutOptions$ = createEffect(() => this.actions$.pipe(ofType(RebateTakeoutOptionsActions.FetchRebateTakeoutOptions), mergeMap(() => this.httpService.post('customerrebate/takeoutoptions', {}).pipe(map(response => RebateTakeoutOptionsActions.FetchRebateTakeoutOptionsSuccess({
            response,
        })), catchError(error => of(RebateTakeoutOptionsActions.FetchRebateTakeoutOptionsFail({
            error,
        })))))));
    }
}
RebateTakeoutOptionsEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RebateTakeoutOptionsEffects, deps: [{ token: NGRX_PRODUCT_LIB_CONFIG }, { token: i1$3.Actions }, { token: ProductHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable });
RebateTakeoutOptionsEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RebateTakeoutOptionsEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RebateTakeoutOptionsEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PRODUCT_LIB_CONFIG]
                }] }, { type: i1$3.Actions }, { type: ProductHttpClientService }]; } });

/* eslint-disable prefer-arrow/prefer-arrow-functions */
const notReadyRebateTakeoutOptionsState = {
    loadingState: LoadingState.notReady,
};
const initialRebateTakeoutOptionsState = {
    loadingState: LoadingState.init,
};
// eslint-disable-next-line no-underscore-dangle
const _rebateTakeoutOptionsReducer = createReducer(notReadyRebateTakeoutOptionsState, on(RebateTakeoutOptionsActions.ResetStore, () => ({
    ...initialRebateTakeoutOptionsState,
})), on(RebateTakeoutOptionsActions.FetchRebateTakeoutOptions, _ => ({
    loadingState: LoadingState.loading,
})), on(RebateTakeoutOptionsActions.FetchRebateTakeoutOptionsSuccess, (_, { response }) => ({
    loadingState: LoadingState.loaded,
    takeoutOptions: response,
})), on(RebateTakeoutOptionsActions.FetchRebateTakeoutOptionsFail, _ => ({
    loadingState: LoadingState.error,
    takeoutOptions: [],
})));
function rebateTakeoutOptionsReducer(state, action) {
    return _rebateTakeoutOptionsReducer(state, action);
}

const personalizationAdapter = createEntityAdapter({
    selectId: personalizationState => personalizationState.personalizationType,
});
const notReadyPersonalizationState = personalizationAdapter.getInitialState({
    loadingState: LoadingState.notReady,
});
const initialPersonalizationState = personalizationAdapter.getInitialState({ loadingState: LoadingState.init });
// eslint-disable-next-line no-underscore-dangle
const _personalizationReducer = createReducer(notReadyPersonalizationState, on(PersonalizationActions.ResetStore, () => initialPersonalizationState), on(PersonalizationActions.GetPersonalizationEvent, (state, { personalizationType }) => {
    const updateState = {
        personalizationType,
        loadingState: LoadingState.loading,
    };
    return personalizationAdapter.upsertOne(updateState, {
        ...state,
        loadingState: LoadingState.loading,
    });
}), on(PersonalizationActions.GetPersonalizationEventSuccess, (state, { request, response }) => {
    const updateState = {
        personalizationType: request.personalizationType,
        response,
        loadingState: LoadingState.loaded,
    };
    return personalizationAdapter.upsertOne(updateState, {
        ...state,
        loadingState: LoadingState.loaded,
    });
}), on(PersonalizationActions.GetPersonalizationEventFail, (state, { request }) => {
    const updateState = {
        personalizationType: request.personalizationType,
        loadingState: LoadingState.error,
    };
    return personalizationAdapter.upsertOne(updateState, {
        ...state,
        loadingState: LoadingState.error,
    });
}));
const personalizationReducer = (state, action) => _personalizationReducer(state, action);

class PersonalizationEffects {
    constructor(actions$, httpService) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.getPersonalizationEvent$ = createEffect(() => this.actions$.pipe(ofType(PersonalizationActions.GetPersonalizationEvent), mergeMap(action => this.httpService
            .getObject('personalization' +
            `?personalizationType=${action.personalizationType}`)
            .pipe(map(response => PersonalizationActions.GetPersonalizationEventSuccess({
            request: { personalizationType: action.personalizationType },
            response,
        })), catchError(e => of(PersonalizationActions.GetPersonalizationEventFail({
            request: { personalizationType: action.personalizationType },
        })))))));
    }
}
PersonalizationEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PersonalizationEffects, deps: [{ token: i1$3.Actions }, { token: ProductHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable });
PersonalizationEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PersonalizationEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PersonalizationEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$3.Actions }, { type: ProductHttpClientService }]; } });

class NgRxProductModule {
    constructor(ngRxProductLibConfig, store, panAppState, webSocketService) {
        this.ngRxProductLibConfig = ngRxProductLibConfig;
        this.store = store;
        this.panAppState = panAppState;
        this.webSocketService = webSocketService;
    }
    static forRoot(ngRxProductLibConfig, store, panAppState, webSocketService) {
        return {
            ngModule: NgRxProductModule,
            providers: [
                {
                    provide: NGRX_PRODUCT_LIB_CONFIG,
                    useValue: ngRxProductLibConfig,
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializeWebSocket,
                    deps: [ProductClientWebSocketService],
                    multi: true,
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializeProductState,
                    deps: [ProductStateService],
                    multi: true,
                },
            ],
        };
    }
}
NgRxProductModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgRxProductModule, deps: [{ token: NGRX_PRODUCT_LIB_CONFIG }, { token: i1.Store }, { token: i5.PanAppState }, { token: i3.WebSocketService, optional: true }], target: i0.ɵɵFactoryTarget.NgModule });
NgRxProductModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: NgRxProductModule, imports: [i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule, i1$3.EffectsFeatureModule, i1.StoreFeatureModule] });
NgRxProductModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgRxProductModule, providers: [
        ProductEffects,
        ProductChangesEffects,
        ProductInventoryEffects,
        ProductContractEffects,
        ProductDetailEffects,
        ProductScoopEffects,
        OpportunityEffects,
        PowerReviewProductEffects,
        NonUSFProductEffects,
        ExternalVendorEffects,
        BetterBuysEffects,
        MerchandisingEffects,
        PersonalizationEffects,
        PimClassEffects,
        RevenueManagementEffects,
        BrowseHierarchyEffects,
        SneakPeekEffects,
        CustomersAlsoBoughtEffects,
        SellerShowcaseEffects,
        BrowserStorageService,
        WebSocketService,
        ProductClientWebSocketService,
        ProductHttpClientService,
        ProductInventoryHttpClientService,
        ProductContractHttpClientService,
        ProductDetailHttpClientService,
        ProductChangesHttpClientService,
        SearchHttpClientService,
        SearchHttpClientServiceV2,
        PowerReviewsHttpClientService,
        ProductOpportunityHttpClientService,
        ProductStateService,
        ProductDetailStoreService,
        ProductScoopStoreService,
        BrowseCategoryStoreService,
        NonUSFProductFacadeService,
        ExternalVendorFacadeService,
        BetterBuysFacadeService,
        BrowseHierarchyFacadeService,
        BrowseHierarchyHttpClientService,
        CustomerRebateEffects,
        CustomerRebateService,
        PersonalizationService,
    ], imports: [EffectsModule.forFeature([ProductEffects]),
        StoreModule.forFeature(PRODUCT_KEY, productReducer),
        EffectsModule.forFeature([ProductChangesEffects]),
        StoreModule.forFeature(PRODUCT_CHANGES_KEY, productChangesReducer),
        EffectsModule.forFeature([ProductInventoryEffects]),
        StoreModule.forFeature(PRODUCT_INVENTORY_KEY, productInventoryReducer),
        EffectsModule.forFeature([ProductContractEffects]),
        StoreModule.forFeature(PRODUCT_CONTRACT_KEY, productContractReducer),
        StoreModule.forFeature(CUSTOMER_PILL_KEY, customerPillReducer),
        EffectsModule.forFeature([ProductDetailEffects]),
        StoreModule.forFeature(PRODUCT_DETAIL_KEY$1, productDetailReducer),
        EffectsModule.forFeature([ProductSummaryEffects]),
        StoreModule.forFeature(PRODUCT_SUMMARY_KEY, productSummaryReducer),
        EffectsModule.forFeature([SearchEffects]),
        StoreModule.forFeature(SEARCH_RESPONSE_KEY, searchResponseReducer),
        EffectsModule.forFeature([SearchEffectsV2]),
        StoreModule.forFeature(SEARCH_RESPONSE_V2_KEY, searchResponseReducerV2),
        EffectsModule.forFeature([SearchTypeaheadEffects]),
        StoreModule.forFeature(SEARCH_TYPEAHEAD_KEY, typeaheadReducer),
        EffectsModule.forFeature([ProductScoopEffects]),
        StoreModule.forFeature(PRODUCT_SCOOP_KEY, productScoopReducer),
        EffectsModule.forFeature([BrowseCategoryEffects]),
        StoreModule.forFeature(BROWSE_CATEGORIES_KEY, browseCategoryReducer),
        EffectsModule.forFeature([OpportunityEffects]),
        StoreModule.forFeature(PRODUCT_OPPORTUNITY_KEY, opportunityReducer),
        EffectsModule.forFeature([PowerReviewProductEffects]),
        StoreModule.forFeature(POWER_REVIEW_PRODUCT_KEY, powerReviewsProductReducer),
        EffectsModule.forFeature([NonUSFProductEffects]),
        StoreModule.forFeature(NON_USF_PRODUCT_KEY, nonUSFProductReducer),
        EffectsModule.forFeature([ExternalVendorEffects]),
        StoreModule.forFeature(EXTERNAL_VENDOR_KEY, externalVendorReducer),
        EffectsModule.forFeature([BetterBuysEffects]),
        StoreModule.forFeature(BETTER_BUYS_KEY, betterBuysReducer),
        StoreModule.forFeature(MSL_PRODUCT_KEY, mslProductReducer),
        EffectsModule.forFeature([MerchandisingEffects]),
        StoreModule.forFeature(MERCHANDISING_KEY, merchandisingReducer),
        EffectsModule.forFeature([PersonalizationEffects]),
        StoreModule.forFeature(PERSONALIZATION_KEY, personalizationReducer),
        EffectsModule.forFeature([PimClassEffects]),
        StoreModule.forFeature(PIM_CLASS_KEY, pimClassReducer),
        EffectsModule.forFeature([RevenueManagementEffects]),
        StoreModule.forFeature(REVENUE_MANAGEMENT_KEY, revenueManagementReducer),
        EffectsModule.forFeature([BrowseHierarchyEffects]),
        StoreModule.forFeature(BROWSE_HIERARCHY_KEY, browseHierarchyReducer),
        EffectsModule.forFeature([SneakPeekEffects]),
        StoreModule.forFeature(SNEAK_PEEK_KEY, sneakPeekReducer),
        EffectsModule.forFeature([CustomersAlsoBoughtEffects]),
        StoreModule.forFeature(CAB_KEY, customersAlsoBoughtReducer),
        EffectsModule.forFeature([SellerShowcaseEffects]),
        StoreModule.forFeature(SELLER_SHOWCASE_KEY, sellerShowcaseReducer),
        EffectsModule.forFeature([CustomerRebateEffects]),
        StoreModule.forFeature(CUSTOMER_REBATE_KEY, customerRebateReducer),
        EffectsModule.forFeature([RebateTakeoutOptionsEffects]),
        StoreModule.forFeature(REBATE_TAKEOUT_OPTIONS_KEY, rebateTakeoutOptionsReducer)] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgRxProductModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                        EffectsModule.forFeature([ProductEffects]),
                        StoreModule.forFeature(PRODUCT_KEY, productReducer),
                        EffectsModule.forFeature([ProductChangesEffects]),
                        StoreModule.forFeature(PRODUCT_CHANGES_KEY, productChangesReducer),
                        EffectsModule.forFeature([ProductInventoryEffects]),
                        StoreModule.forFeature(PRODUCT_INVENTORY_KEY, productInventoryReducer),
                        EffectsModule.forFeature([ProductContractEffects]),
                        StoreModule.forFeature(PRODUCT_CONTRACT_KEY, productContractReducer),
                        StoreModule.forFeature(CUSTOMER_PILL_KEY, customerPillReducer),
                        EffectsModule.forFeature([ProductDetailEffects]),
                        StoreModule.forFeature(PRODUCT_DETAIL_KEY$1, productDetailReducer),
                        EffectsModule.forFeature([ProductSummaryEffects]),
                        StoreModule.forFeature(PRODUCT_SUMMARY_KEY, productSummaryReducer),
                        EffectsModule.forFeature([SearchEffects]),
                        StoreModule.forFeature(SEARCH_RESPONSE_KEY, searchResponseReducer),
                        EffectsModule.forFeature([SearchEffectsV2]),
                        StoreModule.forFeature(SEARCH_RESPONSE_V2_KEY, searchResponseReducerV2),
                        EffectsModule.forFeature([SearchTypeaheadEffects]),
                        StoreModule.forFeature(SEARCH_TYPEAHEAD_KEY, typeaheadReducer),
                        EffectsModule.forFeature([ProductScoopEffects]),
                        StoreModule.forFeature(PRODUCT_SCOOP_KEY, productScoopReducer),
                        EffectsModule.forFeature([BrowseCategoryEffects]),
                        StoreModule.forFeature(BROWSE_CATEGORIES_KEY, browseCategoryReducer),
                        EffectsModule.forFeature([OpportunityEffects]),
                        StoreModule.forFeature(PRODUCT_OPPORTUNITY_KEY, opportunityReducer),
                        EffectsModule.forFeature([PowerReviewProductEffects]),
                        StoreModule.forFeature(POWER_REVIEW_PRODUCT_KEY, powerReviewsProductReducer),
                        EffectsModule.forFeature([NonUSFProductEffects]),
                        StoreModule.forFeature(NON_USF_PRODUCT_KEY, nonUSFProductReducer),
                        EffectsModule.forFeature([ExternalVendorEffects]),
                        StoreModule.forFeature(EXTERNAL_VENDOR_KEY, externalVendorReducer),
                        EffectsModule.forFeature([BetterBuysEffects]),
                        StoreModule.forFeature(BETTER_BUYS_KEY, betterBuysReducer),
                        StoreModule.forFeature(MSL_PRODUCT_KEY, mslProductReducer),
                        EffectsModule.forFeature([MerchandisingEffects]),
                        StoreModule.forFeature(MERCHANDISING_KEY, merchandisingReducer),
                        EffectsModule.forFeature([PersonalizationEffects]),
                        StoreModule.forFeature(PERSONALIZATION_KEY, personalizationReducer),
                        EffectsModule.forFeature([PimClassEffects]),
                        StoreModule.forFeature(PIM_CLASS_KEY, pimClassReducer),
                        EffectsModule.forFeature([RevenueManagementEffects]),
                        StoreModule.forFeature(REVENUE_MANAGEMENT_KEY, revenueManagementReducer),
                        EffectsModule.forFeature([BrowseHierarchyEffects]),
                        StoreModule.forFeature(BROWSE_HIERARCHY_KEY, browseHierarchyReducer),
                        EffectsModule.forFeature([SneakPeekEffects]),
                        StoreModule.forFeature(SNEAK_PEEK_KEY, sneakPeekReducer),
                        EffectsModule.forFeature([CustomersAlsoBoughtEffects]),
                        StoreModule.forFeature(CAB_KEY, customersAlsoBoughtReducer),
                        EffectsModule.forFeature([SellerShowcaseEffects]),
                        StoreModule.forFeature(SELLER_SHOWCASE_KEY, sellerShowcaseReducer),
                        EffectsModule.forFeature([CustomerRebateEffects]),
                        StoreModule.forFeature(CUSTOMER_REBATE_KEY, customerRebateReducer),
                        EffectsModule.forFeature([RebateTakeoutOptionsEffects]),
                        StoreModule.forFeature(REBATE_TAKEOUT_OPTIONS_KEY, rebateTakeoutOptionsReducer),
                    ],
                    exports: [],
                    providers: [
                        ProductEffects,
                        ProductChangesEffects,
                        ProductInventoryEffects,
                        ProductContractEffects,
                        ProductDetailEffects,
                        ProductScoopEffects,
                        OpportunityEffects,
                        PowerReviewProductEffects,
                        NonUSFProductEffects,
                        ExternalVendorEffects,
                        BetterBuysEffects,
                        MerchandisingEffects,
                        PersonalizationEffects,
                        PimClassEffects,
                        RevenueManagementEffects,
                        BrowseHierarchyEffects,
                        SneakPeekEffects,
                        CustomersAlsoBoughtEffects,
                        SellerShowcaseEffects,
                        BrowserStorageService,
                        WebSocketService,
                        ProductClientWebSocketService,
                        ProductHttpClientService,
                        ProductInventoryHttpClientService,
                        ProductContractHttpClientService,
                        ProductDetailHttpClientService,
                        ProductChangesHttpClientService,
                        SearchHttpClientService,
                        SearchHttpClientServiceV2,
                        PowerReviewsHttpClientService,
                        ProductOpportunityHttpClientService,
                        ProductStateService,
                        ProductDetailStoreService,
                        ProductScoopStoreService,
                        BrowseCategoryStoreService,
                        NonUSFProductFacadeService,
                        ExternalVendorFacadeService,
                        BetterBuysFacadeService,
                        BrowseHierarchyFacadeService,
                        BrowseHierarchyHttpClientService,
                        CustomerRebateEffects,
                        CustomerRebateService,
                        PersonalizationService,
                    ],
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PRODUCT_LIB_CONFIG]
                }] }, { type: i1.Store }, { type: i5.PanAppState }, { type: i3.WebSocketService, decorators: [{
                    type: Optional
                }] }]; } });
function initializeWebSocket(webSocketService) {
    const websocket = () => webSocketService.initialize();
    return websocket;
}
function initializeProductState(productStateService) {
    const productState = () => productStateService.initialize();
    return productState;
}

const isInStock = (productInventory, productSummary) => {
    if (!productInventory || !productSummary) {
        // It's not possible to determine the inventory without this elements
        return false;
    }
    if (productSummary.properties?.has(ProductPropertiesEnum.direct) ||
        productSummary.properties?.has(ProductPropertiesEnum.ces) ||
        productSummary.properties?.has(ProductPropertiesEnum.jit) ||
        productSummary.properties?.has(ProductPropertiesEnum.specialOrder)) {
        return !productSummary.properties?.has(ProductPropertiesEnum.supplierOutOfStock);
    }
    else {
        return getAvailableCases(productInventory) > 0;
    }
};
const getAvailableCases = (productInventory) => (productInventory?.casesOnHand ?? 0) -
    (productInventory?.casesOnReserve ?? 0) -
    (productInventory?.casesToEachDemand ?? 0) -
    (productInventory?.casesWaitlistReserve ?? 0);
const getAvailableEaches = (productInventory) => (productInventory?.eachOnHand ?? 0) +
    ((productInventory?.casesToEachDemand ?? 0) *
        (productInventory?.eachConversionFactor ?? 0) -
        (productInventory?.eachOnReserve ?? 0) -
        (productInventory?.eachWaitListReserve ?? 0));
const isOrderable = (productInventory) => 
// TODO Include all the conditions to determine this...
!productHasStatus('2', productInventory);

/**
 * WHILE SUPPLIES LAST
 *
 * @param tracking
 * @returns Data Layer needed for tracing While Supplies Last
 */
const whileSuppliesLastTransformer = (tracingData, ...args) => ({
    event: tracingData.data.attributes.event,
});
const allAvailableDealsTransformer = (tracingData) => ({
    event: tracingData.data.attributes.event,
});

/*
 * Public API Surface of ngrx-product
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ACTIONS, ACTIVE_PROMO, ALERTS_CAPABILITY, BETTER_BUYS, BETTER_BUYS_KEY, BROWSE_CATEGORIES_KEY, BROWSE_HIERARCHY_KEY, BUTTON_NAME, BetterBuysActions, BetterBuysFacadeService, BrowseCategoryActions, BrowseCategoryStoreService, BrowseHierarchyActions, BrowseHierarchyFacadeService, CAB_KEY, CPR, CREATE_OPPORTUNITY, CUSTOMER_PILL_KEY, CUSTOMER_REBATE_KEY, CUSTOM_PRICING_RATIONALE, CustomerRebateActions, CustomerRebateService, CustomersAlsoBoughtActions, DEALS, ENGAGEMENT, EVENT, EXTERNAL_VENDOR_KEY, ExternalVendorActions, ExternalVendorFacadeService, FILE_TYPE, GUEST_LANDING_PAGE, LISTS_CAPABILITY, LoadingState, MERCHANDISING_KEY, MERCH_FEATURE, MERCH_FEATURES, MONETATE_ADD_SEARCH, MONETATE_ENGINE_NAMES, MONETATE_EVENT_NAMES, MSL_PRODUCT_KEY, MY_PRODUCTS_BATCH_SIZE, MY_PRODUCTS_KEY, MY_PRODUCTS_WATERMARK_KEY, MerchandisingActions, MslProductActions, MyProductActions, NON_USF_PRODUCTS, NON_USF_PRODUCT_KEY, NgRxProductModule, NonUSFProductActions, NonUSFProductFacadeService, OpportunityStoreService, PAGE_DETAIL, PAGE_NAME_BASE_PATH, PAGE_PRODUCT_COMPARE, PERSONALIZATION_KEY, PERSONALIZATION_TYPE, PIMClassActions, PIM_CLASS_KEY, POWER_REVIEW_PRODUCT_KEY, PRICING_KEY, PRODUCTS_DOMAIN_NAME, PRODUCTS_KEY, PRODUCTS_WATERMARK_KEY, PRODUCT_ATTRIBUTE_TRACK, PRODUCT_CHANGES_KEY, PRODUCT_COMPARISON_DOWNLOAD, PRODUCT_COMPARISON_PRINT, PRODUCT_CONTRACT_KEY, PRODUCT_DETAIL_KEY$1 as PRODUCT_DETAIL_KEY, PRODUCT_INVENTORY_KEY, PRODUCT_OPPORTUNITY_KEY, PRODUCT_SCOOP_KEY, PRODUCT_STORAGE_KEYS, PRODUCT_SUMMARY_DETAIL_KEY$1 as PRODUCT_SUMMARY_DETAIL_KEY, PRODUCT_SUMMARY_KEY, PersonalizationService, ProductActions, ProductAnalyticsService, ProductContractActions, ProductInventoryActions, ProductScoopActions, ProductScoopStoreService, ProductStateService, ProductSummaryActions, REBATE_TAKEOUT_OPTIONS_KEY, REVENUE_MANAGEMENT_KEY, REVIEW_PRODUCT, RevenueManagementActions, SCOOP, SCOOP_PREVIEW, SEARCH_ENGINE, SEARCH_PAGE_TRACK, SEARCH_RESPONSE_KEY, SEARCH_RESPONSE_V2_KEY, SEARCH_TYPE$1 as SEARCH_TYPE, SEARCH_TYPEAHEAD_KEY, SELLER_INCENTIVES, SELLER_SHOWCASE_KEY, SHARE_TYPE, SNEAK_PEEK_KEY, SUBSTITUTE_TYPE, SearchActions, SearchActionsV2, SearchStoreService, SearchStoreServiceV2, SellerShowcaseActions, SneakPeekActions, SortOptionsEnum, TEAM_CAPABILITY, TM_NOTES_DETAIL_PAGE, TRACE_CONTEXT, VendorColumns, acceptedCount, allAvailableDealsTransformer, allBetterBuys, allExternalVendors, allNonUSFProducts, allProductScoops, betterBuyEntities, betterBuysAdapter, betterBuysLoaded, betterBuysReducer, browseCategoryReducer, browseCategoryResponseAdapter, browseHierarchyAdapter, browseHierarchyReducer, cabAdaptor, customerPillAdapter, customerPillReducer, customersAlsoBoughtReducer, estimatedAllTimeSavings, estimatedTodaySavings, estimatedYearToDateSavings, externalVendorAdapter, externalVendorReducer, getAvailableCases, getAvailableEaches, getBetterBuysById, getExternalVendorById, getKeyword, getLoadingStateOfProducts, getNonUSFProductById, getNonUSFProductSelections, getNonUSFSortOptions, getPriceProduct, getProductAlternates, getProductInventories, getProductSummarie, getProductSummaries, getPropertiesSet, getSearchResultFromCoveoTrackingData, getSearchResultTrackingData, getSearchResultTrackingProductsData, getSubcategoryTrackingData, initialNonUsfProductSortOptions, initializeProductState, initializeWebSocket, isInStock, isMerchandisingStateReady, isOnMasterList, isOrderable, isPersonalizationStateReady, isProductContractStateReady, isProductInventoryStateReady, isProductSummaryStateReady, isRevenueManagementStateReady, isSearchDataLoaded, isSearchStateReady, isSearchStateReadyV2, isSellerShowcaseStateReady, isSneakPeekStateReady, isSupplierOutOfStock, merchandisingAdapter, merchandisingReducer, mslProductAdapter, mslProductReducer, myProductAdapter, myProductReducer, nonUSFProductAdapter, nonUSFProductReducer, opportunityAdapter, opportunityReducer, productAdapter, productChangesAdapter, productChangesReducer, productContractAdapter, productContractReducer, productDetailAdapter, productDetailLoaded, productDetailReducer, productHasStatus, productInventoryAdapter, productInventoryReducer, productReducer, productScoopAdapter, productScoopLoaded, productScoopReducer, productScoopWatermark, productSummaryAdapter, productSummaryDetailAdapter, productSummaryDetailLoaded, productSummaryDetailReducer, productSummaryReducer, productsLoaded, remainingCount, revenueManagementReducer, searchLoadingState, searchResponseAdapter, searchResponseAdapterV2, searchResponseReducer, searchResponseReducerV2, selectAllBrowseHierarchyCategories, selectAllIPIMClasses, selectAllProductScoops, selectAllProductSummaries, selectBrowseCategoryResponse, selectBrowseCategoryResponseState, selectCabProduct, selectCategoryList, selectCompletedProductContracts, selectCompletedProductInventories, selectCompletedProductSummaries, selectCustomerPillEntities, selectExternalVendorsState, selectMerchandisingResponseById, selectMerchandisingResponseByIds, selectMerchandisingState, selectMslProductState, selectMslProductStateEntities, selectMslProductStateLoaded, selectMyProductState, selectNonUSFProductsState, selectPIMClassById, selectPIMClassEntities, selectPIMClassIds, selectPIMClassState, selectPIMWatermark, selectPersonalizationResponseByType, selectPersonalizationState, selectProductChangesState, selectProductContractEntities, selectProductDetail, selectProductDetailState, selectProductDetails, selectProductInventoryEntities, selectProductScoopEntities, selectProductSummaryDetail, selectProductSummaryEntities, selectProductsState, selectRebateTakeoutOptions, selectRevenueManagementState, selectSearchResponse, selectSearchResponseById, selectSearchResponseState, selectSearchResponseStateV2, selectSellerShowcaseState, selectSneakPeekState, sneakPeekReducer, totalAnnualSavings, typeaheadAdapter, typeaheadReducer, whileSuppliesLastTransformer };
